import { SelectedSkillBadges } from "@/components/selected-skill-badges";
import { useAgentSkills } from "@/hooks/use-agent-skills";
import { prependSelectedSkills } from "@/lib/selected-skills";
import "katex/dist/katex.min.css";
import "./index.css";
import "@/lib/i18n"; // side-effect: initialises i18next before React renders

import * as Sentry from "@sentry/react";
import { StrictMode, useCallback, useEffect, useState, useMemo, useRef, memo, type ComponentProps } from "react";
import { createRoot } from "react-dom/client";
import { useTranslation } from "react-i18next";
import { toast } from "sonner";
import { useParams, useLocation, useSearch, type DefaultParams } from "wouter";
import { navigate } from "wouter/use-browser-location";
import type { ChatStatus, FileUIPart } from "ai";
import { useAuthWithTestMode } from "@/hooks/use-auth-with-test-mode";
import { useRefreshOnReconnect } from "@/hooks/use-backend-connectivity";
import { getWorkOSSessionLogoutUrl } from "@/lib/workos-logout";
import { purgeAllUiCache, setVerifiedCacheIdentity, sweep } from "@/lib/ui-cache";

import { useSessions, type TenantDefault } from "@/hooks/use-sessions";
import { attemptInitialSession } from "@/lib/initial-session";
import { useAgents, type ComposerAction } from "@/hooks/use-agents";
import { useAgentPins } from "@/hooks/use-agent-pins";
import {
  firstAccessiblePinnedAgentId,
  useSidebarPinOrdering,
} from "@/hooks/use-sidebar-pin-ordering";
import { useProjects } from "@/hooks/use-projects";
import { ProjectsDataProvider } from "@/hooks/projects-data-context";
import { AgentCatalogRefreshProvider } from "@/hooks/agent-catalog-refresh-context";
import { getTenantAgentEditorAccess, useMe } from "@/hooks/use-me";
import { useTasks } from "@/hooks/use-tasks";
import { useTags } from "@/hooks/use-tags";
import { useTenantUsers } from "@/hooks/use-tenant-users";
import { fetchSessionDetailCached } from "@/lib/session-detail-cache";
import {
  DEFAULT_SUBMIT_SHORTCUT,
  type SubmitShortcut,
  type UseUserPreferencesReturn,
  useUserPreferences,
} from "@/hooks/use-user-preferences";
import { canSwitchAgent, canSwitchModel } from "@/lib/tenant-config";
import { useIsMobile } from "@/hooks/use-media-query";
import { useConversationHeaderLayout } from "@/hooks/use-conversation-header-layout";
import { useAsideViewManager } from "@/hooks/use-aside-view";
import { useViewportHeight } from "@/hooks/use-viewport-height";
import { useIsKeyboardOpen } from "@/hooks/use-keyboard-open";
import { useSessionMessageFeed } from "@/hooks/use-session-message-feed";
import { usePromptQueue } from "@/hooks/use-prompt-queue";
import { useConversationReadEngagement } from "@/hooks/use-conversation-read-engagement";
import { useChildSessionAnchors, useCreateThread } from "@/hooks/use-child-sessions";
import { useSessionBreadcrumb } from "@/hooks/use-session-breadcrumb";
import { useConversationBootstrap } from "@/hooks/use-conversation-bootstrap";
import type { ConversationBootstrap } from "@/api/conversation-bootstrap";
import { useConversationNavigation } from "@/hooks/use-conversation-navigation";
import {
  useVoiceNoteProgress,
  useVoiceNoteProgressSessionIds,
} from "@/hooks/use-voice-note-progress";
import {
  cn,
  fetchWithAuth as fetchWithWorkOSAuth,
  type AccessTokenGetter,
} from "@/lib/utils";
import { SessionSidebar } from "@/components/session-sidebar";
import { AgentConversationsPage } from "@/components/agent-conversations-page";
import { SessionBreadcrumb, type SessionBreadcrumbSegment } from "@/components/session-breadcrumb";
import { pushConversationDestination, type ConversationDestination } from "@/lib/conversation-target";
import { ProjectsWorkspace, type ProjectTaskChatRenderProps } from "@/components/projects-page";
import { ProjectsListPage } from "@/components/projects-list-page";
import { ConversationHeaderControls } from "@/components/conversation-header-controls";
import { ConversationTimelinePanel } from "@/components/conversation-timeline-panel";
import {
  canConvertConversationToTask,
  ConversationActionsMenu,
  ConvertToTaskDialog,
} from "@/components/conversation-actions-menu";
import { IdentityConnectPage } from "@/components/identity-connect";
import {
  PROJECT_TASK_SESSION_METADATA_KEY,
  isProjectTaskSession,
  threadAnchorDisplayFrom,
} from "@/lib/session";
import type { Project, Task, TaskStatus } from "@/lib/project-types";
import { isTaskStatus } from "@/lib/task-status";
import type {
  Session,
  ThreadAnchorDisplay,
  ThreadAnchorRef,
  ThreadCreateRequest,
  ThreadCreateResponse,
} from "@/lib/session";
import {
  isSubAgentSessionData,
  type SubAgentThreadReference,
} from "@/lib/sub-agent-thread";
import { Button } from "@/components/ui/button";
import { Maximize2, Pencil, SquareIcon, Trash2, X } from "lucide-react";
import { MobileSessionMenuButton } from "@/components/mobile-chat-header-buttons";
import { Spinner } from "@/components/ui/spinner";
import { InputGroupButton } from "@/components/ui/input-group";
import {
  PromptInputProvider,
  useOptionalPromptInputController,
} from "@/components/ai-elements/prompt-input";
import { ComposerQuickActions } from "@/components/composer-quick-actions";
import { FollowUpSuggestions, lastCompletedAssistantMessageId } from "@/components/follow-up-suggestions";
import { ChatPromptComposer, CHAT_MAX_UPLOAD_FILES, CHAT_MAX_UPLOAD_SIZE_MB, CHAT_MAX_UPLOAD_SIZE_BYTES } from "@/components/chat-prompt-composer";
import { PromptQueueList } from "@/components/prompt-queue-list";
import type { AgnoChatMessage } from "@/hooks/use-agno-chat";
import { buildRenderItems } from "@/lib/render-items";
import { captureQuoteRange } from "@/lib/thread-quote-anchor";
import { type VoiceNoteButtonHandle } from "@/components/ai-elements/voice-note-button";
import { EnterToSendHint, OPEN_PREFERENCES_EVENT, useEnterToSendHint } from "@/components/enter-to-send-hint";
import { MarkdownViewerPanel } from "@/components/markdown-viewer-panel";
import { MarkdownViewerPage } from "@/components/markdown-viewer-page";
import { HtmlViewerPanel } from "@/components/html-viewer-panel";
import { HtmlViewerPage } from "@/components/html-viewer-page";
import { PdfViewerPanel } from "@/components/pdf-viewer-panel";
import { PdfViewerPage } from "@/components/pdf-viewer-page";
import { DocxFileViewerPanel } from "@/components/docx-file-viewer-panel";
import { DocxFileViewerPage } from "@/components/docx-file-viewer-page";
import { ImageViewerPanel } from "@/components/image-viewer-panel";
import { ImageViewerPage } from "@/components/image-viewer-page";
import { SourceFileViewerPanel } from "@/components/source-file-viewer-panel";
import { SourceFileViewerPage } from "@/components/source-file-viewer-page";
import { ChildSessionPanel } from "@/components/child-session-panel";
import { WorkflowRunPanel } from "@/components/lazy-heavy-components";
import {
  ConversationMessageList,
  threadRequestForSelection,
  type ThreadCreationTarget,
  type ThreadSelectionTarget,
} from "@/components/conversation-message-list";
import { InterruptedRunBanner } from "@/components/interrupted-run-banner";
import { ThreadCreateButton } from "@/components/thread-create-button";
import { touchThreadSelectionBarPosition } from "@/components/thread-selection-bar";
import { TaskBriefCard } from "@/components/task-brief-card";
import { SubtasksSection } from "@/components/subtasks-section";
import { useTaskHierarchyCapability } from "@/hooks/use-task-hierarchy-capability";
import { AgentViewPanel } from "@/components/views/agent-view-panel";
import { ViewTabs } from "@/components/views/view-tabs";
import { viewPagePath, viewRefKey, viewTabIdentityKey, type ViewTab } from "@/api/views";
import {
  conversationViewTab,
  mergeConversationViews,
  pinConversationFile,
  resolveConversationFileView,
} from "@/api/conversation-views";
import { useConversationViews } from "@/hooks/use-conversation-views";
import { useConversationViewCapabilities } from "@/hooks/use-conversation-view-capabilities";
import {
  extractFileInfoFromToken,
  extractFilePathFromToken,
} from "@/lib/file-link-rewrite";
import {
  extractFileToken,
  isDocxFile,
  isHtmlFile,
  isXlsxFile,
  isImageFile,
  isMarkdownFile,
  isPdfFile,
  isSourceFile,
} from "@/lib/file-links";
import { App } from "@/app";
import { NotificationProvider } from "@/components/notification-provider";
import { Toaster } from "@/components/ui/sonner";
import { useNotifications, type SessionPollSession } from "@/hooks/use-notifications";
import { useInboxNotifications } from "@/hooks/use-inbox-notifications";
import { NotificationInbox } from "@/components/notification-inbox";
import {
  beginDraftAttempt,
  completeDraftAttempt,
  DRAFTS_CHANGED_EVENT,
  getDraft,
  getDraftAttempt,
  getDraftAttemptFiles,
  setDraft,
  type DraftAttempt,
} from "@/lib/drafts";
import {
  agentPinBootstrapStorageKey,
  mergeAgentPinBootstrapStates,
  planAgentPinBootstrap,
  readAgentPinBootstrapState,
  readServerAgentPinBootstrapState,
  writeServerAgentPinBootstrapState,
  writeAgentPinBootstrapState,
} from "@/lib/agent-pin-bootstrap";
import {
  navigateToSession,
  resolveThreadPanelParentSessionId,
} from "@/lib/session-navigation";
import {
  isOlderLastActiveAt,
  mergeThreadTitleCacheEntry,
  reconcileThreadSessionOnce,
  refreshSessionAfterRunCompletion,
  type ThreadTitleCacheEntry,
} from "@/lib/thread-session-reconciliation";
import { resolveSessionAgentId } from "@/lib/session-agent-resolution";
import { SettingsMenu } from "@/components/settings-menu";
import {
  revokeCurrentPushSubscription,
  usePushSubscription,
  type PushSubscriptionAPI,
} from "@/hooks/use-push-subscription";
import { useCommandPalette } from "@/hooks/use-command-palette";
import { CommandPalette } from "@/components/command-palette";
import { useQuickLaunch } from "@/hooks/use-quick-launch";
import { useLeaderKeys, type LeaderKeyCommand } from "@/hooks/use-leader-keys";
import { useLeaderKeyPickers } from "@/hooks/use-leader-key-pickers";
import {
  dispatchVoiceDictationShortcut,
  useVoiceDictationShortcut,
} from "@/hooks/use-voice-dictation-shortcut";
import {
  QuickLaunchDialog,
  type QuickLaunchSubmission,
} from "@/components/quick-launch-dialog";
import { LeaderKeysIndicator } from "@/components/leader-keys-indicator";
import { recordQuickLaunchUsage } from "@/lib/quick-launch-api";
import { notifyTaskChanged } from "@/lib/project-events";
import { shouldRetainSubtaskIdempotencyKey, subtaskCreateErrorKey } from "@/lib/subtask-creation";
import { reconcileRelatedTaskStatus, type ConfirmedTaskStatus } from "@/lib/task-status-reconciliation";
import { suggestProjectTaskTitle } from "@/lib/project-task-title";
import { ConnectionStatusBanner } from "@/components/connection-status-banner";
import { WorkOSDashboardCallbackPage } from "@/components/workos-callback-page";
import { shouldHandleWorkOSDashboardCallback } from "@/lib/workos-callback";
import { initializeTheme } from "@/lib/theme";
import { installGlobalErrorReporter } from "@/lib/global-error-reporter";

initializeTheme();

const CONVERSATION_TIMELINE_ENABLED =
  import.meta.env.CONVERSATION_TIMELINE_ENABLED !== "false";

/** ``undefined`` is an old-server capability miss; null means no task. */
function relatedTaskFromSession(
  session: Pick<SessionPollSession, "related_tasks">,
  taskId?: string,
): { id: string; status: TaskStatus; updatedAt: number | null } | null | undefined {
  if (!Object.prototype.hasOwnProperty.call(session, "related_tasks")) return undefined;
  if (!Array.isArray(session.related_tasks)) return undefined;
  if (session.related_tasks.length === 0) return null;
  if (!taskId && session.related_tasks.length !== 1) return undefined;
  const related = taskId
    ? session.related_tasks.find((item) => item && typeof item === "object" && (item as Record<string, unknown>).id === taskId)
    : session.related_tasks[0];
  if (!related || typeof related !== "object") return undefined;
  const record = related as Record<string, unknown>;
  const updatedAt = typeof record.updated_at === "string" ? Date.parse(record.updated_at) : NaN;
  return typeof record.id === "string" && typeof record.status === "string" && isTaskStatus(record.status)
    ? { id: record.id, status: record.status, updatedAt: Number.isFinite(updatedAt) ? updatedAt : null }
    : undefined;
}

/* ------------------------------------------------------------------ */
/*  Sentry — initialise as early as possible                          */
/* ------------------------------------------------------------------ */

Sentry.init({
  dsn: "https://5aa94d2ede5b81e8b0b26b4c9e28588b@o4511587495313408.ingest.de.sentry.io/4511587498983504",
  dataCollection: {
    // To disable sending user data and HTTP bodies, uncomment the lines below. For more info visit:
    // https://docs.sentry.io/platforms/javascript/guides/react/configuration/options/#dataCollection
    // userInfo: false,
    // httpBodies: [],
  },
});

installGlobalErrorReporter();

/* ------------------------------------------------------------------ */
/*  ChatInterface — custom hook with session awareness                */
/* ------------------------------------------------------------------ */

interface ViewPublishRequest {
  id: number;
  sessionId: string;
  prompt: string;
}

interface PendingInitialMessage {
  sessionId: string;
  text: string;
  initialFiles: FileUIPart[];
}

type ProjectTaskChatTarget = Pick<Project, "id"> & {
  default_agent_id?: string | null;
};

interface ProjectTaskChatOptions {
  /** A prompt-derived title remains eligible for first-message auto-titling. */
  title?: string;
  /** Task reference material saved alongside the task before the chat starts. */
  description?: string | null;
  /** Optional first chat message to hand off after the session is created. */
  initialMessage?: string;
  initialFiles?: FileUIPart[];
}

interface ConversationViewPinnedEvent {
  schema_version: 1;
  ui_event: "conversation_view.pinned";
  session_id: string;
  view_id: string;
  created: boolean;
}

interface ConversationViewUnpinnedEvent {
  schema_version: 1;
  ui_event: "conversation_view.unpinned";
  session_id: string;
  view_id: string;
  removed: boolean;
}

// New agents expose pin_view. Keep names from either side of the
// conversation-view rollout so a long-lived agent still opens its pin.
const CONVERSATION_VIEW_TOOL_NAMES = new Set([
  "pin_view",
  "create_view",
  "pin_preview",
  "unpin_view",
]);

function conversationViewPinnedEvent(value: unknown): ConversationViewPinnedEvent | null {
  let candidate = value;
  if (typeof candidate === "string") {
    try {
      candidate = JSON.parse(candidate);
    } catch {
      return null;
    }
  }
  if (!candidate || typeof candidate !== "object") return null;
  const event = candidate as Record<string, unknown>;
  if (
    event.schema_version !== 1
    || event.ui_event !== "conversation_view.pinned"
    || typeof event.session_id !== "string"
    || typeof event.view_id !== "string"
    || typeof event.created !== "boolean"
  ) {
    return null;
  }
  return event as unknown as ConversationViewPinnedEvent;
}

function conversationViewUnpinnedEvent(value: unknown): ConversationViewUnpinnedEvent | null {
  let candidate = value;
  if (typeof candidate === "string") {
    try {
      candidate = JSON.parse(candidate);
    } catch {
      return null;
    }
  }
  if (!candidate || typeof candidate !== "object") return null;
  const event = candidate as Record<string, unknown>;
  if (
    event.schema_version !== 1
    || event.ui_event !== "conversation_view.unpinned"
    || typeof event.session_id !== "string"
    || typeof event.view_id !== "string"
    || typeof event.removed !== "boolean"
  ) {
    return null;
  }
  return event as unknown as ConversationViewUnpinnedEvent;
}

interface ChatInterfaceProps {
  user: any;
  getAccessToken: AccessTokenGetter;
  sessionId: string;
  bootstrap?: ConversationBootstrap | null;
  bootstrapPending?: boolean;
  /** Active session path, ordered from root to current session. */
  sessionBreadcrumb: SessionBreadcrumbSegment[];
  /** Project associated with a task session, shown before the session path. */
  projectBreadcrumbSegment?: SessionBreadcrumbSegment;
  onMenuToggle: () => void;
  isMenuOpen: boolean;
  agents: import("@/hooks/use-agents").Agent[];
  agentsLoading: boolean;
  agentsError: string | null;
  currentAgentId: string | null;
  /** Used only when neither local nor verified session data identifies an agent. */
  fallbackAgentId?: string | null;
  onAgentChange: (agentId: string) => void;
  showAgentSwitcher: boolean;
  /** Whether the ae10-only per-session model switcher should be visible. */
  showModelSwitcher?: boolean;
  /** Archives this conversation when selected from the header actions menu. */
  onArchive?: (id: string) => void | Promise<unknown>;
  /** Current session data for header actions such as participation. */
  session?: Session;
  /** Cached origin for thread-to-task conversion before session detail loads. */
  threadAnchor?: ThreadAnchorDisplay | null;
  /** Authenticated user ID for distinguishing owners from participants. */
  currentUserId?: string | null;
  /** Adds/removes a shared session and refreshes the sidebar list. */
  onAddParticipation?: (id: string) => Promise<void>;
  onRemoveParticipation?: (id: string) => Promise<void>;
  /** Applies a newly created task binding to the active conversation. */
  onTaskCreated?: (sessionId: string, task: Task) => void | Promise<unknown>;
  /** Whether the current user is an admin (ae10 + admin role). */
  isAdminUser?: boolean;
  /** Whether the user may open the narrow, own-tenant shared-agent editor. */
  canManageTenantAgents?: boolean;
  /** Web Push subscription API for the settings menu. */
  pushApi?: import("@/hooks/use-push-subscription").PushSubscriptionAPI;
  /** Server-backed preference state shared with the settings dialog. */
  preferencesApi: UseUserPreferencesReturn;
  /** Effective keyboard submit setting, with Enter as the fallback default. */
  submitShortcut: SubmitShortcut;
  /**
   * Called whenever the SSE/polling status changes so the sidebar stays in sync.
   * Receives BOTH the new and previous status so the caller can react only to
   * real transitions (e.g. out-of-in-flight → terminal) and ignore the mount
   * fire where `prev === newStatus === "ready"`.
   */
  onStatusChange?: (status: ChatStatus, prevStatus: ChatStatus) => void;
  /**
   * Whether the active session was just created by this UI. Used by
   * ``useAgnoChat`` to skip the mount-time ``/runs`` check on the
   * (correctly safe) assumption that a brand-new session cannot have an
   * in-flight run on the server at the moment we got the 201 from POST.
   */
  isNewSession?: boolean;
  /**
   * Monotonically increasing counter bumped by ChatPage whenever a UX event
   * that should return focus to the chat input happens while the user is
   * mid-flow: renaming the active session, or switching the session's
   * agent. ChatInterface folds this into the autoFocusKey passed to the
   * textarea — see PromptInputTextarea's autoFocusKey effect for the actual
   * focus call. Combined with ``sessionId`` so each new key also triggers a
   * re-focus, which the existing effect in the textarea handles essentially
   * for free.
   */
  focusTick?: number;
  /**
   * Called when the user clicks a viewable signed file link (either an
   * attachment pill or an inline ``[label](url)`` link in message text).
   * ChatPage selects the Markdown, HTML, PDF, or source viewer from the
   * signed URL.
   */
  onFileLinkClick?: (url: string, fileName: string) => void;
  /** A one-shot first message supplied by a project task after session creation. */
  initialMessage?: string | null;
  /** Called after the project hand-off has entered the normal chat lifecycle. */
  onInitialMessageSent?: () => void;
  /** Files staged by the project task starter for the first chat send. */
  initialFiles?: FileUIPart[] | null;
  /**
   * Called when the user clicks a thread indicator in the conversation.
   * ChatPage opens the ``ChildSessionPanel`` with the child session ID.
   */
  onOpenThread?: (
    childSessionId: string,
    threadAnchor?: ThreadAnchorDisplay | null,
    threadTitle?: string | null,
    agentId?: string | null,
  ) => void;
  /** Opens a strictly validated sub-agent result in the read-only aside. */
  onOpenSubAgent?: (reference: SubAgentThreadReference) => void;
  onOpenWorkflowRun?: (taskId: string) => void;
  /** Publishes re-derived references so a URL-backed aside survives reload. */
  onSubAgentReferencesResolved?: (
    references: readonly SubAgentThreadReference[],
  ) => void;
  /** Refreshes parent-owned thread views after a successful creation. */
  onThreadCreated?: (parentSessionId: string) => void;
  /** Hydrates the parent panel cache after anchor-query results arrive. */
  onThreadAnchorsLoaded?: (anchors: ThreadAnchorRef[]) => void;
  /** Configured views followed by conversation-scoped pins. */
  agentViews?: ViewTab[];
  viewsLoadError?: string | null;
  onRetryViews?: () => void;
  /** Currently open in-chat view, if any. */
  activeViewId?: string;
  /** Opens a view in the in-chat panel. */
  onViewSelect?: (view: ViewTab) => void;
  /** Optimistically removes an agent-created conversation pin. */
  onViewUnpin?: (view: ViewTab) => void;
  /** Receives structured terminal tool results from normal and continued runs. */
  onToolCompleted?: (event: { toolName: string; result: unknown }) => void;
  /** One-shot publish instruction initiated from a code-preview sidebar. */
  viewPublishRequest?: ViewPublishRequest | null;
  /** Clears a publish request after this chat surface starts its run. */
  onViewPublishRequestConsumed?: (requestId: number) => void;
  /** When provided, renders an X close button in the chat header (used by task chat). */
  onClose?: () => void;
  /** State-first navigation used by the task-chat fullscreen button; falls back to route navigation when absent. */
  onOpenFullSession?: (sessionId: string) => void;
  /** Task-only status shown and updated from the task chat header. */
  taskStatus?: TaskStatus;
  onSaveTitle?: (title: string) => Promise<boolean>;
  /** Task-created reference material, rendered above the task conversation. */
  taskDescription?: string | null;
  taskDueDate?: string | null;
  taskDueDateSupported?: boolean;
  taskArtifacts?: Record<string, unknown>[];
  projectTask?: Task;
  /** Shows the task brief even before a task record has loaded. */
  isTaskConversation?: boolean;
  /** Persists task description edits through the Projects API. */
  onSaveDescription?: (description: string) => Promise<boolean>;
  onSaveDueDate?: (dueDate: string | null) => Promise<boolean>;
  onSaveAssignee?: (assigneeId: string | null) => Promise<boolean>;
  onSaveTags?: (tagIds: string[]) => Promise<boolean>;
  onRemoveArtifact?: (path: string) => Promise<boolean>;
  getArtifactDownloadLink?: (path: string) => Promise<{ url: string; filename: string; media_type: string | null } | null>;
  fetchArtifactRaw?: (path: string) => Promise<Blob | null>;
  /** Persists a task-status selection from the task chat header. */
  onTaskStatusChange?: (status: TaskStatus) => Promise<boolean> | boolean;
}

/** Load project-scoped task controls only while a task brief is mounted. */
function ProjectTaskBrief({ getToken, projectTask, onSaveAssignee, onSaveTags, ...props }: {
  getToken: AccessTokenGetter;
  projectTask?: Task;
} & ComponentProps<typeof TaskBriefCard>) {
  const { tenantUsers, refresh: refreshTenantUsers } = useTenantUsers(getToken);
  const { tags, loading, error, createError, create } = useTags(getToken, projectTask?.project_id ?? null);
  return <TaskBriefCard {...props}
    assignedTo={projectTask?.assigned_to}
    tenantUsers={tenantUsers}
    onAssigneePickerOpen={() => { void refreshTenantUsers().catch(() => {}); }}
    onSaveAssignee={projectTask && Object.prototype.hasOwnProperty.call(projectTask, "assigned_to") ? onSaveAssignee : undefined}
    tags={Array.isArray(projectTask?.tags) ? projectTask.tags : undefined}
    effectiveTags={projectTask?.effective_tags}
    allTags={tags}
    onSaveTags={projectTask && Array.isArray(projectTask.tags) ? onSaveTags : undefined}
    onCreateTag={projectTask && Array.isArray(projectTask.tags) ? create : undefined}
    tagLoading={loading}
    tagLoadError={error}
    tagCreateError={createError}
    tagSupported={!projectTask || Array.isArray(projectTask.tags)}
  />;
}

function taskBreadcrumbSegments(task: Task, currentTitle: string, unavailable: string): SessionBreadcrumbSegment[] {
  const path = task.ancestor_path ?? [];
  const valid = path.every((ancestor) => Boolean(ancestor.id && ancestor.title && ancestor.id !== task.id))
    && new Set(path.map((ancestor) => ancestor.id)).size === path.length
    && (task.parent_task_id ? path.at(-1)?.id === task.parent_task_id : path.length === 0);
  return [
    ...(valid ? path.map((ancestor) => ({
      id: ancestor.id,
      title: ancestor.title,
      href: `/project/${encodeURIComponent(task.project_id)}?task=${encodeURIComponent(ancestor.id)}`,
    })) : [{ id: "", title: unavailable, href: null }]),
    { id: task.session_id ?? task.id, title: currentTitle },
  ];
}

interface ThreadAnchorCacheEntry {
  threadAnchor: ThreadAnchorDisplay | null;
  agent_id?: string | null;
}

const ASSISTANT_MESSAGE_SELECTOR = '[data-assistant-message="true"][data-message-id]';

function selectionNodeElement(node: Node | null): Element | null {
  if (!node) return null;
  return node.nodeType === Node.ELEMENT_NODE ? node as Element : node.parentElement;
}

function closestAssistantMessage(node: Node | null): HTMLElement | null {
  return selectionNodeElement(node)?.closest<HTMLElement>(ASSISTANT_MESSAGE_SELECTOR) ?? null;
}

function selectionParagraphIndex(message: HTMLElement, node: Node | null): number {
  const paragraph = selectionNodeElement(node)?.closest("p");
  if (!paragraph || !message.contains(paragraph)) return 0;

  const index = Array.from(message.querySelectorAll("p")).indexOf(paragraph as HTMLParagraphElement);
  return index >= 0 ? index : 0;
}

/**
 * Attachment upload limits — mirror the agno-server ``UploadService`` default
 * (see ``libs/uploads/service.py``).  The shared ``ChatPromptComposer``
 * validates locally before sending so the user gets immediate feedback
 * instead of a 400/413 from the upload endpoint after the round-trip.
 *
 * NOTE: these are hardcoded mirrors, NOT fetched from the server.  If you
 * change ``_DEFAULT_MAX_UPLOAD_SIZE_MB`` in service.py, update
 * ``CHAT_MAX_UPLOAD_*`` in ``chat-prompt-composer.tsx`` too.
 */
const MAX_UPLOAD_FILES = CHAT_MAX_UPLOAD_FILES;
const MAX_UPLOAD_SIZE_MB = CHAT_MAX_UPLOAD_SIZE_MB;
const MAX_UPLOAD_SIZE_BYTES = CHAT_MAX_UPLOAD_SIZE_BYTES;

/**
 * Fallback push API used when ChatInterface renders without a pushApi
 * prop (should not happen in normal flow, but keeps the component safe).
 */
const dummyPushApi: PushSubscriptionAPI = {
  isSupported: false,
  permission: "default",
  pushEnabled: false,
  enabling: false,
  enable: async () => {},
  disable: async () => {},
};

const ChatInterface = memo(function ChatInterface({
  user,
  getAccessToken,
  sessionId,
  bootstrap,
  bootstrapPending = false,
  sessionBreadcrumb,
  projectBreadcrumbSegment,
  onMenuToggle,
  isMenuOpen,
  agents,
  agentsLoading,
  agentsError,
  currentAgentId,
  fallbackAgentId,
  onAgentChange,
  showAgentSwitcher,
  showModelSwitcher = false,
  onArchive,
  session,
  threadAnchor,
  currentUserId,
  onAddParticipation,
  onRemoveParticipation,
  onTaskCreated,
  isAdminUser,
  canManageTenantAgents,
  pushApi,
  preferencesApi,
  submitShortcut,
  onStatusChange,
  isNewSession,
  focusTick,
  onFileLinkClick,
  initialMessage,
  onInitialMessageSent,
  initialFiles,
  onOpenThread,
  onOpenSubAgent,
  onOpenWorkflowRun,
  onSubAgentReferencesResolved,
  onThreadCreated,
  onThreadAnchorsLoaded,
  agentViews,
  viewsLoadError,
  onRetryViews,
  activeViewId,
  onViewSelect,
  onViewUnpin,
  onToolCompleted,
  viewPublishRequest,
  onViewPublishRequestConsumed,
  onClose,
  onOpenFullSession,
  taskStatus,
  taskDescription,
  taskDueDate,
  taskDueDateSupported,
  taskArtifacts,
  projectTask,
  isTaskConversation = false,
  onSaveDescription,
  onSaveTitle,
  onSaveDueDate,
  onSaveAssignee,
  onSaveTags,
  onRemoveArtifact,
  getArtifactDownloadLink,
  fetchArtifactRaw,
  onTaskStatusChange,
}: ChatInterfaceProps) {
  const { t } = useTranslation();
  const hierarchyCapability = useTaskHierarchyCapability(projectTask?.project_id ?? null, getAccessToken);
  const { createSubtask } = useTasks(getAccessToken);
  const subtaskCreatePendingRef = useRef(false);
  const subtaskIdempotencyRef = useRef<string | null>(null);
  useEffect(() => {
    subtaskIdempotencyRef.current = null;
  }, [projectTask?.id]);
  const handleAddSubtask = useCallback(async () => {
    if (!projectTask
      || projectTask.status === "cancelled"
      || session?.is_archived
      || hierarchyCapability.state !== "supported"
      || subtaskCreatePendingRef.current) return;

    subtaskCreatePendingRef.current = true;
    const key = subtaskIdempotencyRef.current ?? crypto.randomUUID();
    subtaskIdempotencyRef.current = key;
    try {
      const result = await createSubtask(
        projectTask.project_id,
        projectTask.id,
        t("projects:subtasks.new"),
        key,
        "default",
      );
      if (!result.task?.session_id) {
        if (!shouldRetainSubtaskIdempotencyKey(result.status)) subtaskIdempotencyRef.current = null;
        toast.error(t(`projects:${subtaskCreateErrorKey(result.status)}`));
        return;
      }
      subtaskIdempotencyRef.current = null;
      notifyTaskChanged(result.task);
      navigate(`/project/${encodeURIComponent(projectTask.project_id)}?task=${encodeURIComponent(result.task.id)}`);
    } finally {
      subtaskCreatePendingRef.current = false;
    }
  }, [createSubtask, hierarchyCapability.state, projectTask, session?.is_archived, t]);
  const originAnchor = threadAnchor
    ?? threadAnchorDisplayFrom(session?.metadata?.thread_anchor);
  const promptInputController = useOptionalPromptInputController();
  const promptInputControllerRef = useRef(promptInputController);
  promptInputControllerRef.current = promptInputController;
  const mountedRef = useRef(false);
  useEffect(() => {
    mountedRef.current = true;
    return () => {
      mountedRef.current = false;
    };
  }, []);
  const isMobile = useIsMobile();
  const headerRef = useRef<HTMLElement>(null);
  const headerLayout = useConversationHeaderLayout(headerRef);
  // On mobile, hide the mic button while the on-screen keyboard is open —
  // it takes up footer space and is rarely needed while typing.
  const isKeyboardOpen = useIsKeyboardOpen();
  const [composerDraft, setComposerDraft] = useState(Boolean(promptInputController?.textInput.value.trim()));
  const [composerNarrow, setComposerNarrow] = useState(false);
  const composerRowRef = useRef<HTMLDivElement>(null);
  const [localActionMessages, setLocalActionMessages] = useState<AgnoChatMessage[]>([]);
  const enterToSendHint = useEnterToSendHint({ submitShortcut, isMobile, sessionId });
  const {
    hasUserInteracted,
    markUserInteraction,
  } = useConversationReadEngagement(sessionId);

  // Reset when switching sessions because the input provider is replaced with
  // a different draft.
  useEffect(() => {
    setComposerDraft(Boolean(promptInputController?.textInput.value.trim()));
    setLocalActionMessages([]);
  }, [sessionId]);


  // Fetch wrapper that injects user identity. The shared helper owns the
  // WorkOS token lifecycle, including startup retries and a forced refresh
  // after a backend 401.
  const authUserRef = useRef(user);
  const accessTokenRef = useRef(getAccessToken);
  authUserRef.current = user;
  accessTokenRef.current = getAccessToken;
  const fetchWithAuth = useCallback(
    async (url: string, init: RequestInit): Promise<Response> => {
      const headers = new Headers(init.headers);
      headers.set("X-User-Email", authUserRef.current.email ?? "");
      headers.set("X-User-Name", authUserRef.current.firstName
        ? `${authUserRef.current.firstName} ${authUserRef.current.lastName ?? ""}`.trim()
        : "");
      return fetchWithWorkOSAuth(url, { ...init, headers }, accessTokenRef.current);
    },
    []
  );

  const [sessionAccess, setSessionAccess] = useState<Session | null>(null);
  const [sessionAccessUnavailable, setSessionAccessUnavailable] = useState(false);
  useEffect(() => {
    const controller = new AbortController();
    setSessionAccess(null);
    setSessionAccessUnavailable(false);
    if (bootstrap?.session.id === sessionId) {
      setSessionAccess(bootstrap.session);
      return () => controller.abort();
    }
    if (bootstrapPending) return () => controller.abort();

    fetchSessionDetailCached(
      `/api/sessions/${encodeURIComponent(sessionId)}`,
      { headers: {
        "X-User-Email": authUserRef.current.email ?? "",
        "X-User-Name": authUserRef.current.firstName
          ? `${authUserRef.current.firstName} ${authUserRef.current.lastName ?? ""}`.trim() : "",
      }, signal: controller.signal },
      accessTokenRef.current,
    )
      .then((response) => {
        if (!response.ok) throw new Error(`HTTP ${response.status}`);
        return response.json() as Promise<Session>;
      })
      .then(setSessionAccess)
      .catch((error) => {
        if (controller.signal.aborted) return;
        setSessionAccessUnavailable(true);
        console.error("[ui] Failed to load session capabilities", sessionId, error);
      });

    return () => controller.abort();
  }, [bootstrap, bootstrapPending, fetchWithAuth, sessionId]);

  // Session detail is authoritative for participation and sub-agent status.
  // Show the draft field using any session data already available, but keep
  // sending disabled until the detail request confirms access.
  const isReadOnlySubAgent = isSubAgentSessionData(sessionAccess);
  const canParticipateSession = Boolean(
    sessionAccess
    && !sessionAccessUnavailable
    && !isReadOnlySubAgent
    && (sessionAccess.can_participate ?? true),
  );
  const canDraftSession = !isSubAgentSessionData(sessionAccess ?? session)
    && (sessionAccess?.can_participate ?? session?.can_participate ?? true);
  useEffect(() => {
    const row = composerRowRef.current;
    if (!row || typeof ResizeObserver === "undefined") return;
    const observer = new ResizeObserver(([entry]) => setComposerNarrow(entry.contentRect.width < 650));
    observer.observe(row);
    return () => observer.disconnect();
  }, [sessionId, canParticipateSession]);
  const canManageSession = !isReadOnlySubAgent
    && (sessionAccess?.can_manage ?? false);
  // A shared or full-screen child session can be absent from the top-level
  // session list while its already-authoritative detail request has completed.
  // Use that verified binding before a caller-provided fallback; otherwise the
  // bridge can route a run to the wrong agent and the session access guard
  // rejects it.
  // Prefer the active-list value after a confirmed local agent switch, whose
  // detail cache may still describe the prior selection.
  const resolvedAgentId = resolveSessionAgentId(
    currentAgentId,
    sessionAccess?.agent_id,
    fallbackAgentId,
  );
  const isSharedTaskConversation =
    sessionAccess?.is_shared_project === true ||
    (sessionAccess?.is_shared_task_conversation === true &&
      sessionAccess?.is_shared_project === undefined);
  const [convertSession, setConvertSession] = useState<(Pick<Session, "id" | "title" | "project_id"> & { sourceQuote?: string; isThread: boolean }) | null>(null);
  const [unboundConversionSessionId, setUnboundConversionSessionId] = useState<string | null>(null);
  const conversationTitle = sessionAccess?.title
    ?? sessionBreadcrumb[sessionBreadcrumb.length - 1]?.title
    ?? "";
  const isManualThread = sessionAccess?.session_type === "thread"
    || Boolean(sessionAccess?.parent_session_id && !isReadOnlySubAgent);
  const threadSourceQuote = threadAnchorDisplayFrom(sessionAccess?.metadata?.thread_anchor)?.source_quote
    ?? originAnchor?.source_quote;
  const handleConvertToTaskOpen = useCallback(() => {
    setConvertSession({
      id: sessionId,
      title: conversationTitle,
      project_id: sessionAccess?.project_id,
      isThread: isManualThread,
      ...(isManualThread ? { sourceQuote: threadSourceQuote } : {}),
    });
  }, [conversationTitle, isManualThread, sessionAccess?.project_id, sessionId, threadSourceQuote]);

  const {
    messages,
    sendMessage,
    status,
    isPolling,
    stop,
    isUploading,
    isTranscribing,
    recoverableFailedRun,
    continueFromFailedRun,
    continueRun,
    hasMoreMessages,
    totalMessages,
    isLoadingMore,
    loadMoreMessages,
    olderPageError,
    newerPageError,
    isPrepending,
    prependRevision,
    hasLoadedMessages,
    messageLoadError,
    handleRetryMessageLoad,
    loadNavigationTarget,
    hasNewerWindowActivity,
    jumpToLatest,
    navigationCoverage,
    navigationContentMessageId,
    navigationContentError,
    continueNavigationContent,
    cancelNavigationContent,
    isLoadingNewerNavigation,
    loadNewerNavigationMessages,
  } = useSessionMessageFeed({
    sessionId,
    agentId: resolvedAgentId,
    fetchWithAuth,
    // Skip the mount-time /runs check for sessions we just created — the
    // server cannot have an in-flight run on a session it returned via a
    // fresh 201 response milliseconds ago.
    isNewSession,
    bootstrap,
    bootstrapPending,
    onStatusChange,
    onToolCompleted,
    hasUserInteracted,
  });
  const composerActions = agents.find((agent) => agent.id === resolvedAgentId)?.actions ?? [];
  const transcriptMessages = useMemo(() => {
    const canonicalTasks = new Set(messages.map((message) => message.metadata?.task_id).filter(Boolean));
    return [...messages, ...localActionMessages.filter((message) => !canonicalTasks.has(message.metadata?.task_id))];
  }, [messages, localActionMessages]);
  const transcriptActionRuns = useMemo(() => transcriptMessages.flatMap((message) => {
    const metadata = message.metadata;
    return metadata?.source === "composer_action" && typeof metadata.task_id === "string" && typeof metadata.action_id === "string"
      ? [{ taskId: metadata.task_id, actionId: metadata.action_id }] : [];
  }), [transcriptMessages]);
  const activeActionSessionRef = useRef(sessionId);
  activeActionSessionRef.current = sessionId;
  const handleComposerActionQueued = useCallback((action: ComposerAction, taskId: string) => {
    if (activeActionSessionRef.current !== sessionId) return;
    const text = t("chat:composerActions.started", { label: action.label, run: taskId.slice(0, 8) });
    setLocalActionMessages((previous) => [...previous, {
      id: `composer-action-${taskId}`,
      role: "user",
      parts: [{ type: "text", text }],
      content: text,
      metadata: { source: "composer_action", action_id: action.id, action_label: action.label, task_id: taskId },
    }]);
  }, [sessionId, t]);
  // Unlike the hook-local upload/transcription flags, this signal survives a
  // session-component unmount. It lets a returned-to session show that its
  // voice note is still being prepared in the background.
  const voiceNoteProgress = useVoiceNoteProgress(sessionId);

  // Thread creation is intentionally owned by ChatInterface: both the
  // per-message action and the selection popover create threads for the
  // currently rendered parent session.
  const { createThread, creating: creatingThread } = useCreateThread(sessionId, getAccessToken);
  const [threadSelection, setThreadSelection] = useState<ThreadSelectionTarget | null>(null);
  const threadSelectionTimer = useRef<ReturnType<typeof setTimeout> | null>(null);
  const [threadCreationTarget, setThreadCreationTarget] = useState<ThreadCreationTarget | null>(null);
  const [anchorRefreshNonce, setAnchorRefreshNonce] = useState(0);
  // React state does not update synchronously, so this ref closes the small
  // window in which two rapid clicks could start two POSTs before `creating`
  // has re-rendered the controls as disabled.
  const creatingThreadRef = useRef(false);

  const updateThreadSelection = useCallback(() => {
    const selection = window.getSelection();
    if (!selection || selection.rangeCount === 0 || selection.isCollapsed) {
      setThreadSelection(null);
      return;
    }

    const sourceQuote = selection.toString().trim();
    if (!sourceQuote) {
      setThreadSelection(null);
      return;
    }

    const range = selection.getRangeAt(0);
    const startMessage = closestAssistantMessage(range.startContainer);
    const endMessage = closestAssistantMessage(range.endContainer);
    // A selection that crosses bubbles is ambiguous, so only offer the
    // action when both ends live in the same assistant message.
    if (!startMessage || startMessage !== endMessage) {
      setThreadSelection(null);
      return;
    }

    const messageId = startMessage.dataset.messageId;
    const rect = range.getBoundingClientRect();
    if (!messageId || (rect.width === 0 && rect.height === 0)) {
      setThreadSelection(null);
      return;
    }

    const quoteBody = startMessage.querySelector('[data-quote-body="true"]');
    const sourceMessage = transcriptMessages.find((message) => message.id === messageId);
    const sourceItem = buildRenderItems(sourceMessage ? [sourceMessage] : [], status).find(
      (item) => item.kind === "message" && item.id === messageId,
    );
    let quoteRange = null;
    if (quoteBody?.contains(range.startContainer) && quoteBody.contains(range.endContainer)
      && sourceItem?.kind === "message") {
      const before = document.createRange();
      before.selectNodeContents(quoteBody);
      before.setEnd(range.startContainer, range.startOffset);
      const after = document.createRange();
      after.selectNodeContents(quoteBody);
      after.setStart(range.endContainer, range.endOffset);
      quoteRange = captureQuoteRange(sourceItem.text, sourceQuote, before.toString(), after.toString());
    }

    // Keep the fixed button safely on-screen while centering it above the
    // selected range. The exact width is intentionally conservative because
    // translated labels vary in length.
    const horizontalPadding = 88;
    const minLeft = Math.min(horizontalPadding, window.innerWidth / 2);
    const maxLeft = Math.max(minLeft, window.innerWidth - horizontalPadding);
    const centeredLeft = rect.left + rect.width / 2;

    setThreadSelection({
      messageId,
      sourceQuote,
      quoteRange,
      blockIndex: selectionParagraphIndex(startMessage, range.startContainer),
      position: {
        top: Math.max(44, rect.top - 8),
        left: Math.min(Math.max(centeredLeft, minLeft), maxLeft),
      },
      barPosition: window.matchMedia("(pointer: coarse) and (hover: none)").matches
        ? touchThreadSelectionBarPosition(rect, window.innerHeight)
        : undefined,
    });
  }, [transcriptMessages, status]);

  useEffect(() => {
    const cancelScheduledUpdate = () => {
      if (threadSelectionTimer.current !== null) {
        clearTimeout(threadSelectionTimer.current);
        threadSelectionTimer.current = null;
      }
    };
    const handleSelectionChange = () => {
      cancelScheduledUpdate();
      if (window.matchMedia("(pointer: coarse) and (hover: none)").matches) {
        threadSelectionTimer.current = setTimeout(() => {
          threadSelectionTimer.current = null;
          updateThreadSelection();
        }, 200);
      } else {
        updateThreadSelection();
      }
    };
    const handleMouseUp = () => {
      if (!window.matchMedia("(pointer: coarse) and (hover: none)").matches) {
        updateThreadSelection();
      }
    };
    const handlePointerDown = (event: PointerEvent) => {
      if (
        event.target instanceof Element
        && event.target.closest("[data-thread-create-button]")
      ) {
        return;
      }
      cancelScheduledUpdate();
      setThreadSelection(null);
    };

    document.addEventListener("selectionchange", handleSelectionChange);
    document.addEventListener("mouseup", handleMouseUp);
    document.addEventListener("pointerdown", handlePointerDown);
    return () => {
      cancelScheduledUpdate();
      document.removeEventListener("selectionchange", handleSelectionChange);
      document.removeEventListener("mouseup", handleMouseUp);
      document.removeEventListener("pointerdown", handlePointerDown);
    };
  }, [updateThreadSelection, sessionId]);

  useEffect(() => {
    setThreadSelection(null);
  }, [sessionId]);

  const dismissThreadSelection = useCallback(() => {
    if (threadSelectionTimer.current !== null) {
      clearTimeout(threadSelectionTimer.current);
      threadSelectionTimer.current = null;
    }
    window.getSelection()?.removeAllRanges();
    setThreadSelection(null);
  }, []);

  const openCreatedThread = useCallback(
    (response: ThreadCreateResponse) => {
      const threadAnchor = threadAnchorDisplayFrom(
        response.session.metadata?.thread_anchor,
      );
      setAnchorRefreshNonce((nonce) => nonce + 1);
      onThreadCreated?.(sessionId);
      onOpenThread?.(
        response.session.id,
        threadAnchor,
        response.session.title,
        response.session.agent_id,
      );
    },
    [onOpenThread, onThreadCreated, sessionId],
  );

  const createThreadForAnchor = useCallback(
    async (
      request: ThreadCreateRequest,
      target: ThreadCreationTarget,
    ): Promise<ThreadCreateResponse | null> => {
      if (creatingThreadRef.current) return null;

      creatingThreadRef.current = true;
      setThreadCreationTarget(target);
      try {
        const response = await createThread(request);
        if (response) openCreatedThread(response);
        return response;
      } finally {
        creatingThreadRef.current = false;
        setThreadCreationTarget(null);
      }
    },
    [createThread, openCreatedThread],
  );

  const handleCreateMessageThread = useCallback(
    (messageId: string, sourceQuote: string) =>
      createThreadForAnchor(
        {
          source_message_id: messageId,
          source_quote: sourceQuote,
          anchor: { kind: "message" },
        },
        { messageId, kind: "message" },
      ),
    [createThreadForAnchor],
  );

  const handleCreateSelectedTextThread = useCallback(async () => {
    if (!threadSelection) return;

    const selectedTarget = threadSelection;
    const response = await createThreadForAnchor(
      threadRequestForSelection(selectedTarget),
      { messageId: selectedTarget.messageId, kind: "block" },
    );

    if (response) {
      window.getSelection()?.removeAllRanges();
      setThreadSelection(null);
    }
  }, [threadSelection, createThreadForAnchor]);

  // ── Per-session draft: restore on mount, clear after an accepted send ─────
  const initialDraft = useMemo(() => getDraft(sessionId).text, [sessionId]);
  const agentSkills = useAgentSkills(resolvedAgentId, sessionId, getAccessToken);
  const selectedSkillsRef = useRef(agentSkills.selected);
  selectedSkillsRef.current = agentSkills.selected;
  const draftSavedRef = useRef(initialDraft);
  const draftAttemptRef = useRef<DraftAttempt | null>(getDraftAttempt(sessionId));

  // ── Debounced draft persistence ───────────────────────────────────────────
  // Writing the draft on every keystroke calls JSON.parse + JSON.stringify on
  // the whole draft map and a synchronous localStorage.setItem — main-thread
  // work that shows up as input lag while typing.  Coalesce writes behind a
  // short debounce and flush eagerly on submit and on session switch/unmount
  // so no keystrokes are lost.
  const draftTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const pendingDraftRef = useRef<{ sessionId: string; text: string } | null>(null);

  const flushDraft = useCallback(() => {
    if (draftTimerRef.current) {
      clearTimeout(draftTimerRef.current);
      draftTimerRef.current = null;
    }
    const pending = pendingDraftRef.current;
    if (pending) {
      // pending carries its own sessionId, so a flush triggered by a session
      // switch still writes to the session the text was typed in.
      setDraft(pending.sessionId, pending.text, selectedSkillsRef.current);
      pendingDraftRef.current = null;
    }
  }, []);

  const scheduleDraftSave = useCallback(
    (sid: string, text: string) => {
      pendingDraftRef.current = { sessionId: sid, text };
      if (draftTimerRef.current) clearTimeout(draftTimerRef.current);
      draftTimerRef.current = setTimeout(flushDraft, 400);
    },
    [flushDraft],
  );

  const cancelPendingDraft = useCallback(() => {
    if (draftTimerRef.current) {
      clearTimeout(draftTimerRef.current);
      draftTimerRef.current = null;
    }
    pendingDraftRef.current = null;
  }, []);

  // Flush any pending draft when the session changes or the component unmounts
  // so a draft typed just before switching threads is not dropped.
  useEffect(() => flushDraft, [sessionId, flushDraft]);

  // A send can finish after this conversation has been remounted. Clear the
  // rehydrated composer only when it still exactly matches the attempted
  // payload; a newer user edit must remain untouched.
  useEffect(() => {
    const handleDraftChange = () => {
      const attempt = draftAttemptRef.current;
      if (!attempt || getDraftAttempt(sessionId)) return;

      if (mountedRef.current && promptInputController) {
        const currentFiles = promptInputController.attachments.files;
        const filesMatch = currentFiles.length === attempt.files.length
          && currentFiles.every((file, index) => {
            const attemptedFile = attempt.files[index];
            return file.rawFile === attemptedFile.rawFile
              && file.filename === attemptedFile.filename
              && file.mediaType === attemptedFile.mediaType;
          });
        if (
          promptInputController.textInput.value === attempt.text
          && filesMatch
        ) {
          promptInputController.textInput.clear();
          promptInputController.attachments.clear();
        }
      }
      draftAttemptRef.current = null;
    };

    window.addEventListener(DRAFTS_CHANGED_EVENT, handleDraftChange);
    return () => window.removeEventListener(DRAFTS_CHANGED_EVENT, handleDraftChange);
  }, [promptInputController, sessionId]);

  const restoreDraftAttempt = useCallback((attempt: DraftAttempt) => {
    draftAttemptRef.current = attempt;
    // The attempted payload is also kept in the session draft store.  If this
    // component has already unmounted, the next provider uses that snapshot;
    // when it is still mounted, hydrate the cleared composer immediately.
    const controller = promptInputControllerRef.current;
    if (mountedRef.current && controller) {
      if (controller.textInput.value.trim() === "") {
        controller.textInput.setInput(attempt.text);
      }
      controller.attachments.restore(attempt.files);
    }
  }, []);

  const settleDraftAttempt = useCallback(
    (
      attempt: DraftAttempt,
      result: Awaited<ReturnType<typeof sendMessage>>,
      source: string,
    ) => {
      if (result.outcome === "sent") {
        completeDraftAttempt(sessionId, attempt.id);
        agentSkills.clearIfMatches(attempt.selectedSkills);
        return;
      }

      restoreDraftAttempt(attempt);
      if (selectedSkillsRef.current.length === 0) agentSkills.restore(attempt.selectedSkills);
      console.error(`[ui] ${source} was not delivered; restored the draft`, result.error);
    },
    [agentSkills.clearIfMatches, agentSkills.restore, restoreDraftAttempt, sendMessage, sessionId],
  );

  const hasMessages = messages.length > 0;
  const hasAgentHeaderControl = (showAgentSwitcher
    && canManageSession
    && (!hasMessages || resolvedAgentId !== null))
    || (isReadOnlySubAgent && resolvedAgentId !== null);
  const hasTaskStatusHeaderControl = !isReadOnlySubAgent
    && taskStatus !== undefined
    && onTaskStatusChange !== undefined;
  const hasMovableHeaderControls = hasAgentHeaderControl
    || (!isReadOnlySubAgent && isSharedTaskConversation)
    || hasTaskStatusHeaderControl;
  const moveHeaderControlsToOverflow = headerLayout === "overflow" && hasMovableHeaderControls;
  const headerControlDensity = headerLayout === "full"
    ? "full"
    : headerLayout === "compact"
      ? "compact"
      : "icon";
  const headerControlProps = {
    agents,
    agentsLoading,
    agentsError,
    currentAgentId: resolvedAgentId,
    onAgentChange,
    showAgentSwitcher,
    canManageSession,
    selectionLocked: hasMessages,
    showReadOnlyAgentInfo: isReadOnlySubAgent,
    getToken: getAccessToken,
    isAdminUser: !!isAdminUser,
    isSharedTaskConversation: !isReadOnlySubAgent && isSharedTaskConversation,
    taskStatus: isReadOnlySubAgent ? undefined : taskStatus,
    onTaskStatusChange: isReadOnlySubAgent ? undefined : onTaskStatusChange,
  };

  const {
    promptQueue, submitError, editingPromptId, setEditingPromptId, editingPromptText, setEditingPromptText,
    promptQueueSupported, editQueuedPrompt, deleteQueuedPrompt, queueIfBusy, submitDisabled,
    promptResendSupported, promptResendError, promptDeleteError, resendingPromptId, resendQueuedPrompt,
  } = usePromptQueue({ sessionId, agentId: resolvedAgentId, status, messages, fetchWithAuth, bootstrapQueue: bootstrap?.prompt_queue, bootstrapPending });

  const handleSubmit = useCallback(
    ({ text, files }: { text: string; files: any[] }) => {
      console.log("[ui] Submitting message to session", sessionId, ":", text);
      // Keep the complete attempted payload until the server accepts it.
      // PromptInput clears its visible text immediately, while this snapshot
      // lets a failed or backgrounded send rehydrate the composer later.
      cancelPendingDraft();
      const attempt = beginDraftAttempt(sessionId, text, files, selectedSkillsRef.current);
      const submittedText = prependSelectedSkills(attempt.selectedSkills, text);
      draftAttemptRef.current = attempt;
      draftSavedRef.current = text;
      setComposerDraft(false);
      enterToSendHint.hide();
      enterToSendHint.notifySubmit(text);
      // Fire-and-forget: do NOT await sendMessage.  The upload + SSE stream
      // run in the background.  This lets onSubmit resolve immediately so
      // PromptInput clears the attachment chips right away — the user sees
      // the "Uploading files…" spinner (driven by isUploading state) for
      // upload feedback, not the chips.  Awaiting would keep the chips
      // visible until the entire model response finished streaming.
      // Text-only follow-ups use the durable prompt queue while a run is
      // active. A 404 means the server lacks the queue endpoint, so restore
      // the draft instead of starting a second run.
      void queueIfBusy(submittedText, files).then((queued) => queued ? { outcome: "sent" as const } : sendMessage({ text: submittedText, files: files.length > 0 ? files : undefined }))
        .then((result) => settleDraftAttempt(attempt, result, "Message send"))
        .catch((err) => {
          restoreDraftAttempt(attempt);
          if (selectedSkillsRef.current.length === 0) agentSkills.restore(attempt.selectedSkills);
          console.error("[ui] Message submit error; restored the draft:", err);
        });
    },
    [
      agentSkills.restore,
      cancelPendingDraft,
      restoreDraftAttempt,
      sendMessage,
      sessionId,
      settleDraftAttempt,
      enterToSendHint,
      queueIfBusy,
    ]
  );

  // A preview's Publish button is an explicit user instruction, but the
  // existing agent run remains the execution path. This keeps validation,
  // source-control approval, HITL, streaming, and persistence identical to a
  // typed publish request instead of introducing a second deployment API.
  const consumedPublishRequestRef = useRef<number | null>(null);
  useEffect(() => {
    const request = viewPublishRequest;
    if (
      !request
      || isReadOnlySubAgent
      || request.sessionId !== sessionId
      || consumedPublishRequestRef.current === request.id
      || status === "submitted"
      || status === "streaming"
      || isUploading
      || isTranscribing
    ) {
      return;
    }

    consumedPublishRequestRef.current = request.id;
    void sendMessage({ text: request.prompt })
      .catch((error) => {
        console.error("[ui] code-preview publish request failed:", error);
      })
      .finally(() => {
        onViewPublishRequestConsumed?.(request.id);
      });
  }, [
    viewPublishRequest,
    sessionId,
    status,
    isUploading,
    isTranscribing,
    sendMessage,
    onViewPublishRequestConsumed,
    isReadOnlySubAgent,
  ]);

  // A project task starts with an ordinary session plus the user's task
  // prompt. Once its empty history has loaded, submit through the same
  // lifecycle as the normal composer.
  const submittedInitialMessageRef = useRef<string | null>(null);
  useEffect(() => {
    const text = initialMessage?.trim();
    const files = initialFiles?.length ? initialFiles : undefined;
    // A task-created session is added by the project API rather than
    // useSessions().create(), so its agent detail can arrive one render after
    // the initial history. Wait for that authoritative session agent before
    // handing the draft to useAgnoChat; otherwise /chat/web could install a
    // stale recommendation as a session-agent override when a project's
    // default changed between routing and task creation.
    if (
      isReadOnlySubAgent
      || (!text && !files)
      || !hasLoadedMessages
      || status !== "ready"
      || !resolvedAgentId
    ) return;

    const handoffKey = `${sessionId}:${text ?? ""}:${files?.map((file) => `${file.filename ?? ""}:${file.url}`).join("|") ?? ""}`;
    if (submittedInitialMessageRef.current === handoffKey) return;
    submittedInitialMessageRef.current = handoffKey;
    cancelPendingDraft();
    const attempt = beginDraftAttempt(sessionId, text ?? "", files ?? []);
    draftAttemptRef.current = attempt;
    draftSavedRef.current = text ?? "";
    void sendMessage({ text: text ?? "", files })
      .then((result) => settleDraftAttempt(attempt, result, "Project task initial message"))
      .catch((err) => {
        restoreDraftAttempt(attempt);
        console.error("[ui] project task initial message error; restored the draft:", err);
      });
    onInitialMessageSent?.();
  }, [
    cancelPendingDraft,
    hasLoadedMessages,
    initialFiles,
    initialMessage,
    resolvedAgentId,
    onInitialMessageSent,
    restoreDraftAttempt,
    sendMessage,
    sessionId,
    settleDraftAttempt,
    status,
    isReadOnlySubAgent,
  ]);

  // ── Voice-note submit handler ─────────────────────────────────────────
  // Called by VoiceNoteButton when the user clicks the submit (arrow-up)
  // button while recording.  The recording is stopped, the audio File is
  // wrapped in a FileUIPart-compatible shape, and sendMessage is called
  // immediately — no attachment chip, no separate submit click.  The
  // textarea text (if any) is included as the message text.  Any existing
  // attachment files that were already staged (e.g. images the user
  // attached before recording) are merged with the voice note file.
  // sendMessage handles uploading the files and transcribing the audio
  // via ElevenLabs Scribe v2 before sending the chat message.
  const handleSubmitVoiceNote = useCallback(
    (
      text: string,
      file: File,
      existingFiles: FileUIPart[] = [],
    ) => {
      console.log("[ui] Submitting voice note to session", sessionId);
      cancelPendingDraft();
      const voiceFile: FileUIPart & { rawFile?: File } = {
        type: "file" as const,
        filename: file.name,
        mediaType: file.type,
        url: URL.createObjectURL(file),
        rawFile: file,
      };
      const allFiles = [...existingFiles, voiceFile];
      const attempt = beginDraftAttempt(sessionId, text, allFiles);
      draftAttemptRef.current = attempt;
      draftSavedRef.current = text;
      void sendMessage({
        text,
        files: allFiles.length > 0 ? allFiles : undefined,
      })
        .then((result) => settleDraftAttempt(attempt, result, "Voice-note send"))
        .catch((err) => {
          restoreDraftAttempt(attempt);
          console.error("[ui] sendMessage (voice note) error; restored the draft:", err);
        });
    },
    [
      cancelPendingDraft,
      restoreDraftAttempt,
      sendMessage,
      sessionId,
      settleDraftAttempt,
    ]
  );

  // ── Voice-note recording state ────────────────────────────────────────
  // The VoiceNoteButton notifies us when recording starts/stops via
  // onRecordingChange.  We track this so the main PromptInputSubmit
  // button can swap its icon to ArrowUpIcon and act as the voice-note
  // send button while recording is active.
  const voiceNoteRef = useRef<VoiceNoteButtonHandle>(null);
  const voiceSubmitRef = useRef<HTMLButtonElement>(null);
  const [isRecordingVoice, setIsRecordingVoice] = useState(false);
  const startVoiceDictation = useCallback(() => {
    voiceNoteRef.current?.startRecording();
  }, []);
  const handleVoiceDictationShortcut = useCallback(() => {
    dispatchVoiceDictationShortcut("normal-chat");
  }, []);
  const handleVoiceSubmit = useCallback(() => {
    voiceNoteRef.current?.submitVoiceNote();
  }, []);

  useVoiceDictationShortcut(
    canParticipateSession ? "normal-chat" : undefined,
    startVoiceDictation,
  );

  useEffect(() => {
    if (!isRecordingVoice) return;
    // Run after the textarea's initial auto-focus frame, which otherwise can
    // reclaim focus when recording begins immediately after mounting.
    const focusVoiceSubmit = () => voiceSubmitRef.current?.focus();
    if (typeof requestAnimationFrame === "undefined") {
      focusVoiceSubmit();
      return;
    }
    const frame = requestAnimationFrame(focusVoiceSubmit);
    return () => cancelAnimationFrame(frame);
  }, [isRecordingVoice]);

  // Anchor queries stay parent-owned so parent views can refresh from the
  // same data. The message list receives the resolved refs and attaches them
  // while it constructs its render rows.
  const threadAnchorMessageIds = useMemo(
    () => messages
      .filter((message) => message.role === "user" || message.role === "assistant")
      .map((message) => message.id),
    [messages],
  );
  const { anchors: threadAnchors } = useChildSessionAnchors(
    sessionId,
    threadAnchorMessageIds,
    getAccessToken,
    { enabled: true, refreshNonce: anchorRefreshNonce, initialAnchors: bootstrap?.anchors, pending: bootstrapPending },
  );
  useEffect(() => {
    if (threadAnchors.length > 0) onThreadAnchorsLoaded?.(threadAnchors);
  }, [onThreadAnchorsLoaded, threadAnchors]);
  const conversationNavigation = useConversationNavigation({
    sessionId,
    messages: transcriptMessages,
    status,
    threadAnchors,
    getToken: getAccessToken,
    fetchWithAuth,
    hasLoaded: hasLoadedMessages,
    enabled: CONVERSATION_TIMELINE_ENABLED,
    incompleteStart: navigationCoverage ? !navigationCoverage.startsAtTurnBoundary : false,
    incompleteEnd: navigationCoverage ? !navigationCoverage.endsAtTurnBoundary : false,
    refreshNonce: anchorRefreshNonce,
    bootstrap,
    bootstrapPending,
  });
  const conversationActionsRef = useRef<HTMLButtonElement>(null);
  const [timelineDialogOpen, setTimelineDialogOpen] = useState(false);
  useEffect(() => { setTimelineDialogOpen(false); }, [sessionId]);
  const hasTimelineContent = CONVERSATION_TIMELINE_ENABLED && (
    conversationNavigation.turns.length > 0
    || conversationNavigation.otherThreads.length > 0
    || conversationNavigation.loadingChildren
    || conversationNavigation.capabilityState !== "supported"
    || !conversationNavigation.indexSupported
    || (conversationNavigation.indexState !== null && conversationNavigation.indexState !== "ready")
    || conversationNavigation.indexError
    || Boolean(conversationNavigation.childrenError)
  );
  const showTimelineRail = hasTimelineContent && !isMobile && !onClose && headerLayout === "full";
  const navigateFromTimeline = useCallback((target: ConversationDestination["target"]) => {
    if (!target) return;
    pushConversationDestination({ sessionId, target });
  }, [sessionId]);
  const openTimelineThread = useCallback((childSessionId: string) => {
    const thread = conversationNavigation.turns
      .flatMap((turn) => turn.threads)
      .concat(conversationNavigation.otherThreads)
      .find((item) => item.child.id === childSessionId);
    onOpenThread?.(
      childSessionId,
      null,
      thread?.child.title,
      thread?.child.agent_id,
    );
  }, [conversationNavigation.otherThreads, conversationNavigation.turns, onOpenThread]);

  return (
    <div className="flex h-full flex-col bg-background text-foreground min-w-0 overflow-hidden">
      <ConnectionStatusBanner getToken={getAccessToken} />
      {/* Header */}
      <header
        ref={headerRef}
        className="flex min-w-0 items-center gap-2 border-b px-3 py-2.5 min-h-[53px]"
      >
        <div className="flex min-w-0 flex-1 items-center gap-2">
          {isMobile && !onClose && (
            <MobileSessionMenuButton
              isMenuOpen={isMenuOpen}
              onMenuToggle={onMenuToggle}
            />
          )}
          {sessionBreadcrumb.length > 0 && (
            <SessionBreadcrumb
              segments={sessionBreadcrumb}
              projectSegment={projectBreadcrumbSegment}
              compact={isMobile}
              className="min-w-0 flex-1"
            />
          )}
        </div>
        {hasTimelineContent && !showTimelineRail && (
          <ConversationTimelinePanel
            variant="dialog"
            open={timelineDialogOpen}
            onOpenChange={setTimelineDialogOpen}
            hideTrigger={isMobile}
            returnFocusRef={conversationActionsRef}
            sessionId={sessionId}
            navigation={conversationNavigation}
            onNavigate={navigateFromTimeline}
            onOpenThread={openTimelineThread}
          />
        )}
        {!moveHeaderControlsToOverflow && (
          <ConversationHeaderControls
            {...headerControlProps}
            density={headerControlDensity}
            className="min-w-0 max-w-[60%]"
          />
        )}
        {/* Keep the overflow control immediately before notifications. At
            constrained widths it also contains the selected agent, shared
            context, and task status instead of competing with the title. */}
        <ConversationActionsMenu
          triggerRef={conversationActionsRef}
          agentId={resolvedAgentId}
          sessionId={sessionId}
          getToken={getAccessToken}
          showModelPicker={!isReadOnlySubAgent && showModelSwitcher}
          onArchive={isReadOnlySubAgent ? undefined : onArchive}
          onAddSubtask={projectTask
            && projectTask.status !== "cancelled"
            && !session?.is_archived
            && hierarchyCapability.state === "supported"
            ? () => { void handleAddSubtask(); }
            : undefined}
          session={session}
          currentUserId={currentUserId}
          onAddParticipation={onAddParticipation}
          onRemoveParticipation={onRemoveParticipation}
          onConvertToTask={
            canConvertConversationToTask({
              isReadOnlySubAgent,
              isTaskConversation,
              taskStatus,
              session: sessionAccess ?? session,
            })
            && unboundConversionSessionId !== sessionId
            && onTaskCreated
              ? handleConvertToTaskOpen
              : undefined
          }
          onViewTimeline={isMobile && hasTimelineContent ? () => setTimelineDialogOpen(true) : undefined}
          conversationActionsLabel={t("header.conversationActions")}
          archiveConversationLabel={t("header.archiveConversation")}
          archiveTaskLabel={taskStatus !== undefined ? t("header.archiveTask") : undefined}
          convertToTaskLabel={t("sidebar.convertToTask")}
          canManageSession={canManageSession}
          onClose={onClose}
          overflowContent={moveHeaderControlsToOverflow ? (
            <ConversationHeaderControls
              {...headerControlProps}
              density="full"
              inOverflow
            />
          ) : undefined}
        />
        {isMobile && !onClose && <NotificationInbox />}
        {isMobile && !onClose && (
          <SettingsMenu
            className="shrink-0"
            user={user}
            isAdminUser={!!isAdminUser}
            canManageTenantAgents={!!canManageTenantAgents}
            pushApi={pushApi ?? dummyPushApi}
            preferencesApi={preferencesApi}
            agents={agents}
            agentsLoading={agentsLoading}
            agentsError={agentsError}
          />
        )}
        {onClose && (
          <Button
            variant="ghost"
            size="icon"
            className="size-7 shrink-0"
            onClick={() => (onOpenFullSession ? onOpenFullSession(sessionId) : navigate(`/session/${sessionId}`))}
            aria-label={t("viewer.openInFullscreen")}
            title={t("viewer.openInFullscreen")}
          >
            <Maximize2 className="size-4" />
          </Button>
        )}
        {onClose && (
          <Button
            variant="ghost"
            size="icon"
            className="size-7 shrink-0"
            onClick={onClose}
            aria-label={t("viewer.closeViewer")}
          >
            <X className="size-4" />
          </Button>
        )}
      </header>

      {!isReadOnlySubAgent && convertSession && !onClose && onTaskCreated && (
        <ConvertToTaskDialog
          session={convertSession}
          isThread={convertSession.isThread}
          sourceQuote={convertSession.sourceQuote}
          getToken={getAccessToken}
          onTaskCreated={onTaskCreated}
          onUnboundTaskCreated={() => setUnboundConversionSessionId(sessionId)}
          onClose={() => setConvertSession(null)}
        />
      )}

      {!isReadOnlySubAgent && resolvedAgentId && agentViews && agentViews.length > 0 && (
        <ViewTabs
          views={agentViews}
          activeViewKey={activeViewId}
          onSelect={onViewSelect}
          onUnpin={onViewUnpin}
        />
      )}
      {viewsLoadError && (
        <div className="border-b px-4 py-2 text-xs text-muted-foreground" role="status">
          {t("chat:viewsLoadUnavailable")}
          {onRetryViews && <button type="button" className="ml-2 underline" onClick={onRetryViews}>{t("common:actions.retry")}</button>}
        </div>
      )}

      <div className="flex min-h-0 flex-1">
      <ConversationMessageList
        sessionId={sessionId}
        messages={transcriptMessages}
        status={status}
        isPolling={isPolling}
        agentId={resolvedAgentId}
        fetchWithAuth={fetchWithAuth}
        continueRun={continueRun}
        canManageSession={canManageSession}
        readOnly={isReadOnlySubAgent}
        allAgents={agents}
        onOpenSubAgent={onOpenSubAgent}
        onOpenWorkflowRun={onOpenWorkflowRun}
        onSubAgentReferencesResolved={onSubAgentReferencesResolved}
        onFileLinkClick={onFileLinkClick}
        onOpenThread={onOpenThread}
        threadAnchors={threadAnchors}
        threadCreation={
          canParticipateSession
            ? {
                enabled: true,
                onCreateMessageThread: handleCreateMessageThread,
                onCreateSelectedTextThread: handleCreateSelectedTextThread,
                onDismissSelectedTextThread: dismissThreadSelection,
                threadSelection,
                creatingThread,
                threadCreationTarget,
              }
            : { enabled: false }
        }
        hasMore={hasMoreMessages}
        totalMessages={totalMessages}
        isLoadingMore={isLoadingMore}
        olderPageError={olderPageError}
        newerPageError={newerPageError}
        isPrepending={isPrepending}
        prependRevision={prependRevision}
        loadOlder={loadMoreMessages}
        hasLoaded={hasLoadedMessages}
        error={messageLoadError}
        retry={handleRetryMessageLoad}
        loadNavigationTarget={loadNavigationTarget}
        hasNewerWindowActivity={hasNewerWindowActivity}
        jumpToLatest={jumpToLatest}
        navigationCoverage={navigationCoverage}
        navigationContentMessageId={navigationContentMessageId}
        navigationContentError={navigationContentError}
        continueNavigationContent={continueNavigationContent}
        cancelNavigationContent={cancelNavigationContent}
        isLoadingNewerNavigation={isLoadingNewerNavigation}
        loadNewerNavigationMessages={loadNewerNavigationMessages}
        onUserInteraction={markUserInteraction}
        scrollSpy={{
          enabled: conversationNavigation.scrollSpyEnabled,
          turns: conversationNavigation.turns,
          onVisibleTargetChange: conversationNavigation.onVisibleTargetChange,
        }}
        leadingContent={
          isTaskConversation ? (
            <div className="shrink-0 p-3 pb-0">
              {/* Wait for history before treating an empty array as an empty conversation. */}
              <ProjectTaskBrief
                getToken={getAccessToken}
                projectTask={projectTask}
                headerActions={projectTask && (
                  <SubtasksSection
                    key={projectTask.id}
                    task={projectTask}
                    getToken={getAccessToken}
                    archived={Boolean(session?.is_archived)}
                  />
                )}
                description={taskDescription}
                taskTitle={projectTask?.title}
                onSaveTitle={onSaveTitle}
                dueDate={taskDueDate}
                dueDateSupported={taskDueDateSupported}
                taskStatus={taskStatus}
                timezone={preferencesApi.data?.preferences.timezone}
                onSaveDueDate={onSaveDueDate}
                onSaveAssignee={onSaveAssignee}
                onSaveTags={onSaveTags}
                artifacts={taskArtifacts}
                collapsed={!hasLoadedMessages || transcriptMessages.length > 0}
                onSaveDescription={onSaveDescription}
                onRemoveArtifact={onRemoveArtifact}
                getArtifactDownloadLink={getArtifactDownloadLink}
                fetchArtifactRaw={fetchArtifactRaw}
                onOpenFile={onFileLinkClick}
              />
            </div>
          ) : messages.length === 0 && (isTranscribing || voiceNoteProgress) ? (
            <div
              className="flex h-full flex-col items-center justify-center gap-2 p-4 text-center text-muted-foreground"
              data-testid="messages-loading"
              aria-busy="true"
              aria-live="polite"
            >
              <Spinner className="size-5" />
              <p className="text-sm">
                {voiceNoteProgress === "uploading"
                  ? t("common:status.uploadingFiles")
                  : isTranscribing || voiceNoteProgress === "transcribing"
                    ? t("common:status.transcribingVoiceNote")
                    : t("common:status.loadingMessages")}
              </p>
            </div>
          ) : undefined
        }
      />
      {showTimelineRail && (
        <ConversationTimelinePanel
          variant="rail"
          sessionId={sessionId}
          navigation={conversationNavigation}
          onNavigate={navigateFromTimeline}
          onOpenThread={openTimelineThread}
        />
      )}
      </div>
      {canParticipateSession && (
        <InterruptedRunBanner
          recoverableFailedRun={recoverableFailedRun}
          onContinue={continueFromFailedRun}
        />
      )}
      {canDraftSession && (
        <ChatPromptComposer
          inputKey={sessionId}
          autoFocusKey={`${sessionId}:${focusTick ?? 0}`}
          textareaName="message"
          placeholder={t("input.placeholder")}
          onSubmit={handleSubmit}
          onVoiceNoteSubmit={handleSubmitVoiceNote}
          voiceNoteRef={voiceNoteRef}
          submitButtonRef={voiceSubmitRef}
          rowRef={composerRowRef}
          onVoiceSubmit={handleVoiceSubmit}
          onRecordingChange={setIsRecordingVoice}
          onVoiceDictationShortcut={canParticipateSession ? handleVoiceDictationShortcut : undefined}
          onInputChange={(value) => { setComposerDraft(Boolean(value.trim())); scheduleDraftSave(sessionId, value); }}
          onKeyDown={(event) => { markUserInteraction(event.nativeEvent); enterToSendHint.onKeyDown(event); }}
          submitShortcut={submitShortcut}
          status={status}
          disabled={!canParticipateSession}
          isUploading={isUploading}
          isTranscribing={isTranscribing}
          submitDisabled={submitDisabled && !isRecordingVoice}
          className="shrink-0 border-t bg-background p-3 flex flex-col gap-2"
          multiple
          maxFiles={MAX_UPLOAD_FILES}
          maxFileSize={MAX_UPLOAD_SIZE_BYTES}
          onError={(err) => {
            if (err.code === "max_files") console.warn(`[ui] Attachment rejected: max ${MAX_UPLOAD_FILES} files`);
            else if (err.code === "max_file_size") console.warn(`[ui] Attachment rejected: max ${MAX_UPLOAD_SIZE_MB} MB per file`);
            else if (err.code === "accept") console.warn("[ui] Attachment rejected: file type not accepted");
          }}
          promptQueue={<PromptQueueList items={promptQueue} editingId={editingPromptId} setEditingId={setEditingPromptId} editingText={editingPromptText} setEditingText={setEditingPromptText} onEdit={editQueuedPrompt} onDelete={deleteQueuedPrompt} submitError={submitError} resendError={promptResendError} deleteError={promptDeleteError} resendSupported={promptResendSupported} resendingId={resendingPromptId} onResend={resendQueuedPrompt} />}
          enterToSendHint={<EnterToSendHint open={enterToSendHint.open} onDismiss={enterToSendHint.dismiss} onOpenPreferences={() => { window.dispatchEvent(new CustomEvent(OPEN_PREFERENCES_EVENT)); enterToSendHint.hide(); }} />}
          followUpSuggestions={<FollowUpSuggestions sessionId={sessionId} lastAssistantMessageId={lastCompletedAssistantMessageId(messages)} completedSuccessfully={status === "ready"} writable={canParticipateSession && !isReadOnlySubAgent} status={status} getToken={getAccessToken} bootstrapCapability={bootstrap?.follow_up_capability} bootstrapSuggestions={bootstrap?.follow_up_suggestions} bootstrapPending={bootstrapPending} hasDraft={composerDraft || Boolean(promptInputController?.textInput.value.trim())} onSubmit={handleSubmit} />}
          selectedSkillBadges={<SelectedSkillBadges skills={agentSkills.selected} onRemove={(name) => { flushDraft(); agentSkills.remove(name); }} />}
          quickActions={({ stacked }) => resolvedAgentId ? <ComposerQuickActions stacked={stacked} key={`${sessionId}:${resolvedAgentId}`} actions={composerActions} agentId={resolvedAgentId} sessionId={sessionId} getToken={getAccessToken} hasDraft={composerDraft || Boolean(promptInputController?.textInput.value.trim())} narrow={isMobile || composerNarrow} disabled={!canParticipateSession || status === "submitted" || status === "streaming" || isUploading || isTranscribing} onQueued={handleComposerActionQueued} transcriptRuns={transcriptActionRuns} skillsStatus={agentSkills.status} skills={agentSkills.skills} selectedSkills={agentSkills.selected} skillsLimitReached={agentSkills.limitReached} onToggleSkill={(name) => { flushDraft(); agentSkills.toggle(name); }} /> : undefined}
          stopControl={(status === "submitted" || status === "streaming") && canManageSession ? <InputGroupButton type="button" variant="default" size="icon-sm" aria-label={t("input.stop")} onClick={stop}><SquareIcon className="size-4" /></InputGroupButton> : undefined}
        />
      )}
      {canDraftSession && sessionAccessUnavailable && (
        <p className="px-3 pb-3 text-sm text-destructive" role="alert">
          {t("input.sessionAccessUnavailable")}
        </p>
      )}
    </div>
  );
});

/* ------------------------------------------------------------------ */
/*  ChatPage — Container with sidebar + session management            */
/* ------------------------------------------------------------------ */

export function ChatPage() {
  const { t, i18n } = useTranslation();
  const { user, isLoading: authLoading, signIn, getAccessToken } = useAuthWithTestMode();
  const isMobile = useIsMobile();
  const viewportHeight = useViewportHeight();
  const [menuOpen, setMenuOpen] = useState(false);
  const [activeChatStatus, setActiveChatStatus] = useState<ChatStatus>("ready");
  const [previewRefreshKey, setPreviewRefreshKey] = useState(0);
  const [viewPublishRequest, setViewPublishRequest] =
    useState<ViewPublishRequest | null>(null);
  const viewPublishRequestIdRef = useRef(0);
  const commandPalette = useCommandPalette();
  const quickLaunch = useQuickLaunch();

  // ── Magic logout link ────────────────────────────────────────────────
  // ?logout=1 (or ?logout=true) in the URL triggers a full sign-out. Capture
  // the WorkOS session ID from the current token, then navigate through our
  // same-origin logout proxy. This must be the only request that revokes the
  // hosted session: AuthKit's navigate:false signOut also fetches this URL,
  // and a second request with the same sid makes WorkOS display its error page.
  // The primary navigation is important because AuthKit's background request
  // is skipped entirely after a reload when its in-memory access token is
  // absent. This lets us send a customer a simple link (e.g.
  // https://chat-bridge.l-9.net/?logout=1) to force a token refresh when their
  // session is stuck.
  //
  // We capture the flag into state on the first render (before AuthKit
  // finishes initializing) so the "Signing out…" screen is shown
  // immediately. The actual logout navigation/signIn fallback is deferred
  // until authLoading is false, because the AuthKitProvider exposes no-op
  // stubs while the real client is still being created.
  const [isLoggingOut] = useState(() => {
    try {
      const params = new URLSearchParams(window.location.search);
      // Skip logout in test mode — AuthKit redirects are no-ops there, so the
      // flow would never redirect away and the "Signing out…" screen would
      // stay forever.
      if (params.get("test_mode") === "1" || localStorage.getItem("l9-test-mode") === "1") {
        return false;
      }
      return params.get("logout") === "1" || params.get("logout") === "true";
    } catch {
      return false;
    }
  });
  const logoutStartedRef = useRef(false);

  useEffect(() => {
    if (!isLoggingOut || authLoading || logoutStartedRef.current) return;
    logoutStartedRef.current = true;

    // Strip the logout param so it doesn't re-trigger after the
    // redirect-back from WorkOS.
    try {
      const params = new URLSearchParams(window.location.search);
      params.delete("logout");
      const cleanSearch = params.toString();
      const cleanUrl = cleanSearch
        ? `${window.location.pathname}?${cleanSearch}`
        : window.location.pathname;
      window.history.replaceState({}, "", cleanUrl);
    } catch {
      // ignore — URL manipulation is best-effort
    }

    (async () => {
      let logoutUrl: string | null = null;
      try {
        const accessToken = await getAccessToken();
        logoutUrl = getWorkOSSessionLogoutUrl(accessToken, window.location.origin);
      } catch {
        // AuthKit may already have cleared its in-memory token. We can still
        // send already-logged-out users through the normal hosted sign-in flow.
      }

      try {
        // Revoke the current endpoint while the old verified JWT is still
        // available. This prevents a shared browser profile from keeping an
        // authenticated account's push binding after logout.
        await revokeCurrentPushSubscription(getAccessToken);
      } catch {
        // The helper is defensive, but logout must continue even if a service
        // worker/browser API is unavailable.
      }
      await purgeAllUiCache();
      if (logoutUrl) {
        // This full navigation is the one authoritative WorkOS logout. It
        // unloads AuthKit's in-memory state; after WorkOS revokes the session,
        // any persisted state is rejected and cleared on the redirect back.
        window.location.assign(logoutUrl);
        return;
      }

      // No access token means there is no session ID available for WorkOS's
      // logout endpoint. Fall back to sign-in so this link also works for
      // users who are already logged out.
      void signIn();
    })();
  }, [isLoggingOut, authLoading, signIn, getAccessToken]);

  // The URL is the single source of truth for the active session. On `/`, a
  // stored session is only a fallback when no safe pinned-agent landing exists.
  const [location] = useLocation();
  const isRootRoute = location === "/";
  const isProjectsListRoute = location === "/projects/list";
  const params = useParams<DefaultParams>();
  const projectId = params?.projectId ?? null;
  const isProjectCreateRoute = location === "/project/new";
  const isProjectWorkspaceRoute = location.startsWith("/project/") && !!projectId;
  const isProjectsWorkspaceRoute = isProjectCreateRoute || isProjectWorkspaceRoute;
  const isProjectsSurface = isProjectsWorkspaceRoute || isProjectsListRoute;
  const isAgentConversationsRoute = /^\/agent\/[^/]+\/conversations$/.test(location);
  const isNonChatSurface = isProjectsSurface || isAgentConversationsRoute;
  const routeAgentId = isAgentConversationsRoute ? params?.agentId ?? null : null;
  const urlSessionId = params?.sessionId ?? null;

  const [activeSessionId, setActiveSessionIdRaw] = useState<string | null>(
    () => {
      if (isNonChatSurface) return null;
      try {
        return urlSessionId ?? localStorage.getItem("l9-active-session-id");
      } catch {
        return null;
      }
    }
  );

  // Keep the URL-derived value in sync if the browser navigates externally.
  useEffect(() => {
    setActiveSessionIdRaw(
      isNonChatSurface
        ? null
        : urlSessionId ?? localStorage.getItem("l9-active-session-id"),
    );
  }, [isNonChatSurface, urlSessionId]);

  // A task chat is a normal session rendered within /projects. Keep the shared
  // notification poller pointed at it without making the session list treat
  // the route as a normal /session/:id view.
  const [activeProjectSessionId, setActiveProjectSessionId] = useState<string | null>(null);
  const [activeProjectAgentId, setActiveProjectAgentId] = useState<string | null>(null);
  const handleActiveProjectSessionChange = useCallback((sessionId: string | null, agentId: string | null) => {
    setActiveProjectSessionId(sessionId);
    setActiveProjectAgentId(agentId);
  }, []);
  const visibleSessionId = isProjectWorkspaceRoute || isProjectCreateRoute
    ? activeProjectSessionId
    : isProjectsListRoute || isAgentConversationsRoute
      ? null
      : activeSessionId;

  const [justCreatedSessionId, setJustCreatedSessionId] = useState<string | null>(null);
  const bootstrapEnabled = Boolean(activeSessionId && justCreatedSessionId !== activeSessionId);
  const { state: bootstrapState } = useConversationBootstrap(
    bootstrapEnabled ? activeSessionId : null, getAccessToken, bootstrapEnabled,
  );
  const activeBootstrap = bootstrapState.phase === "ready" && bootstrapState.sessionId === activeSessionId
    ? bootstrapState.data : null;
  const bootstrapPending = bootstrapEnabled && (bootstrapState.phase === "idle"
    || bootstrapState.phase === "loading"
    || (bootstrapState.phase === "ready" && !activeBootstrap)
    || bootstrapState.phase === "error");
  // Ref mirror of the visible session, passed to useNotifications so the poller
  // can suppress notifications for the session the user is currently viewing
  // without re-creating the polling effect on every session switch.
  const activeSessionIdRef = useRef<string | null>(visibleSessionId);
  useEffect(() => {
    activeSessionIdRef.current = visibleSessionId;
  }, [visibleSessionId]);

  useEffect(() => {
    setActiveChatStatus("ready");
    setPreviewRefreshKey(0);
    setViewPublishRequest(null);
  }, [activeSessionId]);

  const setActiveSessionId = useCallback((id: string | null) => {
    navigateToSession(id, {
      setActiveSessionId: setActiveSessionIdRaw,
      activeSessionIdRef,
      setActiveProjectSessionId,
      navigate,
    });
    setMenuOpen(false);
    try {
      if (id) {
        localStorage.setItem("l9-active-session-id", id);
      } else {
        localStorage.removeItem("l9-active-session-id");
      }
    } catch {
      // ignore storage errors
    }
  }, []);

  // Pass the getAccessToken function so that a fresh token is fetched
  // before every API call, avoiding stale-token 401s.
  const { sessions, loaded: sessionsLoaded, cacheState: sessionsCacheState, loading, error, create, rename, updateAgent, archive, removeParticipation, addParticipation, getTenantDefault, refresh: refreshSessions,
          searchQuery, setSearchQuery, debouncedQuery, knownSessionIds, registerKnownSessionIds, searching, hasMore, loadingMore, loadMore,
          canRetryLoadMore, retryLoadMore } = useSessions(
    getAccessToken
  );
  // Polls and run completions update the newest page, but must not reset a
  // user who is currently paging through older sidebar history.
  const refreshSessionsInBackground = useCallback(
    () => refreshSessions("background"),
    [refreshSessions],
  );
  const {
    getBySession: getTaskBySession,
    update: updateTask,
    removeArtifact: removeTaskArtifact,
    getArtifactDownloadLink: getTaskArtifactDownloadLink,
    fetchArtifactRaw: fetchTaskArtifactRaw,
    create: createTask,
    bindSession: bindTaskSession,
  } = useTasks(getAccessToken);
  // Child/thread sessions can still be absent when paired with an older
  // server. Keep their fetched detail here so header controls can use the
  // same active session data as normal full-screen sessions.
  const [fallbackActiveSession, setFallbackActiveSession] = useState<Session | null>(null);
  // Task data for a project-task session opened through the regular sidebar.
  // Keep its source session ID alongside it so a completed request can never
  // briefly render a previous task's status after a quick session switch.
  const [sidebarTask, setSidebarTask] = useState<{
    sessionId: string;
    task: Task;
  } | null>(null);
  const sidebarTaskRef = useRef<typeof sidebarTask>(sidebarTask);
  useEffect(() => {
    sidebarTaskRef.current = sidebarTask;
  }, [sidebarTask]);
  // Sidebar rows only carry their task-session marker, not the task itself.
  // Keep a small per-session cache so both expanded and collapsed rows can
  // render the same status color without refetching on every render.
  const taskStatusBySessionRef = useRef(new Map<string, TaskStatus | null>());
  const taskStatusFetchesRef = useRef(new Set<string>());
  const taskIdBySessionRef = useRef(new Map<string, string>());
  const confirmedTaskStatusesRef = useRef(new Map<string, ConfirmedTaskStatus>());
  const [taskStatusBySession, setTaskStatusBySession] = useState<ReadonlyMap<string, TaskStatus | null>>(
    () => new Map(),
  );
  const cacheTaskStatus = useCallback((sessionId: string, status: TaskStatus | null) => {
    const current = taskStatusBySessionRef.current;
    if (current.has(sessionId) && current.get(sessionId) === status) return;

    const next = new Map(current);
    next.set(sessionId, status);
    taskStatusBySessionRef.current = next;
    setTaskStatusBySession(next);
  }, []);
  const getTaskStatusForSession = useCallback(
    (sessionId: string): TaskStatus | null | undefined => taskStatusBySession.get(sessionId),
    [taskStatusBySession],
  );
  const reconcileSessionTaskStatus = useCallback((session: SessionPollSession, fetchStartedAt: number) => {
    if (session.metadata?.[PROJECT_TASK_SESSION_METADATA_KEY] !== true) return;
    const knownTaskId = taskIdBySessionRef.current.get(session.id);
    const related = relatedTaskFromSession(session, knownTaskId);
    if (related === undefined) return;
    const taskId = related?.id ?? knownTaskId;
    const confirmedKey = taskId ? `${session.id}:${taskId}` : undefined;
    const confirmed = confirmedKey ? confirmedTaskStatusesRef.current.get(confirmedKey) : undefined;
    const decision = reconcileRelatedTaskStatus(
      confirmed,
      { status: related?.status ?? null, updatedAt: related?.updatedAt ?? null },
      fetchStartedAt,
    );
    if (decision === "ignore") return;
    if (decision === "refresh") {
      if (taskStatusFetchesRef.current.has(session.id)) return;
      taskStatusFetchesRef.current.add(session.id);
      const getStartedAt = Date.now();
      void getTaskBySession(session.id).then((task) => {
        const latest = confirmedKey ? confirmedTaskStatusesRef.current.get(confirmedKey) : undefined;
        if (latest && getStartedAt <= latest.confirmedAt) return;
        if (task) taskIdBySessionRef.current.set(session.id, task.id);
        cacheTaskStatus(session.id, task?.status ?? null);
        if (task && confirmedKey && latest) {
          const updatedAt = Date.parse(task.updated_at);
          confirmedTaskStatusesRef.current.set(confirmedKey, {
            ...latest, status: task.status,
            updatedAt: Number.isFinite(updatedAt) ? updatedAt : latest.updatedAt,
          });
        }
      }).finally(() => taskStatusFetchesRef.current.delete(session.id));
      return;
    }
    if (related) taskIdBySessionRef.current.set(session.id, related.id);
    if (confirmed && confirmedKey && related) {
      confirmedTaskStatusesRef.current.set(confirmedKey, {
        ...confirmed, status: related.status,
        updatedAt: related.updatedAt ?? confirmed.updatedAt,
      });
    }
    cacheTaskStatus(session.id, related?.status ?? null);
  }, [getTaskBySession, cacheTaskStatus]);
  const cacheTaskStatusesFromSessionList = useCallback(
    (polledSessions: SessionPollSession[], fetchStartedAt: number) => {
      for (const session of polledSessions) reconcileSessionTaskStatus(session, fetchStartedAt);
    },
    [reconcileSessionTaskStatus],
  );
  // The project name is needed only for a task session opened through the
  // regular chat route. Keep its source identifiers to avoid showing a stale
  // project after a fast session switch.
  const [sidebarTaskProjectSegment, setSidebarTaskProjectSegment] = useState<{
    sessionId: string;
    projectId: string;
    segment: SessionBreadcrumbSegment;
  } | null>(null);
  // This is intentionally independent from useSessions: voice-note work is
  // client-side and exists before the backend has a run record to report.
  const pendingVoiceNoteSessionIds = useVoiceNoteProgressSessionIds();
  const {
    agents: allAgents,
    previewAgents,
    cacheState: agentsCacheState,
    loading: agentsLoading,
    error: agentsError,
    agentCatalogLoaded,
    agentCatalogUpdatedAt,
    agentCatalogError,
    refresh: refreshAgents,
  } = useAgents(getAccessToken);
  const {
    views: conversationViews,
    loading: conversationViewsLoading,
    loaded: conversationViewsLoaded,
    error: conversationViewsError,
    refresh: refreshConversationViews,
    unpin: unpinConversationView,
  } = useConversationViews(
    visibleSessionId,
    getAccessToken,
    activeBootstrap?.session.id === visibleSessionId ? activeBootstrap.views : undefined,
    bootstrapPending,
  );
  const conversationViewCapabilities = useConversationViewCapabilities(
    visibleSessionId,
    getAccessToken,
    activeBootstrap?.session.id === visibleSessionId ? activeBootstrap.view_capabilities : undefined,
    bootstrapPending,
  );
  const browserFilePinSupported = conversationViewCapabilities === null
    ? (visibleSessionId ? null : false)
    : conversationViewCapabilities.browserFilePinsSupported;
  // Pinned-view summaries intentionally never expose canonical target paths.
  // Track paths pinned through this viewer locally so its action is disabled
  // immediately after a successful pin without weakening that privacy bound.
  const [browserPinnedFilePaths, setBrowserPinnedFilePaths] = useState<Set<string>>(
    () => new Set(),
  );
  const [pinningFilePath, setPinningFilePath] = useState<string | null>(null);
  useEffect(() => {
    setBrowserPinnedFilePaths(new Set());
    setPinningFilePath(null);
  }, [visibleSessionId]);
  const runPinBaselineRef = useRef<Set<string> | null>(null);
  const {
    agentIds: agentPins,
    loading: agentPinsLoading,
    loaded: agentPinsLoaded,
    updatedAt: agentPinsUpdatedAt,
    loadError: agentPinsLoadError,
    error: agentPinsError,
    refresh: refreshAgentPins,
    pin: pinAgent,
    unpin: unpinAgent,
  } = useAgentPins(getAccessToken);
  // Keep pin ordering at the shell so the root-path landing and both sidebar
  // renderers agree on the same persisted order.
  const sidebarPinOrdering = useSidebarPinOrdering(getAccessToken);
  const pinnedByDefaultAgentIds = useMemo(() => (
    allAgents
      .filter((agent) => agent.type === "agent" && agent.pinnedByDefault === true)
      .sort((left, right) => (
        (left.order ?? 100) - (right.order ?? 100)
        || left.name.localeCompare(right.name)
        || left.id.localeCompare(right.id)
      ))
      .map((agent) => agent.id)
  ), [allAgents]);
  const orderedPinnedAgentIds = useMemo(
    () => sidebarPinOrdering.displayIds(
      agentPins,
      "agentIds",
      pinnedByDefaultAgentIds,
    ),
    [agentPins, pinnedByDefaultAgentIds, sidebarPinOrdering.displayIds],
  );
  // The sidebar and Projects workspace consume this same shell-owned list
  // through ProjectsDataProvider. It warms pinned-project picks at mount and
  // means a confirmed pin/unpin is immediately visible to the next <leader>p.
  const sharedProjects = useProjects(getAccessToken);
  const {
    loadAgents: loadLeaderAgents,
    loadPinnedProjects: loadLeaderPinnedProjects,
    loadSessions: loadLeaderSessions,
  } = useLeaderKeyPickers({
    agents: allAgents,
    agentCatalogLoaded,
    agentCatalogUpdatedAt,
    agentCatalogError,
    refreshAgents,
    agentIds: agentPins,
    agentPinsLoaded,
    agentPinsUpdatedAt,
    agentPinsLoadError,
    refreshAgentPins,
    projects: sharedProjects.projects,
    projectsLoaded: sharedProjects.loaded,
    projectsUpdatedAt: sharedProjects.updatedAt,
    projectsListError: sharedProjects.listError,
    refreshProjects: sharedProjects.refresh,
    sessions,
    sessionTodayLabel: t("sidebar.today"),
    sessionYesterdayLabel: t("sidebar.yesterday"),
    sessionLocale: i18n.language,
  });
  const [defaultPinInitializing, setDefaultPinInitializing] = useState(false);
  const [defaultPinBootstrapVersion, setDefaultPinBootstrapVersion] = useState(0);
  const defaultPinBootstrapRef = useRef(false);
  const defaultPinInitializationInFlightRef = useRef(false);
  const defaultPinInitializationMountedRef = useRef(false);
  const rawAgentPinsRef = useRef(agentPins);
  useEffect(() => {
    defaultPinInitializationMountedRef.current = true;
    return () => {
      defaultPinInitializationMountedRef.current = false;
    };
  }, []);
  useEffect(() => {
    rawAgentPinsRef.current = agentPins;
  }, [agentPins]);
  const { me } = useMe(getAccessToken, typeof user?.id === "string" ? user.id : null);
  useEffect(() => {
    setVerifiedCacheIdentity(me, typeof user?.id === "string" ? user.id : null);
  }, [me, user?.id]);
  const pinBootstrapStorageKey = useMemo(() => (
    agentPinBootstrapStorageKey(
      "browser-profile",
      me?.user_id ?? me?.email ?? (typeof user?.id === "string" ? user.id : null),
    )
  ), [me?.email, me?.user_id, user?.id]);
  // Preferences load with ChatPage so keyboard handling and saved defaults
  // apply without opening settings; the dialog still refreshes when opened.
  const userPreferences = useUserPreferences(getAccessToken);
  const submitShortcut = userPreferences.data?.preferences.submit_shortcut
    ?? DEFAULT_SUBMIT_SHORTCUT;
  // Hide-until-known: only show the switcher once we know the tenant allows it,
  // avoiding a flash of the dropdown for tenants where it must stay hidden.
  const showAgentSwitcher = canSwitchAgent(me?.tenant);
  // Per-session model switcher. Same hide-until-known rule: the header actions
  // menu is gated on the registry-backed capability from /api/me.
  const showModelSwitcher = canSwitchModel(me?.model_switcher_enabled);

  // The identity endpoint returns server-computed status for new deployments.
  // Its helper retains an older-response fallback so a tenant-admin can still
  // reach the explicit unsupported-deployment state instead of crashing.
  const tenantAgentEditorAccess = getTenantAgentEditorAccess(me);
  // Keep the existing platform-admin boolean separate from the narrowly
  // scoped tenant-admin editor capability so no other admin UI is exposed.
  const isAdminUser = tenantAgentEditorAccess.isPlatformAdmin;
  const canManageTenantAgents = tenantAgentEditorAccess.canManageTenantAgents;

  // ── Aside URL state ──────────────────────────────────────────────────────
  // The aside manager owns the canonical `panel` search parameter. useSearch()
  // stays reactive when only a query string changes, while the manager keeps
  // legacy viewer/thread links compatible during rolling UI deployments.
  const search = useSearch();
  const queryParams = useMemo(() => new URLSearchParams(search), [search]);
  const asideManager = useAsideViewManager({
    pathname: location,
    search,
    navigate,
  });
  const {
    activeViewId,
    aside: activeAside,
    viewerFile,
    viewerIsHtml,
    viewerIsPdf,
    viewerIsDocx,
    viewerIsImage,
    viewerIsSource,
    isPopout,
    threadSessionId,
    subAgentSessionId,
    scheduleRunSessionId,
  } = asideManager;
  const activeViewIdRef = useRef(activeViewId);
  activeViewIdRef.current = activeViewId;
  const activeProjectId = projectId && projectId !== "new" ? projectId : null;
  const previousProjectRouteRef = useRef(activeProjectId);
  useEffect(() => {
    if (previousProjectRouteRef.current === activeProjectId) return;
    previousProjectRouteRef.current = activeProjectId;
    if (activeProjectId) void sharedProjects.revalidateAccess();
  }, [activeProjectId, sharedProjects.revalidateAccess]);
  // The thread panel is visible alongside its parent, so it needs to count
  // as active for notification suppression even though activeSessionId stays
  // pointed at the parent route.
  const activeChildSessionIdRef = useRef<string | null>(
    scheduleRunSessionId ?? subAgentSessionId ?? threadSessionId,
  );
  activeChildSessionIdRef.current = scheduleRunSessionId ?? subAgentSessionId ?? threadSessionId;
  const [threadAnchorCache, setThreadAnchorCache] = useState<
    Record<string, ThreadAnchorCacheEntry>
  >({});
  const [threadTitleCache, setThreadTitleCache] = useState<
    Record<string, ThreadTitleCacheEntry>
  >({});
  const [subAgentReferenceCache, setSubAgentReferenceCache] = useState<
    Record<string, SubAgentThreadReference>
  >({});
  const threadReconciliationRequestsRef = useRef<
    Map<string, Promise<Session | null>>
  >(new Map());
  const [childRefreshTrigger, setChildRefreshTrigger] = useState<{
    parentSessionId: string;
    nonce: number;
  } | null>(null);

  // ── Child-session (thread) right-sidebar URL state ──────────────────────
  // The active thread is URL-addressable: /session/{parentId}?panel=thread:{childId}.
  // The small cache keeps a just-created thread's origin quote visible until
  // the panel is closed; the active thread identity remains in the URL.

  const handleCloseActiveView = asideManager.closeAgentView;
  const handleCloseViewer = asideManager.closeViewer;
  const handleCloseChildSession = asideManager.closeThread;
  const handleCloseSubAgent = asideManager.closeSubAgent;

  const handleSubAgentReferencesResolved = useCallback((
    references: readonly SubAgentThreadReference[],
  ) => {
    if (references.length === 0) return;
    setSubAgentReferenceCache((current) => {
      let changed = false;
      const next = { ...current };
      for (const reference of references) {
        const previous = current[reference.childSessionId];
        if (
          !previous
          || previous.taskId !== reference.taskId
          || previous.parentSessionId !== reference.parentSessionId
          || previous.state !== reference.state
          || previous.promptSummary !== reference.promptSummary
          || previous.viewAvailable !== reference.viewAvailable
        ) {
          next[reference.childSessionId] = reference;
          changed = true;
        }
      }
      return changed ? next : current;
    });
  }, []);

  const handleOpenSubAgent = useCallback((reference: SubAgentThreadReference) => {
    if (!reference.viewAvailable) return;
    handleSubAgentReferencesResolved([reference]);
    asideManager.openSubAgent(reference.childSessionId);
  }, [asideManager.openSubAgent, handleSubAgentReferencesResolved]);

  const handleNavigateSubAgentBreadcrumb = useCallback((destination: ConversationDestination) => {
    const sessionId = destination.sessionId;
    const rootSessionId = activeSessionId ?? activeProjectSessionId;
    if (sessionId !== rootSessionId) {
      // The shared panel validates the target from session detail, so direct
      // links can drill from C back to B without relying on card-local cache.
      asideManager.openSubAgent(sessionId);
      return;
    }
    handleCloseSubAgent();
    if (sessionId !== activeSessionId) setActiveSessionId(sessionId);
    if (destination.target) pushConversationDestination(destination);
  }, [
    activeSessionId,
    activeProjectSessionId,
    asideManager.openSubAgent,
    handleCloseSubAgent,
    setActiveSessionId,
  ]);

  const handleThreadAnchorsLoaded = useCallback((anchors: ThreadAnchorRef[]) => {
    setThreadAnchorCache((current) => {
      let next: Record<string, ThreadAnchorCacheEntry> | null = null;

      for (const ref of anchors) {
        const threadAnchor = threadAnchorDisplayFrom(ref);
        if (!threadAnchor) continue;

        const cache = next ?? current;
        const existing = cache[ref.child_session_id];
        const agentId = existing?.agent_id ?? ref.agent_id ?? null;
        if (
          existing?.threadAnchor?.source_anchor_kind === threadAnchor.source_anchor_kind
          && existing.threadAnchor.source_quote === threadAnchor.source_quote
          && existing.agent_id === agentId
        ) {
          continue;
        }

        if (next === null) next = { ...current };
        next[ref.child_session_id] = { threadAnchor, agent_id: agentId };
      }

      return next ?? current;
    });

    setThreadTitleCache((current) => {
      let next: Record<string, ThreadTitleCacheEntry> | null = null;

      for (const ref of anchors) {
        if (!ref.title) continue;
        const cache = next ?? current;
        const existing = cache[ref.child_session_id];
        const merged = mergeThreadTitleCacheEntry(existing, {
          title: ref.title,
          lastActiveAt: ref.last_active_at,
          source: "anchor",
        });
        if (merged === existing) continue;
        if (next === null) next = { ...current };
        next[ref.child_session_id] = merged;
      }

      return next ?? current;
    });
  }, []);

  const handleThreadCreated = useCallback((parentSessionId: string) => {
    setChildRefreshTrigger((current) => ({
      parentSessionId,
      nonce: (current?.nonce ?? 0) + 1,
    }));
  }, []);

  const reconcileThreadSession = useCallback(
    (childSessionId: string): Promise<Session | null> => (
      reconcileThreadSessionOnce(
        childSessionId,
        threadReconciliationRequestsRef.current,
        {
          fetchSession: async () => {
            const res = await fetchWithWorkOSAuth(
              `/api/sessions/${encodeURIComponent(childSessionId)}`,
              { headers: {}, cache: "no-store" },
              getAccessToken,
            );
            if (!res.ok) throw new Error(`HTTP ${res.status}`);
            return await res.json() as Session;
          },
          isActiveSession: (sessionId) => activeSessionIdRef.current === sessionId,
          updateTitle: (session) => {
            setThreadTitleCache((current) => {
              const existing = current[childSessionId];
              const merged = mergeThreadTitleCacheEntry(existing, {
                title: session.title,
                lastActiveAt: session.last_active_at,
                source: "detail",
              });
              return merged === existing
                ? current
                : { ...current, [childSessionId]: merged };
            });
          },
          updateActiveSession: (session) => {
            setFallbackActiveSession((current) => (
              current?.id === session.id
              && isOlderLastActiveAt(session.last_active_at, current.last_active_at)
                ? current
                : session
            ));
          },
          refreshChildren: handleThreadCreated,
          onError: (error) => {
            console.error(
              "[ui] Failed to reconcile thread session:",
              childSessionId,
              error,
            );
          },
        },
      )
    ),
    [getAccessToken, handleThreadCreated],
  );

  const handleThreadRunComplete = useCallback(
    (childSessionId: string) => reconcileThreadSession(childSessionId),
    [reconcileThreadSession],
  );

  const handleOpenThreadForParent = useCallback((
    parentSessionId: string | null,
    childSessionId: string,
    threadAnchor?: ThreadAnchorDisplay | null,
    threadTitle?: string | null,
    agentId?: string | null,
    targetPathname?: string,
  ) => {
    if (!parentSessionId) return;
    if (threadAnchor || agentId !== undefined) {
      setThreadAnchorCache((current) => ({
        ...current,
        [childSessionId]: {
          threadAnchor: threadAnchor ?? current[childSessionId]?.threadAnchor ?? null,
          // Preserve a selection made during this page load when a stale
          // anchor-query response is used to reopen the thread.
          agent_id: current[childSessionId]?.agent_id ?? agentId ?? null,
        },
      }));
    }
    if (threadTitle) {
      setThreadTitleCache((current) => (
        current[childSessionId]
          ? current
          : {
              ...current,
              [childSessionId]: {
                title: threadTitle,
                lastActiveAt: null,
                source: "seed",
              },
            }
      ));
    }
    asideManager.openThread(childSessionId, targetPathname);
  }, [asideManager.openThread]);

  const handleOpenThread = useCallback((
    childSessionId: string,
    threadAnchor?: ThreadAnchorDisplay | null,
    threadTitle?: string | null,
    agentId?: string | null,
  ) => {
    if (!activeSessionId) return;
    handleOpenThreadForParent(
      activeSessionId,
      childSessionId,
      threadAnchor,
      threadTitle,
      agentId,
      `/session/${activeSessionId}`,
    );
  }, [activeSessionId, handleOpenThreadForParent]);

  // Project task chats keep their parent selection outside the normal session
  // route state. Publish that known task session before opening the shared
  // URL-backed aside so a newly-created thread cannot be dropped while the
  // project workspace is still resolving its task state.
  const handleOpenProjectTaskThread = useCallback((
    parentSessionId: string,
    childSessionId: string,
    threadAnchor?: ThreadAnchorDisplay | null,
    threadTitle?: string | null,
    agentId?: string | null,
  ) => {
    setActiveProjectSessionId(parentSessionId);
    handleOpenThreadForParent(
      parentSessionId,
      childSessionId,
      threadAnchor,
      threadTitle,
      agentId,
    );
  }, [handleOpenThreadForParent]);

  // Open a child session as a full-screen view (navigates to the child
  // session ID, closing the right-sidebar panel). Publish the active session
  // first: Wouter can render the new route synchronously, and leaving the
  // parent ID active for that render lets async sidebar work attach the child
  // to the previous session.
  const handleOpenChildFullSession = useCallback((childSessionId: string) => {
    setActiveSessionId(childSessionId);
  }, [setActiveSessionId]);

  const handleThreadAgentChange = useCallback((
    childSessionId: string,
    agentId: string | null,
  ) => {
    setThreadAnchorCache((current) => ({
      ...current,
      [childSessionId]: {
        threadAnchor: current[childSessionId]?.threadAnchor ?? null,
        agent_id: agentId,
      },
    }));
  }, []);

  const handleOpenViewerInNewWindow = useCallback(() => {
    if (!viewerFile || !activeSessionId) return;
    const popoutUrl = asideManager.buildViewerPopoutUrl(
      viewerFile.url,
      viewerFile.fileName,
      `/session/${activeSessionId}`,
    );
    window.open(popoutUrl, "_blank", "noopener");
    // Auto-close the in-app sidebar so the user isn't looking at two copies.
    handleCloseViewer();
  }, [activeSessionId, asideManager.buildViewerPopoutUrl, handleCloseViewer, viewerFile]);

  // Auto-redirect unauthenticated users to WorkOS Hosted Sign-In
  useEffect(() => {
    if (!isLoggingOut && !authLoading && !user) {
      console.log("[ui] Redirecting to login…");
      signIn();
    }
  }, [isLoggingOut, authLoading, user, signIn]);

  // ── Web Push subscription (background notifications) ───────────────────
  // This hook manages the server-push subscription that powers system-level
  // notifications when the tab is closed or the app is installed.  It is
  // SEPARATE from the existing polling system (use-notifications.ts) which
  // drives sidebar indicators and in-tab notifications.
  //
  // Auto-subscribe on mount if the user has already granted notification
  // permission in a previous session — this keeps push working across
  // reloads without requiring the user to re-toggle it every time.
  const pushApi = usePushSubscription(getAccessToken);
  // Keep a ref to the latest enable() so the auto-subscribe effect can
  // call it without depending on its identity (which changes when the
  // `enabling` flag toggles, causing an infinite retry loop).
  const enablePushRef = useRef(pushApi.enable);
  enablePushRef.current = pushApi.enable;
  useEffect(() => {
    if (!user) return;
    // Always re-save an existing browser endpoint for a new authenticated
    // account/tenant identity. saveSubscription() transactionally revokes any
    // prior owner, so this is both safe on reload and required on switches.
    if (pushApi.isSupported && pushApi.permission === "granted") {
      void enablePushRef.current();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user?.id, me?.tenant, pushApi.isSupported, pushApi.permission]);

  // Auto-select most recent session on load, or create one if none exist.
  // Only runs after sessions have finished loading to avoid racing with
  // the empty initial state.
  // ── Cached tenant default ────────────────────────────────────────────────
  // The /api/sessions/tenant-default response is stable for the lifetime of
  // the page (determined by the WorkOS tenant claim).  We fetch it once on
  // mount and reuse the cached value for every subsequent session create —
  // including the auto-create below, the user-clicked "+" handler, and any
  // future caller.  Before this cache, every create() hit the network for
  // the same response, contributing to the request-multiplier calls on the
  // new-session flow.
  const tenantDefaultRef = useRef<TenantDefault | null | undefined>(undefined);
  useEffect(() => {
    if (tenantDefaultRef.current !== undefined) return;
    let cancelled = false;
    getTenantDefault()
      .then((def) => {
        if (!cancelled) tenantDefaultRef.current = def;
      })
      .catch(() => {
        // getTenantDefault already warns; record that we tried so we don't
        // attempt the fetch again on every subsequent render/mount.
        if (!cancelled) tenantDefaultRef.current = null;
      });
    return () => { cancelled = true; };
  }, [getTenantDefault]);
  useRefreshOnReconnect(async () => {
    tenantDefaultRef.current = await getTenantDefault();
  });
  // Resolves to the cached tenant default. Pads with a live fetch if the
  // mount fetch hasn't returned yet (so the first create still gets a
  // default agent), and populates the cache for next time.
  const getCachedTenantDefault = useCallback(async (): Promise<TenantDefault | null> => {
    if (tenantDefaultRef.current !== undefined && tenantDefaultRef.current !== null) {
      return tenantDefaultRef.current;
    }
    if (tenantDefaultRef.current === null) return null;
    const def = await getTenantDefault();
    if (def) tenantDefaultRef.current = def;
    return def;
  }, [getTenantDefault]);

  const getUserDefaultAgentId = useCallback(async (): Promise<string | null> => {
    const preferences = userPreferences.data ?? await userPreferences.refresh();
    return preferences?.preferences.default_agent_id ?? null;
  }, [userPreferences.data, userPreferences.refresh]);

  // Seed first-time pins from both the tenant default and per-agent markers.
  // The server owns both the actual pin relation and the per-user record of
  // defaults already offered. Browser state is retained for rolling-deploy
  // compatibility with older servers. Markers are considered even when an
  // existing user already has other pins, which lets a newly deployed dashboard
  // become available without replacing that user's choices.
  useEffect(() => {
    if (
      defaultPinBootstrapRef.current
      || !agentPinsLoaded
      || agentPinsLoading
      || agentsLoading
      || !agentCatalogLoaded
    ) return;

    let cancelled = false;
    void (async () => {
      let claimedBootstrap = false;
      const initialPinIds = rawAgentPinsRef.current;
      const localState = readAgentPinBootstrapState(pinBootstrapStorageKey);
      let serverState: Awaited<ReturnType<typeof readServerAgentPinBootstrapState>> = null;
      try {
        serverState = await readServerAgentPinBootstrapState(getAccessToken);
      } catch {
        // Do not risk re-offering defaults when a current server has a
        // transient failure. Unsupported older servers return null above and
        // deliberately continue through the browser-profile fallback.
        return;
      }
      if (cancelled || defaultPinBootstrapRef.current) return;
      const storedState = serverState === null
        ? localState
        : mergeAgentPinBootstrapStates(serverState, localState);
      const bootstrapPlan = planAgentPinBootstrap(
        storedState,
        initialPinIds,
        pinnedByDefaultAgentIds,
      );
      const initializedMarkerIds = new Set(
        bootstrapPlan.initializedPinnedByDefaultAgentIds,
      );
      const { markerIdsToPin } = bootstrapPlan;
      let defaultAgentId: string | null = null;
      let { defaultAgentInitialized } = bootstrapPlan;

      try {
        if (bootstrapPlan.shouldResolveDefaultAgent) {
          try {
            const tenantDefault = await getCachedTenantDefault();
            if (cancelled || defaultPinBootstrapRef.current) return;
            const defaultAgent = tenantDefault?.entityType === "agent"
              ? allAgents.find((agent) => (
                agent.id === tenantDefault.entityId && agent.type === "agent"
              ))
              : undefined;
            if (defaultAgent) {
              defaultAgentId = defaultAgent.id;
            } else {
              // A team, inaccessible/stale ID, or failed default lookup must
              // leave the explicit empty Add Agent affordance intact rather
              // than guessing, matching the old bootstrap behavior.
              defaultAgentInitialized = true;
            }
          } catch {
            // Keep the default eligible for a later retry if its lookup had a
            // transient failure. Marker initialization can still proceed.
          }
        }

        // Wait to claim the bootstrap until after its only read-only await.
        // React Strict Mode tears down and restarts effects in development;
        // claiming before that await would leave the restarted effect unable
        // to complete an otherwise cancelled first pass.
        if (cancelled || defaultPinBootstrapRef.current) return;
        defaultPinBootstrapRef.current = true;
        defaultPinInitializationInFlightRef.current = true;
        claimedBootstrap = true;

        const pinTargets = [...new Set([
          ...markerIdsToPin,
          ...(defaultAgentId ? [defaultAgentId] : []),
        ])];
        if (pinTargets.length > 0 && defaultPinInitializationMountedRef.current) {
          setDefaultPinInitializing(true);
        }

        const successfulPins = new Set<string>();
        // useAgentPins intentionally serializes mutations through a monotonic
        // generation. Pin sequentially so multiple marked agents cannot
        // invalidate each other's optimistic response.
        for (const agentId of pinTargets) {
          const result = await pinAgent(agentId);
          if (result !== null) successfulPins.add(agentId);
        }

        for (const agentId of markerIdsToPin) {
          if (successfulPins.has(agentId)) initializedMarkerIds.add(agentId);
        }
        if (defaultAgentId && successfulPins.has(defaultAgentId)) {
          defaultAgentInitialized = true;
        }
        const completedState = {
          defaultAgentInitialized,
          initializedPinnedByDefaultAgentIds: [...initializedMarkerIds],
        };
        writeAgentPinBootstrapState(pinBootstrapStorageKey, completedState);
        if (serverState !== null) {
          try {
            await writeServerAgentPinBootstrapState(getAccessToken, completedState);
          } catch {
            // The local compatibility cache prevents repeated offers on this
            // profile while a transient server write is unavailable.
          }
        }
      } finally {
        if (claimedBootstrap) {
          defaultPinInitializationInFlightRef.current = false;
          // A duplicate catalog response can re-run this effect while its pin
          // request is pending. The one-shot guard means this request still
          // owns the resolving state, so it must clear it even after cleanup.
          if (defaultPinInitializationMountedRef.current) {
            // A completed no-op bootstrap (for example no valid tenant
            // default) has no pin-state update to wake the root landing
            // effect, so publish its settlement explicitly.
            setDefaultPinBootstrapVersion((version) => version + 1);
            setDefaultPinInitializing(false);
          }
        }
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [
    agentCatalogLoaded,
    agentPinsLoaded,
    agentPinsLoading,
    agentsLoading,
    allAgents,
    getCachedTenantDefault,
    getAccessToken,
    pinAgent,
    pinBootstrapStorageKey,
    pinnedByDefaultAgentIds,
  ]);

  // ── "Newly created" marker ────────────────────────────────────────────────
  // Tracks the id of a session this component just created via POST.  Passed
  // down to ChatInterface -> useAgnoChat as `isNewSession` so the hook can
  // skip the mount-time /runs check on a session the server cannot possibly
  // have an in-flight run on (it returned the 201 milliseconds ago).
  //
  // We use useState (not useRef) so the prop value flips to false after
  // ChatInterface consumes the flag on its first render, via the effect
  // below.  Using a ref would leave the prop sticky-true across re-renders,
  // forcing the hook to keep skipping /runs forever on subsequent mounts.
  // The prop is then re-evaluated to true only the next time we POST a
  // fresh session through handleCreate / quick launch / auto-create.
  // A quick launch creates a session before there is a normal chat surface.
  // Store the payload before navigation so ChatInterface's existing
  // initial-message effect performs the real upload/send lifecycle.
  const [pendingInitialMessage, setPendingInitialMessage] =
    useState<PendingInitialMessage | null>(null);
  // Leader-key project creation has no local button surface on which to show
  // a failure, so retain its project here for an accessible retry notice.
  const [projectTaskChatCreationFailure, setProjectTaskChatCreationFailure] =
    useState<Project | null>(null);
  // Cleared immediately after the very first render where the prop is
  // consumed.  Mount-on-effect order guarantees ChatInterface has already
  // mounted (and useAgnoChat has run its mount useEffect) by the time this
  // fires — so the next render evaluates the prop back to false without
  // having skipped the /runs check.
  useEffect(() => {
    if (justCreatedSessionId && justCreatedSessionId === activeSessionId) {
      setJustCreatedSessionId(null);
    }
  }, [justCreatedSessionId, activeSessionId]);

  // ── ChatInput focus bump ────────────────────────────────────────────────
  // Bumped after a successful rename of the active session or after a
  // successful agent switch. Combined with sessionId into the autoFocusKey
  // passed to PromptInputTextarea, this re-fires the existing auto-focus
  // effect so focus returns to the chat text field after these UX
  // transitions — the user no longer has to click into the input to keep
  // typing. The PromptInputTextarea auto-focus is skipped on mobile, so
  // this won't pop up the on-screen keyboard unexpectedly.
  const [chatInputFocusTick, setChatInputFocusTick] = useState(0);

  const didCreateRef = useRef(false);
  // Root is an agent-hub landing route, not a session route. Keep its
  // selection unresolved until pins, catalog, and default-pin bootstrap have
  // settled so a stored session cannot win the race on first paint.
  const rootLandingRouteRef = useRef(isRootRoute);
  const rootPinnedLandingResolvedRef = useRef(!isRootRoute);
  const [rootPinnedLandingVersion, setRootPinnedLandingVersion] = useState(0);
  if (rootLandingRouteRef.current !== isRootRoute) {
    rootLandingRouteRef.current = isRootRoute;
    rootPinnedLandingResolvedRef.current = !isRootRoute;
  }
  const pendingCheckRef = useRef<string | null>(null);
  const fallbackActiveSessionRequestRef = useRef<{
    sessionId: string;
    requestId: number;
  } | null>(null);
  const fallbackActiveSessionRequestCounterRef = useRef(0);

  // The sessions endpoint only returns top-level records. When a thread is
  // opened as its own full-screen route, fetch its detail so the agent and
  // model switchers receive a real active session instead of `undefined`.
  useEffect(() => {
    // The agent hub is deliberately a non-session surface. A route change
    // can render once before the URL-sync effect clears the prior selection,
    // so stop the thread-detail fallback here as well rather than issuing a
    // transient request for the conversation that was just left.
    if (isNonChatSurface) {
      fallbackActiveSessionRequestRef.current = null;
      setFallbackActiveSession(null);
      return;
    }
    const isTopLevelSession = activeSessionId !== null
      && sessions.some((session) => session.id === activeSessionId);
    if (!activeSessionId || isTopLevelSession) {
      fallbackActiveSessionRequestRef.current = null;
      setFallbackActiveSession(null);
      return;
    }

    const inFlight = fallbackActiveSessionRequestRef.current;
    if (inFlight?.sessionId === activeSessionId) return;

    const sessionIdToFetch = activeSessionId;
    const requestId = fallbackActiveSessionRequestCounterRef.current + 1;
    fallbackActiveSessionRequestCounterRef.current = requestId;
    fallbackActiveSessionRequestRef.current = { sessionId: sessionIdToFetch, requestId };
    let cancelled = false;
    const isCurrentRequest = () => {
      const current = fallbackActiveSessionRequestRef.current;
      return !cancelled
        && current?.sessionId === sessionIdToFetch
        && current.requestId === requestId;
    };

    // Don't retain data for the previously opened thread while this detail
    // request is resolving. The active-session memo only accepts matching
    // IDs, but clearing here also keeps the fallback cache accurate.
    setFallbackActiveSession((current) => (
      current?.id === sessionIdToFetch ? current : null
    ));

    void (async () => {
      try {
        const res = await fetchWithWorkOSAuth(
          `/api/sessions/${encodeURIComponent(sessionIdToFetch)}`,
          { headers: {} },
          getAccessToken,
        );
        if (!res.ok || !isCurrentRequest()) return;

        const session = await res.json() as Session;
        if (!isCurrentRequest() || session.id !== sessionIdToFetch) return;
        setFallbackActiveSession(session);
      } catch {
        // The stale-session validation below remains responsible for 404s.
        // A transient detail failure should not disrupt an already-resolved
        // thread's controls.
      } finally {
        const current = fallbackActiveSessionRequestRef.current;
        if (current?.sessionId === sessionIdToFetch && current.requestId === requestId) {
          fallbackActiveSessionRequestRef.current = null;
        }
      }
    })();

    return () => {
      cancelled = true;
      const current = fallbackActiveSessionRequestRef.current;
      if (current?.sessionId === sessionIdToFetch && current.requestId === requestId) {
        fallbackActiveSessionRequestRef.current = null;
      }
    };
  }, [activeSessionId, sessions, getAccessToken, isNonChatSurface]);

  useEffect(() => {
    // Projects uses the same session shell/sidebar but should not silently
    // select or create a normal conversation when its workspace loads.
    if (isNonChatSurface) return;
    // Root first resolves its pinned-agent landing. Do not restore a stored
    // session, select history, or auto-create while that decision is pending.
    if (isRootRoute && !rootPinnedLandingResolvedRef.current) return;
    // Only a successful list response can establish that the account is
    // empty. A failed initial request must never trigger auto-creation.
    if (!sessionsLoaded || loading) return;

    // ── Stale-session validation ─────────────────────────────────────────
    // The active session ID is initialised from localStorage, which may hold
    // a reference to a session that no longer exists — it was archived,
    // deleted from another device, or belongs to a different tenant after a
    // login switch.  Without this check the stale ID bypasses both the
    // auto-select and auto-create branches below (because !activeSessionId
    // is false), leaving ChatInterface pointing at a non-existent session
    // and causing 404s when the user sends a message or switches agents.
    //
    // Unknown IDs are checked against the session endpoint instead of being
    // cleared synchronously. The top-level list intentionally omits child
    // sessions, so a valid thread can be absent from ``knownSessionIds``.
    //
    // BUT: if we just created this session (justCreatedSessionId matches),
    // it may not yet be in knownSessionIds — though create() adds it
    // immediately, a refresh triggered by a previous run's status change
    // can race.  In that case, keep the active ID and trust the next
    // refresh to include it.
    if (activeSessionId && !knownSessionIds.has(activeSessionId)) {
      if (activeSessionId === justCreatedSessionId) {
        // Just-created session not yet known — don't clear it.
        return;
      }
      if (pendingCheckRef.current === activeSessionId) return;

      const sessionIdToValidate = activeSessionId;
      pendingCheckRef.current = sessionIdToValidate;
      void (async () => {
        try {
          const res = await fetchWithWorkOSAuth(
            `/api/sessions/${encodeURIComponent(sessionIdToValidate)}`,
            { headers: {} },
            getAccessToken,
          );

          // A newer navigation started while this request was in flight.
          if (activeSessionIdRef.current !== sessionIdToValidate) return;

          if (res.status === 200) {
            registerKnownSessionIds([sessionIdToValidate]);
          } else if (res.status === 404) {
            console.log("[ui] Active session ID confirmed stale, clearing:", sessionIdToValidate);
            setActiveSessionId(null);
          }
          // For all other response statuses, fail open and leave the current
          // session active. A transient auth or network problem must not
          // discard the user's URL/local session selection.
        } catch {
          // Fail open on network errors for the same reason as above.
        } finally {
          // Do not clear a newer validation request started for another ID.
          if (pendingCheckRef.current === sessionIdToValidate) {
            pendingCheckRef.current = null;
          }
        }
      })();
      return;
    }

    // ── Search guard for auto-select / auto-create ───────────────────────
    // The branches below operate on the ``sessions`` array directly —
    // picking the most recent session or checking whether the account has
    // any sessions at all.  While a search is active, ``sessions`` is a
    // server-filtered subset, so we must skip: we shouldn't auto-select a
    // filtered result, and we must not auto-create a session just because a
    // search returned no matches.  ``debouncedQuery`` is always in sync with
    // ``sessions`` (it is the query the array was fetched with), so it is
    // the correct signal for "is sessions filtered right now?".
    if (debouncedQuery.trim().length > 0) return;

    if (sessions.length > 0 && !activeSessionId) {
      const mostRecent = sessions.reduce((a, b) =>
        new Date(a.last_active_at) > new Date(b.last_active_at) ? a : b
      );
      setActiveSessionId(mostRecent.id);
    } else if (knownSessionIds.size === 0 && !activeSessionId && !didCreateRef.current) {
      // No sessions exist at all — auto-create once with the saved user
      // default (falling back to the tenant default) so the user isn't
      // stuck. We check knownSessionIds.size
      // (unfiltered) rather than sessions.length so an empty search result
      // doesn't trigger a spurious auto-create.
      void attemptInitialSession(
        didCreateRef,
        getUserDefaultAgentId,
        async () => (await getCachedTenantDefault())?.entityId ?? null,
        (agentId) => create(undefined, agentId),
      ).then((session) => {
        if (session) {
          setJustCreatedSessionId(session.id);
          setActiveSessionId(session.id);
        }
      });
    }
  }, [isNonChatSurface, isRootRoute, rootPinnedLandingVersion, sessionsLoaded, loading, sessions, knownSessionIds, activeSessionId, justCreatedSessionId, create, setActiveSessionId, getCachedTenantDefault, getAccessToken, getUserDefaultAgentId, registerKnownSessionIds]);

  const createAndOpenSession = useCallback(async (agentId: string | null) => {
    const session = await create(undefined, agentId);
    if (session) {
      setJustCreatedSessionId(session.id);
      // Use the same state-first navigation path as sidebar selection. Calling
      // navigate() directly leaves the previous session active for one render
      // while Wouter publishes the new route, which is enough for an
      // in-flight refresh or thread expansion to associate the new row with
      // the old sidebar context.
      setActiveSessionId(session.id);
    }
  }, [create, setActiveSessionId]);

  const handleCreate = useCallback(async () => {
    const userDefaultAgentId = await getUserDefaultAgentId();
    if (userDefaultAgentId) {
      await createAndOpenSession(userDefaultAgentId);
      return;
    }

    // Default to the agent of the currently open session so that a new
    // conversation continues with the same agent the user is looking at.
    // Fall back to the tenant default when there is no active session or it
    // has no agent assigned.
    const activeAgentId = activeSessionId
      ? sessions.find((s) => s.id === activeSessionId)?.agent_id ?? null
      : null;
    const agentId = activeAgentId ?? (await getCachedTenantDefault())?.entityId ?? null;
    await createAndOpenSession(agentId);
  }, [activeSessionId, createAndOpenSession, getCachedTenantDefault, getUserDefaultAgentId, sessions]);

  /**
   * Creates a project task and opens its bound task session directly.
   *
   * Current servers create that session atomically with the task. Older
   * servers return an unbound task, so retain the create-and-bind fallback
   * before handing any optional first message to ChatInterface.
   */
  const createAndOpenProjectTaskChat = useCallback(async (
    project: ProjectTaskChatTarget,
    {
      title = t("projects:taskTitleFallback"),
      description,
      initialMessage,
      initialFiles = [],
    }: ProjectTaskChatOptions = {},
  ): Promise<void> => {
    const task = await createTask(
      project.id,
      title,
      null,
      undefined,
      description,
      "default",
    );
    if (!task) throw new Error(t("quickLaunch.errors.createTask"));

    let boundTask = task;
    let sessionId = task.session_id;
    if (!sessionId) {
      const defaultAgentId = project.default_agent_id?.trim();
      if (!defaultAgentId) {
        throw new Error(t("quickLaunch.defaultAgentUnavailable"));
      }
      const fallbackSession = await create(
        undefined,
        defaultAgentId,
        { [PROJECT_TASK_SESSION_METADATA_KEY]: true },
        project.id,
      );
      if (!fallbackSession) {
        throw new Error(t("quickLaunch.errors.createTaskSession"));
      }
      const taskWithSession = await bindTaskSession(task.id, fallbackSession.id);
      if (!taskWithSession?.session_id) {
        throw new Error(t("quickLaunch.errors.bindTaskSession"));
      }
      boundTask = taskWithSession;
      sessionId = taskWithSession.session_id;
    }

    // Independent task hooks power the project sidebar and workspace. Publish
    // the confirmed task before entering the chat so an expanded folder does
    // not have to wait for its next polling cycle to show the new card.
    notifyTaskChanged(boundTask);

    // Set all navigation-sensitive state before switching to /session/:id.
    // The normal ChatInterface handoff then performs the first-message upload
    // and send lifecycle, if this caller supplied one.
    setJustCreatedSessionId(sessionId);
    if (initialMessage !== undefined) {
      setPendingInitialMessage({ sessionId, text: initialMessage, initialFiles });
    }
    setActiveSessionId(sessionId);
  }, [bindTaskSession, create, createTask, setActiveSessionId, t]);

  // <leader>p starts the same direct task-chat creation used by the sidebar
  // and quick launch. Unlike the old route flag, the project workspace is
  // never mounted just to create and immediately leave a task.
  const handleOpenProjectNewTaskChat = useCallback((projectId: string) => {
    const project = sharedProjects.projects.find((candidate) => candidate.id === projectId);
    if (!project) return;

    setProjectTaskChatCreationFailure(null);
    void createAndOpenProjectTaskChat(project).catch(() => {
      setProjectTaskChatCreationFailure(project);
    });
    setMenuOpen(false);
  }, [createAndOpenProjectTaskChat, sharedProjects.projects]);

  const retryProjectTaskChatCreation = useCallback(() => {
    if (!projectTaskChatCreationFailure) return;
    const project = projectTaskChatCreationFailure;
    setProjectTaskChatCreationFailure(null);
    void createAndOpenProjectTaskChat(project).catch(() => {
      setProjectTaskChatCreationFailure(project);
    });
  }, [createAndOpenProjectTaskChat, projectTaskChatCreationFailure]);

  const handleSelect = useCallback((id: string) => {
    setActiveSessionId(id);
  }, [setActiveSessionId]);

  const leaderSequences = useMemo<readonly LeaderKeyCommand[]>(() => [
    {
      code: "KeyN",
      label: t("leaderKeys.newChat"),
      action: handleCreate,
    },
    {
      code: "KeyA",
      label: t("leaderKeys.agents"),
      load: loadLeaderAgents,
      onSelect: (agent) => createAndOpenSession(agent.id),
      loadingLabel: !agentCatalogLoaded || !agentPinsLoaded
        ? t("leaderKeys.warmingAgents")
        : t("leaderKeys.loadingAgents"),
      emptyLabel: t("leaderKeys.noAgents"),
      unavailableLabel: t("leaderKeys.agentsUnavailable"),
    },
    {
      code: "KeyT",
      label: t("leaderKeys.gotoSessions"),
      load: loadLeaderSessions,
      onSelect: (session) => handleSelect(session.id),
      loadingLabel: t("leaderKeys.loadingSessions"),
      emptyLabel: t("leaderKeys.noSessions"),
      unavailableLabel: t("leaderKeys.sessionsUnavailable"),
    },
    {
      code: "KeyP",
      label: t("leaderKeys.pinnedProjects"),
      load: loadLeaderPinnedProjects,
      onSelect: (project) => handleOpenProjectNewTaskChat(project.id),
      loadingLabel: !sharedProjects.loaded
        ? t("leaderKeys.warmingPinnedProjects")
        : t("leaderKeys.loadingPinnedProjects"),
      emptyLabel: t("leaderKeys.noPinnedProjects"),
      unavailableLabel: t("leaderKeys.pinnedProjectsUnavailable"),
    },
  ], [
    createAndOpenSession,
    handleCreate,
    handleOpenProjectNewTaskChat,
    handleSelect,
    loadLeaderAgents,
    loadLeaderPinnedProjects,
    loadLeaderSessions,
    agentCatalogLoaded,
    agentPinsLoaded,
    sharedProjects.loaded,
    t,
  ]);
  const leaderKeys = useLeaderKeys(leaderSequences);

  const handleMenuToggle = useCallback(() => {
    setMenuOpen((prev) => !prev);
  }, []);

  const openAgentConversations = useCallback((agentId: string) => {
    const agent = allAgents.find((candidate) => (
      candidate.id === agentId && candidate.type === "agent"
    ));
    if (!agent) return;
    // Agent hubs are non-session surfaces. Clear the local selection before
    // routing so a previous conversation cannot briefly render or appear
    // active in the sidebar while Wouter publishes the new URL.
    activeSessionIdRef.current = null;
    setActiveSessionIdRaw(null);
    setActiveProjectSessionId(null);
    navigate(`/agent/${encodeURIComponent(agent.id)}/conversations`);
    setMenuOpen(false);
  }, [allAgents]);

  // `/` is the neutral landing route. Once the shell has a safe pin/catalog
  // snapshot, turn it into the conversations hub for the first *displayed*
  // pinned agent. The same ordering API feeds the sidebar, so marked agents
  // promoted above saved user pins become the landing target too. Session and
  // panel deep links never reach this branch because their pathname/asides win.
  useEffect(() => {
    if (!isRootRoute || rootPinnedLandingResolvedRef.current) return;
    if (activeAside !== null || isPopout) return;

    const catalogUnavailable = !agentCatalogLoaded && (
      agentCatalogError !== null || agentsError !== null
    );
    const pinsUnavailable = !agentPinsLoaded && agentPinsLoadError !== null;
    if (pinsUnavailable || catalogUnavailable) {
      // Preserve the former root/session fallback when an independently
      // deployed server cannot provide a usable pin/catalog snapshot.
      rootPinnedLandingResolvedRef.current = true;
      setRootPinnedLandingVersion((version) => version + 1);
      return;
    }

    if (
      !agentPinsLoaded
      || agentPinsLoading
      || !agentCatalogLoaded
      || agentsLoading
      || !sidebarPinOrdering.loaded
      || !defaultPinBootstrapRef.current
      || defaultPinInitializationInFlightRef.current
      || defaultPinInitializing
    ) return;

    const accessibleAgentIds = new Set(
      allAgents
        .filter((agent) => agent.type === "agent")
        .map((agent) => agent.id),
    );
    const firstPinnedAgentId = firstAccessiblePinnedAgentId(
      orderedPinnedAgentIds,
      accessibleAgentIds,
    );

    // Mark before navigate: Wouter publishes its location asynchronously, and
    // a root re-render must not issue a duplicate route push in the meantime.
    rootPinnedLandingResolvedRef.current = true;
    setRootPinnedLandingVersion((version) => version + 1);
    if (firstPinnedAgentId) openAgentConversations(firstPinnedAgentId);
  }, [
    activeAside,
    agentCatalogError,
    agentCatalogLoaded,
    agentPinsLoadError,
    agentPinsLoaded,
    agentPinsLoading,
    agentsError,
    agentsLoading,
    allAgents,
    defaultPinBootstrapVersion,
    defaultPinInitializing,
    isPopout,
    isRootRoute,
    openAgentConversations,
    orderedPinnedAgentIds,
    sidebarPinOrdering.loaded,
  ]);

  const createConversationForAgent = useCallback(async (agentId: string) => {
    const agent = allAgents.find((candidate) => (
      candidate.id === agentId && candidate.type === "agent"
    ));
    if (!agent) return;
    const session = await create(undefined, agent.id);
    if (!session) return;
    setJustCreatedSessionId(session.id);
    setActiveSessionId(session.id);
  }, [allAgents, create, setActiveSessionId]);

  const handleQuickLaunch = useCallback(async ({
    target,
    text,
    files,
  }: QuickLaunchSubmission): Promise<void> => {
    if (target.kind === "project") {
      // Project quick launches must create a task before sending. Its
      // prompt-derived title remains eligible for first-message auto-titling.
      await createAndOpenProjectTaskChat(target, {
        title: suggestProjectTaskTitle(text),
        description: text,
        initialMessage: text,
        initialFiles: files,
      });
    } else {
      const session = await create(undefined, target.id);
      if (!session) throw new Error(t("quickLaunch.errors.createSession"));
      // Set all navigation-sensitive state before switching to /session/:id.
      // This lets the existing ChatInterface handoff wait for initial history
      // and use the normal upload/SSE/message persistence implementation.
      setJustCreatedSessionId(session.id);
      setPendingInitialMessage({ sessionId: session.id, text, initialFiles: files });
      setActiveSessionId(session.id);
    }

    // Usage is a low-stakes routing hint. A failed aggregate write must never
    // block or undo the session/task that was already created successfully.
    void recordQuickLaunchUsage(target, getAccessToken).catch(() => {});
  }, [create, createAndOpenProjectTaskChat, getAccessToken, setActiveSessionId, t]);

  const handleOpenProject = useCallback((selectedProjectId: string, taskId?: string) => {
    setActiveProjectSessionId(null);
    const params = new URLSearchParams();
    if (taskId) params.set("task", taskId);
    const queryString = params.toString();
    navigate(`/project/${encodeURIComponent(selectedProjectId)}${queryString ? `?${queryString}` : ""}`);
    setMenuOpen(false);
  }, []);

  const handleRename = useCallback(
    async (id: string, title: string) => {
      // Only focus the textarea when renaming the *active* session — silently
      // bumping focus after a background rename would be surprising.
      const updated = await rename(id, title);
      if (updated && updated.id === activeSessionId) {
        setChatInputFocusTick((t) => t + 1);
      }
    },
    [rename, activeSessionId]
  );

  const handleArchive = useCallback(async (id: string) => {
    // A task-backed conversation archives its backing task too: the task is
    // the canonical work item, so mark it cancelled (the same outcome as the
    // kanban board's delete action) before the session row is archived. The
    // session is still archived even when the task update fails.
    const sidebarTask = sidebarTaskRef.current?.sessionId === id
      ? sidebarTaskRef.current.task
      : null;
    if (sidebarTask && (sidebarTask.descendant_open_count ?? 0) > 0 && !window.confirm(t("projects:subtasks.parentArchiveConfirm", { count: sidebarTask.descendant_open_count }))) return;
    if (sidebarTask) {
      const updated = await updateTask(sidebarTask.id, { status: "cancelled" });
      if (updated) {
        cacheTaskStatus(id, "cancelled");
        setSidebarTask((current) => (
          current?.sessionId === id ? { ...current, task: updated } : current
        ));
      }
    }
    const archived = await archive(id);
    // The hook removes the row optimistically. If that was the conversation
    // currently on screen, leave its route rather than keeping a chat pointed
    // at a now-archived session. This also covers task chats, whose visible
    // ID is mirrored by activeSessionIdRef.
    if (archived && activeSessionIdRef.current === id) {
      setActiveSessionId(null);
    }
  }, [archive, setActiveSessionId, updateTask, cacheTaskStatus, t]);

  const handleConversationTaskCreated = useCallback(async (sessionId: string, task: Task) => {
    if (activeSessionIdRef.current === sessionId) {
      // The task response is already server-confirmed. Apply its binding to
      // the active chat immediately while the session-list refresh obtains
      // the complete updated session record (including its shared-task state).
      setSidebarTask({ sessionId, task });
      setFallbackActiveSession((current) => (
        current?.id === sessionId
          ? {
              ...current,
              metadata: {
                ...current.metadata,
                [PROJECT_TASK_SESSION_METADATA_KEY]: true,
              },
              project_id: task.project_id,
            }
          : current
      ));
    }
    await refreshSessionsInBackground();
  }, [refreshSessionsInBackground]);

  const handleAgentChange = useCallback(
    async (agentId: string) => {
      if (!activeSessionId) return;
      const updated = await updateAgent(activeSessionId, agentId);
      if (updated) {
        // `useSessions` only patches top-level records. Preserve the
        // confirmed selection when the active session is a full-screen
        // thread backed by fallbackActiveSession instead.
        setFallbackActiveSession((current) => (
          current?.id === updated.id ? updated : current
        ));
        setChatInputFocusTick((t) => t + 1);
      }
    },
    [activeSessionId, updateAgent]
  );

  // Cache the last resolved active-session object so it survives while a
  // search filters it out of `sessions`.  Without this, the agent switcher
  // (fed by activeSession?.agent_id) would flicker to its default whenever a
  // search hides the active session, even though the session stays selected.
  const lastActiveSessionRef = useRef<Session | undefined>(undefined);
  const activeSession = useMemo(() => {
    if (error && /HTTP (401|403)\b/.test(error)) {
      lastActiveSessionRef.current = undefined;
      return undefined;
    }
    const found = sessions.find((s) => s.id === activeSessionId);
    let resolved: Session | undefined;
    if (found) {
      resolved = found;
    } else if (fallbackActiveSession?.id === activeSessionId) {
      resolved = fallbackActiveSession;
    } else if (lastActiveSessionRef.current?.id === activeSessionId) {
      // Fall back to the cached object only when it still matches the active id.
      resolved = lastActiveSessionRef.current;
    }
    if (!resolved) return undefined;

    // A successful conversion returns the complete task before the session
    // list's follow-up refresh finishes. Overlay its known binding so the
    // active chat exposes task status and project state without a stale gap.
    const task = sidebarTask?.sessionId === resolved.id ? sidebarTask.task : null;
    const active = task
      ? {
          ...resolved,
          metadata: {
            ...resolved.metadata,
            [PROJECT_TASK_SESSION_METADATA_KEY]: true,
          },
          project_id: task.project_id,
        }
      : resolved;
    lastActiveSessionRef.current = active;
    return active;
  }, [sessions, activeSessionId, fallbackActiveSession, sidebarTask, error]);
  useEffect(() => { void sweep(true); }, [activeSessionId, activeSession?.agent_id, activeProjectId]);

  const sidebarTaskSessionId = activeSession && isProjectTaskSession(activeSession)
    ? activeSession.id
    : null;
  const activeSidebarTask = sidebarTask?.sessionId === sidebarTaskSessionId
    ? sidebarTask.task
    : null;

  // A task brief is generated asynchronously after its first prompt. Refresh
  // slowly while the answer streams, then once more when the run finishes.
  useEffect(() => {
    const sessionId = sidebarTaskSessionId;
    if (!sessionId) return;
    if (activeChatStatus !== "streaming") return;
    let cancelled = false;
    const refreshBrief = () => {
      void getTaskBySession(sessionId).then((task) => {
        if (!task || cancelled) return;
        if (sidebarTaskRef.current?.sessionId === sessionId) {
          setSidebarTask({ sessionId, task });
        }
        notifyTaskChanged(task);
      });
    };
    const initialTimer = window.setTimeout(refreshBrief, 5_000);
    const timer = window.setInterval(refreshBrief, 60_000);
    return () => {
      cancelled = true;
      window.clearTimeout(initialTimer);
      window.clearInterval(timer);
    };
  }, [sidebarTaskSessionId, activeChatStatus, getTaskBySession]);

  const streamingTaskSessionRef = useRef<string | null>(null);
  useEffect(() => {
    if (activeChatStatus === "streaming" && sidebarTaskSessionId) {
      streamingTaskSessionRef.current = sidebarTaskSessionId;
      return;
    }
    const completedSessionId = streamingTaskSessionRef.current;
    streamingTaskSessionRef.current = null;
    if (!completedSessionId || completedSessionId !== activeSessionId) return;
    void getTaskBySession(completedSessionId).then((task) => {
      if (!task || activeSessionIdRef.current !== completedSessionId) return;
      if (sidebarTaskRef.current?.sessionId === completedSessionId) {
        setSidebarTask({ sessionId: completedSessionId, task });
      }
      notifyTaskChanged(task);
    });
  }, [activeChatStatus, sidebarTaskSessionId, activeSessionId, getTaskBySession]);

  useEffect(() => {
    if (!sidebarTaskSessionId) {
      setSidebarTask(null);
      return;
    }

    let cancelled = false;
    if (sidebarTaskRef.current?.sessionId !== sidebarTaskSessionId) {
      setSidebarTask(null);
    }
    taskStatusFetchesRef.current.add(sidebarTaskSessionId);
    const getStartedAt = Date.now();
    void getTaskBySession(sidebarTaskSessionId).then((task) => {
      const taskId = task?.id ?? taskIdBySessionRef.current.get(sidebarTaskSessionId);
      const confirmed = taskId && confirmedTaskStatusesRef.current.get(`${sidebarTaskSessionId}:${taskId}`);
      if (confirmed && getStartedAt <= confirmed.confirmedAt) return;
      if (task) taskIdBySessionRef.current.set(sidebarTaskSessionId, task.id);
      cacheTaskStatus(sidebarTaskSessionId, task?.status ?? null);
      if (!cancelled) {
        setSidebarTask(task ? { sessionId: sidebarTaskSessionId, task } : null);
      }
    }).finally(() => {
      taskStatusFetchesRef.current.delete(sidebarTaskSessionId);
    });

    return () => {
      cancelled = true;
    };
  }, [sidebarTaskSessionId, getTaskBySession, cacheTaskStatus]);

  useEffect(() => {
    for (const session of sessions) {
      if (!isProjectTaskSession(session) || session.id === sidebarTaskSessionId) {
        continue;
      }
      const related = relatedTaskFromSession(session, taskIdBySessionRef.current.get(session.id));
      if (related !== undefined) {
        reconcileSessionTaskStatus(session, session.list_fetch_started_at ?? 0);
        continue;
      }
      if (
        taskStatusBySessionRef.current.has(session.id)
        || taskStatusFetchesRef.current.has(session.id)
      ) {
        continue;
      }

      taskStatusFetchesRef.current.add(session.id);
      const getStartedAt = Date.now();
      void getTaskBySession(session.id)
        .then((task) => {
          const taskId = task?.id ?? taskIdBySessionRef.current.get(session.id);
          const confirmed = taskId && confirmedTaskStatusesRef.current.get(`${session.id}:${taskId}`);
          if (confirmed && getStartedAt <= confirmed.confirmedAt) return;
          if (task) taskIdBySessionRef.current.set(session.id, task.id);
          cacheTaskStatus(session.id, task?.status ?? null);
        })
        .finally(() => {
          taskStatusFetchesRef.current.delete(session.id);
        });
    }
  }, [sessions, sidebarTaskSessionId, getTaskBySession, cacheTaskStatus, reconcileSessionTaskStatus]);

  const handleSidebarTaskStatusChange = useCallback(
    async (status: TaskStatus): Promise<boolean> => {
      if (!activeSidebarTask || !sidebarTaskSessionId) return false;

      const updated = await updateTask(activeSidebarTask.id, { status });
      if (!updated) return false;

      setSidebarTask((current) => (
        current?.sessionId === sidebarTaskSessionId && current.task.id === updated.id
          ? { ...current, task: updated }
          : current
      ));
      taskIdBySessionRef.current.set(sidebarTaskSessionId, updated.id);
      const updatedAt = Date.parse(updated.updated_at);
      confirmedTaskStatusesRef.current.set(`${sidebarTaskSessionId}:${updated.id}`, {
        status: updated.status,
        confirmedAt: Date.now(),
        updatedAt: Number.isFinite(updatedAt) ? updatedAt : null,
      });
      cacheTaskStatus(sidebarTaskSessionId, updated.status);
      return true;
    },
    [activeSidebarTask, sidebarTaskSessionId, updateTask, cacheTaskStatus],
  );

  const handleSidebarTaskDescriptionSave = useCallback(
    async (description: string): Promise<boolean> => {
      if (!activeSidebarTask || !sidebarTaskSessionId) return false;
      const updated = await updateTask(activeSidebarTask.id, { description });
      if (!updated) return false;
      setSidebarTask((current) => (
        current?.sessionId === sidebarTaskSessionId && current.task.id === updated.id
          ? { ...current, task: updated }
          : current
      ));
      notifyTaskChanged(updated);
      return true;
    },
    [activeSidebarTask, sidebarTaskSessionId, updateTask],
  );

  const handleSidebarTaskTitleSave = useCallback(async (title: string): Promise<boolean> => {
    if (!activeSidebarTask || !sidebarTaskSessionId) return false;
    const updated = await updateTask(activeSidebarTask.id, { title });
    if (!updated) return false;
    setSidebarTask((current) => current?.sessionId === sidebarTaskSessionId && current.task.id === updated.id
      ? { ...current, task: updated } : current);
    notifyTaskChanged(updated);
    return true;
  }, [activeSidebarTask, sidebarTaskSessionId, updateTask]);

  const handleSidebarTaskArtifactRemove = useCallback(async (path: string): Promise<boolean> => {
    if (!activeSidebarTask || !sidebarTaskSessionId) return false;
    const updated = await removeTaskArtifact(activeSidebarTask.id, path);
    if (!updated) return false;
    setSidebarTask((current) => (
      current?.sessionId === sidebarTaskSessionId && current.task.id === updated.id
        ? { ...current, task: updated } : current
    ));
    notifyTaskChanged(updated);
    return true;
  }, [activeSidebarTask, sidebarTaskSessionId, removeTaskArtifact]);

  const activeSidebarTaskProjectId = activeSidebarTask?.project_id ?? null;

  useEffect(() => {
    if (!sidebarTaskSessionId || !activeSidebarTaskProjectId) {
      setSidebarTaskProjectSegment(null);
      return;
    }

    let cancelled = false;
    setSidebarTaskProjectSegment(null);

    void (async () => {
      try {
        const response = await fetchWithWorkOSAuth(
          `/api/projects/${encodeURIComponent(activeSidebarTaskProjectId)}`,
          { headers: {} },
          getAccessToken,
        );
        if (!response.ok) return;

        const project = await response.json() as Pick<Project, "id" | "name">;
        if (!cancelled && project.id === activeSidebarTaskProjectId) {
          setSidebarTaskProjectSegment({
            sessionId: sidebarTaskSessionId,
            projectId: project.id,
            segment: { id: project.id, title: project.name },
          });
        }
      } catch {
        // The task chat remains usable without a project breadcrumb if the
        // project is no longer accessible or cannot be loaded.
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [activeSidebarTaskProjectId, getAccessToken, sidebarTaskSessionId]);

  const activeSidebarTaskProjectSegment = activeSidebarTask
    && sidebarTaskProjectSegment?.sessionId === sidebarTaskSessionId
    && sidebarTaskProjectSegment.projectId === activeSidebarTaskProjectId
    ? sidebarTaskProjectSegment.segment
    : undefined;

  const { segments: sessionBreadcrumb } = useSessionBreadcrumb(
    activeSession?.id ?? null,
    getAccessToken,
    activeSession,
    activeBootstrap,
    bootstrapPending,
  );
  const threadPanelTitle = useMemo(() => {
    if (!threadSessionId) return null;
    return sessions.find((session) => session.id === threadSessionId)?.title
      ?? threadTitleCache[threadSessionId]?.title
      ?? null;
  }, [sessions, threadSessionId, threadTitleCache]);

  const threadPanelParentSessionId = resolveThreadPanelParentSessionId(
    isProjectsWorkspaceRoute,
    activeSessionId,
    activeProjectSessionId,
  );
  const threadPanelParentSession = isProjectsWorkspaceRoute
    ? sessions.find((session) => session.id === activeProjectSessionId)
    : activeSession;
  const viewSessionId = isProjectsWorkspaceRoute ? activeProjectSessionId : activeSessionId;
  const viewAgentId = isProjectsWorkspaceRoute
    ? sessions.find((session) => session.id === activeProjectSessionId)?.agent_id ?? activeProjectAgentId
    : activeSession?.agent_id;

  const configuredAgentViews = useMemo(
    () => (agentCatalogLoaded ? allAgents : previewAgents).find((agent) => agent.id === viewAgentId)?.views ?? [],
    [viewAgentId, allAgents, previewAgents, agentCatalogLoaded],
  );

  const activeAgentViews = useMemo(
    () => viewSessionId && viewAgentId
      ? mergeConversationViews(
          viewAgentId,
          configuredAgentViews,
          viewSessionId,
          conversationViews,
        )
      : [],
    [
      viewAgentId,
      viewSessionId,
      configuredAgentViews,
      conversationViews,
    ],
  );

  const selectedHubAgent = useMemo(
    () => routeAgentId
      ? allAgents.find((agent) => agent.id === routeAgentId && agent.type === "agent")
      : undefined,
    [allAgents, routeAgentId],
  );

  const nextActiveAgentView = useMemo(
    () => activeAgentViews.find((view) => viewRefKey(view.ref) === activeViewId),
    [activeAgentViews, activeViewId],
  );
  // Conversation-view refreshes reconstruct the full tab list. Retain the
  // selected tab when it still represents the same view so its child keeps a
  // stable viewRef and does not reset its loaded page or scroll position.
  const stableActiveAgentView = useRef<{
    key: string | null;
    value: ViewTab | undefined;
  }>({ key: null, value: undefined });
  const nextActiveAgentViewKey = nextActiveAgentView
    ? viewTabIdentityKey(nextActiveAgentView)
    : null;
  if (stableActiveAgentView.current.key !== nextActiveAgentViewKey) {
    stableActiveAgentView.current = {
      key: nextActiveAgentViewKey,
      value: nextActiveAgentView,
    };
  }
  const activeAgentView = stableActiveAgentView.current.value;
  const dashboardCacheOwner = JSON.stringify([user?.id ?? null, viewSessionId]);
  const [dashboardCache, setDashboardCache] = useState<{
    owner: string;
    views: ViewTab[];
  }>(() => ({ owner: dashboardCacheOwner, views: [] }));
  const keptDashboardViews = dashboardCache.owner === dashboardCacheOwner
    ? dashboardCache.views
    : [];
  const agentDashboardListReady = !agentsLoading
    && (agentCatalogLoaded || (Boolean(agentCatalogError) && previewAgents.length === 0));
  const conversationDashboardListReady = agentDashboardListReady
    && conversationViewsLoaded && !conversationViewsLoading && !conversationViewsError;
  const dashboardIsCurrent = useCallback((view: ViewTab) => {
    if (view.ref.agentId !== viewAgentId) return false;
    if (view.ref.scope === "conversation" && view.ref.sessionId !== viewSessionId) return false;
    const listReady = view.ref.scope === "agent"
      ? agentDashboardListReady
      : conversationDashboardListReady;
    return !listReady || activeAgentViews.some((candidate) => (
      viewRefKey(candidate.ref) === viewRefKey(view.ref)
      && candidate.type === "dashboard"
      && candidate.availability !== "unavailable"
    ));
  }, [activeAgentViews, agentDashboardListReady, conversationDashboardListReady, viewAgentId, viewSessionId]);
  useEffect(() => {
    setDashboardCache((current) => {
      const currentViews = current.owner === dashboardCacheOwner ? current.views : [];
      const retained = currentViews.filter(dashboardIsCurrent);
      if (
        !isProjectsWorkspaceRoute
        && activeAside?.kind === "agent"
        && activeAgentView?.type === "dashboard"
        && activeAgentView.availability !== "unavailable"
        && !retained.some((view) => viewRefKey(view.ref) === activeViewId)
      ) retained.push(activeAgentView);
      return current.owner === dashboardCacheOwner
        && retained.length === currentViews.length
        && retained.every((view, index) => view === currentViews[index])
        ? current
        : { owner: dashboardCacheOwner, views: retained };
    });
  }, [activeAgentView, activeAside?.kind, activeViewId, dashboardCacheOwner, dashboardIsCurrent, isProjectsWorkspaceRoute]);
  const visibleDashboardViews = keptDashboardViews.filter(dashboardIsCurrent);
  if (
    !isProjectsWorkspaceRoute
    && activeAside?.kind === "agent"
    && activeAgentView?.type === "dashboard"
    && activeAgentView.availability !== "unavailable"
    && !visibleDashboardViews.some((view) => viewRefKey(view.ref) === activeViewId)
  ) visibleDashboardViews.push(activeAgentView);
  useEffect(() => {
    if (activeAside?.kind !== "agent" || !activeAgentView) return;
    if (isProjectsWorkspaceRoute && activeAgentView.type === "dashboard") {
      navigate(viewPagePath(activeAgentView.ref), { replace: true });
    } else if (activeAgentView.type === "visual_editor") {
      if (isMobile) navigate(viewPagePath(activeAgentView.ref), { replace: true });
      else asideManager.closeAgentView();
    }
  }, [activeAgentView, activeAside?.kind, asideManager.closeAgentView, isMobile, isProjectsWorkspaceRoute, navigate]);
  const pinnedFileEditingUnsupported = Boolean(
    activeAgentView?.ref.scope === "conversation"
    && activeAgentView.file_editing_contract_version === 1
    && conversationViewCapabilities?.pinnedFileEditingSupported === false,
  );

  // A panel belongs to the current session's agent. Preserve an initial
  // deep-link while its session metadata loads, then close only when a
  // genuinely resolved owner changes. A session id collapsing to null is
  // not an owner change: non-chat surfaces (the agent hub) clear the
  // active session without invalidating a URL deep-linked panel, which
  // the destination route validates on its own.
  const agentViewOwnerRef = useRef<{
    sessionId: string | null;
    agentId: string | null;
  } | null>(null);
  useEffect(() => {
    const nextOwner = {
      sessionId: viewSessionId,
      agentId: viewAgentId ?? null,
    };
    const previousOwner = agentViewOwnerRef.current;
    const ownerChanged = Boolean(
      previousOwner
      && (
        (
          previousOwner.sessionId !== null
          && nextOwner.sessionId !== null
          && previousOwner.sessionId !== nextOwner.sessionId
        )
        || (
          previousOwner.agentId !== null
          && nextOwner.agentId !== null
          && previousOwner.agentId !== nextOwner.agentId
        )
      ),
    );
    agentViewOwnerRef.current = nextOwner;
    if (ownerChanged) asideManager.closeAgentView();
  }, [viewAgentId, viewSessionId, asideManager.closeAgentView]);

  // A URL may outlive a deleted, unpinned, or RBAC-hidden view. Wait for both
  // view sources to settle before removing a conversation-scoped target so a
  // valid deep link never flashes closed during initial data loading.
  useEffect(() => {
    if (activeAside?.kind !== "agent") return;
    // This route has no active conversation session. Its own RBAC-filtered
    // configured-view list performs the equivalent target validation.
    if (isAgentConversationsRoute) return;
    if (!viewSessionId) {
      asideManager.closeAgentView();
      return;
    }
    if (!viewAgentId || agentsLoading) return;
    const isConversationScopedView = activeViewId?.startsWith("conversation:") ?? false;
    if (
      isConversationScopedView
      && (!conversationViewsLoaded || conversationViewsLoading || conversationViewsError)
    ) return;
    if (!activeAgentView) asideManager.closeAgentView();
  }, [
    activeAgentView,
    activeAside?.kind,
    viewAgentId,
    viewSessionId,
    activeViewId,
    agentsLoading,
    asideManager.closeAgentView,
    conversationViewsError,
    conversationViewsLoaded,
    conversationViewsLoading,
    isAgentConversationsRoute,
  ]);

  // Child-session panels need a parent chat shell. A pasted child-panel link
  // (or any non-agent aside on the agent hub) is invalid in this context, so
  // normalise it back to the ordinary page instead of leaving a non-rendering
  // selection behind.
  useEffect(() => {
    if (isAgentConversationsRoute && activeAside?.kind && activeAside.kind !== "agent") {
      asideManager.closeAside();
      return;
    }
    const isChildAside = activeAside?.kind === "thread" || activeAside?.kind === "sub-agent";
    if (!isChildAside) return;
    if (
      !isProjectsWorkspaceRoute && !threadPanelParentSessionId
    ) {
      asideManager.closeAside();
    }
  }, [
    activeAside?.kind,
    asideManager.closeAside,
    isAgentConversationsRoute,
    isProjectsWorkspaceRoute,
    threadPanelParentSessionId,
  ]);

  const handleViewSelect = useCallback((view: ViewTab) => {
    if (view.ref.scope === "agent" && !agentCatalogLoaded) return;
    if (view.ref.scope === "conversation" && (!conversationViewsLoaded || conversationViewsLoading || conversationViewsError || !conversationViews.some((item) => item.id === view.ref.viewId && item.availability === "available"))) return;
    if (view.type === "visual_editor") {
      if (isMobile) navigate(viewPagePath(view.ref));
      else window.open(viewPagePath(view.ref), "_blank", "noopener");
      return;
    }
    if (isProjectsWorkspaceRoute && view.type === "dashboard") {
      navigate(viewPagePath(view.ref));
      return;
    }
    asideManager.openAgentView(viewRefKey(view.ref));
  }, [asideManager.openAgentView, agentCatalogLoaded, conversationViews, conversationViewsLoaded, conversationViewsLoading, conversationViewsError, isMobile, isProjectsWorkspaceRoute, navigate]);

  // A late resolve response must not replace a newer file selection.
  const fileLinkResolveRequestRef = useRef(0);
  const handleFileLinkClick = useCallback((url: string, fileName: string) => {
    const requestId = ++fileLinkResolveRequestRef.current;
    if (
      isHtmlFile(url, fileName)
      || isXlsxFile(url, fileName)
      || isPdfFile(url, fileName)
      || isDocxFile(url, fileName)
      || isImageFile(url, fileName)
    ) {
      const token = extractFileToken(url);
      asideManager.openViewer(url, isXlsxFile(url, fileName) && token
        ? extractFileInfoFromToken(token)?.fileName ?? fileName : fileName);
      return;
    }
    const isSource = isSourceFile(url, fileName);
    if (!isMarkdownFile(url, fileName) && !isSource) return;

    const sessionId = activeSessionId;
    const token = extractFileToken(url);
    const path = token ? extractFilePathFromToken(token) : null;
    const viewerFileName = isSource && token
      ? extractFileInfoFromToken(token)?.fileName ?? fileName
      : fileName;
    if (!sessionId || !path) {
      asideManager.openViewer(url, viewerFileName);
      return;
    }

    const requestIsCurrent = () => (
      fileLinkResolveRequestRef.current === requestId
      && activeSessionIdRef.current === sessionId
    );

    void resolveConversationFileView(sessionId, path, getAccessToken)
      .then(async (resolved) => {
        if (!requestIsCurrent()) return;
        if (!resolved) {
          asideManager.openViewer(url, viewerFileName);
          return;
        }

        // Keep the sidebar's local view list authoritative before selecting
        // the returned pin (including one created by an earlier agent run).
        let view = resolved;
        try {
          const nextViews = await refreshConversationViews();
          if (!requestIsCurrent()) return;
          view = nextViews.find((candidate) => candidate.id === resolved.id) ?? resolved;
        } catch (error) {
          console.error("[ui] Failed to refresh resolved conversation view:", error);
        }

        if (requestIsCurrent()) {
          handleViewSelect(conversationViewTab(sessionId, view));
        }
      })
      .catch((error) => {
        if (!requestIsCurrent()) return;
        console.error("[ui] Failed to resolve pinned conversation view:", error);
        asideManager.openViewer(url, viewerFileName);
      });
  }, [
    activeSessionId,
    asideManager.openViewer,
    getAccessToken,
    handleViewSelect,
    refreshConversationViews,
  ]);

  const isViewerFilePinned = useCallback((path: string): boolean => (
    browserPinnedFilePaths.has(path)
  ), [browserPinnedFilePaths]);

  const handlePinViewerFile = useCallback(async (path: string): Promise<void> => {
    const sessionId = activeSessionIdRef.current;
    if (!sessionId) throw new Error(t("views.conversationUnavailable"));
    if (browserPinnedFilePaths.has(path)) return;
    setPinningFilePath(path);
    try {
      const event = await pinConversationFile(sessionId, path, getAccessToken);
      if (activeSessionIdRef.current !== sessionId || event.session_id !== sessionId) return;
      setBrowserPinnedFilePaths((current) => {
        if (current.has(path)) return current;
        const next = new Set(current);
        next.add(path);
        return next;
      });
      try {
        const nextViews = await refreshConversationViews();
        if (activeSessionIdRef.current !== sessionId) return;
        const pinned = nextViews.find((view) => view.id === event.view_id);
        if (pinned) handleViewSelect(conversationViewTab(sessionId, pinned));
      } catch (error) {
        // Persistence already succeeded. Keep the local action disabled and
        // let the normal conversation-view refresh make the tab visible.
        console.error("[ui] Failed to refresh pinned conversation view:", error);
      }
    } catch (error) {
      console.error("[ui] Failed to pin browser file to conversation:", error);
      throw error;
    } finally {
      if (activeSessionIdRef.current === sessionId) {
        setPinningFilePath((current) => current === path ? null : current);
      }
    }
  }, [browserPinnedFilePaths, getAccessToken, handleViewSelect, refreshConversationViews, t]);

  const handleViewUnpin = useCallback((view: ViewTab) => {
    if (view.ref.scope !== "conversation") return;
    if (!conversationViewsLoaded || conversationViewsLoading || conversationViewsError || !conversationViews.some((item) => item.id === view.ref.viewId && item.can_unpin)) return;
    if (activeViewId === viewRefKey(view.ref)) asideManager.closeAgentView();
    void unpinConversationView(view.ref.viewId).catch((error) => {
      console.error("[ui] Failed to unpin conversation view:", error);
    });
  }, [activeViewId, asideManager.closeAgentView, unpinConversationView, conversationViews, conversationViewsLoaded, conversationViewsLoading, conversationViewsError]);

  const handleToolCompleted = useCallback(({
    toolName,
    result,
  }: { toolName: string; result: unknown }) => {
    if (!CONVERSATION_VIEW_TOOL_NAMES.has(toolName)) return;
    if (toolName === "unpin_view") {
      const unpinned = conversationViewUnpinnedEvent(result);
      if (!unpinned || unpinned.session_id !== activeSessionIdRef.current) return;
      if (unpinned.removed) {
        if (activeViewId === `conversation:${unpinned.view_id}`) asideManager.closeAgentView();
        setBrowserPinnedFilePaths(new Set());
      }
      void refreshConversationViews().catch((error) => {
        if (error instanceof DOMException && error.name === "AbortError") return;
        console.error("[ui] Failed to refresh unpinned conversation view:", error);
        toast.error(t("views.errors.load"), { id: "conversation-views-refresh" });
      });
      return;
    }
    const event = conversationViewPinnedEvent(result);
    if (!event || event.session_id !== activeSessionIdRef.current) return;
    void refreshConversationViews()
      .then((nextViews) => {
        if (activeSessionIdRef.current !== event.session_id) return;
        const pinned = nextViews.find((view) => view.id === event.view_id);
        if (pinned && !activeViewIdRef.current) handleViewSelect(conversationViewTab(event.session_id, pinned));
      })
      .catch((error) => {
        if (error instanceof DOMException && error.name === "AbortError") return;
        console.error("[ui] Failed to refresh pinned conversation view:", error);
        toast.error(t("views.errors.load"), { id: "conversation-views-refresh" });
      });
  }, [activeViewId, asideManager.closeAgentView, handleViewSelect, refreshConversationViews, t]);

  const handleCodePreviewPublish = useCallback((prompt: string) => {
    if (
      !activeSessionId
      || activeChatStatus === "submitted"
      || activeChatStatus === "streaming"
    ) {
      return;
    }
    viewPublishRequestIdRef.current += 1;
    setViewPublishRequest({
      id: viewPublishRequestIdRef.current,
      sessionId: activeSessionId,
      prompt,
    });
  }, [activeChatStatus, activeSessionId]);

  const handleViewPublishRequestConsumed = useCallback((requestId: number) => {
    setViewPublishRequest((current) => (
      current?.id === requestId ? null : current
    ));
  }, []);

  // Restore the draft for the active session.  Memoised on activeSessionId so
  // the localStorage read + JSON.parse only runs on session switch, not on
  // every ChatPage re-render (e.g. notification-poll state changes).
  const activeDraft = useMemo(
    () => getDraft(activeSessionId ?? "").text,
    [activeSessionId]
  );
  const activeDraftAttachments = useMemo(
    () => getDraftAttemptFiles(activeSessionId ?? ""),
    [activeSessionId],
  );

  // Notification polling — use a patched fetch that injects Auth header.
  // Reuse the shared fetchWithAuth helper so it retries once when AuthKit
  // hasn't finished initializing the session yet.
  const fetchWithAuthForNotif = useCallback(
    async (url: string, init: RequestInit): Promise<Response> => {
      return fetchWithWorkOSAuth(url, init, getAccessToken);
    },
    [getAccessToken]
  );
  // This intentionally stays in memory only. A reload starts a new page
  // session, while a live SSE completion seen in this tab suppresses a slower
  // durable notification row for the remainder of the current session.
  const seenRunIdsRef = useRef<Set<string>>(new Set());
  const registerSeenRun = useCallback((_sessionId: string, runId: string) => {
    seenRunIdsRef.current.add(runId);
  }, []);
  const inbox = useInboxNotifications(
    fetchWithAuthForNotif,
    seenRunIdsRef,
    activeSessionIdRef,
  );
  // The durable inbox is authoritative for browser/OS delivery after its
  // first successful connection. A ref lets the legacy sidebar poller read
  // that fact without being recreated on every inbox refresh.
  const durableInboxAvailableRef = useRef(inbox.isAvailable);
  durableInboxAvailableRef.current = inbox.isAvailable;
  const { notificationState, markSessionRead, api: notifApi, setRunStatus } = useNotifications(
    fetchWithAuthForNotif,
    activeSessionIdRef,
    // When the notification poller detects that the session list has changed
    // (new sessions from other devices, auto-generated titles, activity
    // reordering), refresh the sidebar so it reflects the latest server state.
    // The poller only fires this callback when something actually changed —
    // not on every tick — so this doesn't add redundant requests.
    refreshSessionsInBackground,
    durableInboxAvailableRef,
    activeChildSessionIdRef,
    reconcileThreadSession,
    cacheTaskStatusesFromSessionList,
  );

  // A service-worker notification click adds notificationId to the routed
  // URL. The app is now authenticated, so it can acknowledge that durable
  // recipient row and then remove the one-shot parameter.
  const clickNotificationId = queryParams.get("notificationId");
  const acknowledgedClickRef = useRef<string | null>(null);
  const acknowledgementInFlightRef = useRef<string | null>(null);
  useEffect(() => {
    if (
      !user ||
      !clickNotificationId ||
      acknowledgedClickRef.current === clickNotificationId ||
      acknowledgementInFlightRef.current === clickNotificationId
    ) return;

    acknowledgementInFlightRef.current = clickNotificationId;
    void inbox.markRead(clickNotificationId)
      .then((marked) => {
        if (!marked) return;
        // Only a confirmed POST makes this acknowledgement one-shot. A failed
        // request will retry after auth becomes ready or the next inbox refresh.
        acknowledgedClickRef.current = clickNotificationId;
        const params = new URLSearchParams(window.location.search);
        params.delete("notificationId");
        const cleanSearch = params.toString();
        navigate(cleanSearch ? `${window.location.pathname}?${cleanSearch}` : window.location.pathname);
      })
      .finally(() => {
        if (acknowledgementInFlightRef.current === clickNotificationId) {
          acknowledgementInFlightRef.current = null;
        }
      });
  }, [clickNotificationId, inbox.markRead, inbox.refreshGeneration, user?.id]);

  // Refresh the sessions list whenever a run completes in the active session.
  // This updates last_active_at in the sidebar so groupings (Today/Yesterday)
  // stay current without requiring a manual reload.
  //
  // Transition-aware: only refresh when there is a real transition out of an
  // in-flight state.  useAgnoChat initialises `status` to "ready", so the
  // status-sync useEffect in ChatInterface fires once on mount with prev ===
  // status === "ready".  Without the prevStatus argument we would issue an
  // extraneous refreshSessions() on every ChatInterface mount, including
  // sessions that never had a run — one of the calls contributing to the
  // six request-multiplier fires observed when creating a new session.
  const handleStatusChange = useCallback(
    (newStatus: ChatStatus, prevStatus: ChatStatus) => {
      setActiveChatStatus(newStatus);
      const wasInFlight = prevStatus === "submitted" || prevStatus === "streaming";
      const isInFlight = newStatus === "submitted" || newStatus === "streaming";
      if (!wasInFlight && isInFlight) {
        runPinBaselineRef.current = new Set(conversationViews.map((view) => view.id));
      }
      // A shared native run is discoverable as soon as the server accepts it.
      // Refresh at that transition so participation appears before the model
      // finishes a potentially long response.
      if (
        prevStatus === "submitted" && newStatus === "streaming"
        && activeSession && activeSession.user_id !== (me?.user_id ?? user?.id)
      ) {
        void refreshSessionsInBackground();
      }
      if (wasInFlight && (newStatus === "ready" || newStatus === "error")) {
        refreshSessionAfterRunCompletion(
          activeSession,
          reconcileThreadSession,
          refreshSessionsInBackground,
        );
      }
      if (wasInFlight && newStatus === "ready") {
        // A fresh capability URL reloads the iframe and every relative asset.
        // This same status transition covers live SSE and recovered polling.
        setPreviewRefreshKey((current) => current + 1);
        const baseline = runPinBaselineRef.current ?? new Set<string>();
        runPinBaselineRef.current = null;
        void refreshConversationViews()
          .then((nextViews) => {
            const newest = nextViews.filter((view) => !baseline.has(view.id)).at(-1);
            if (
              newest
              && activeSessionIdRef.current
              && activeSessionIdRef.current === activeSession?.id
              && !activeViewIdRef.current
            ) {
              handleViewSelect(
                conversationViewTab(activeSessionIdRef.current, newest),
              );
            }
          })
          .catch((error) => {
            if (error instanceof DOMException && error.name === "AbortError") return;
            console.error("[ui] Failed to reconcile conversation views after run:", error);
            toast.error(t("views.errors.load"), { id: "conversation-views-refresh" });
          });
      } else if (wasInFlight && newStatus === "error") {
        runPinBaselineRef.current = null;
      }
    },
    [
      activeSession,
      conversationViews,
      handleViewSelect,
      me?.user_id,
      reconcileThreadSession,
      refreshConversationViews,
      refreshSessionsInBackground,
      user?.id,
      t,
    ]
  );

  // Project tasks are ordinary sessions rendered under /projects. Keep their
  // agent changes on the same optimistic session-update path as normal chats.
  const handleProjectTaskAgentChange = useCallback(async (sessionId: string, agentId: string) => {
    const updated = await updateAgent(sessionId, agentId);
    if (updated) setChatInputFocusTick((tick) => tick + 1);
  }, [updateAgent]);

  // The same selected aside occupies the task conversation pane on project routes.
  // Clearing the panel URL unmounts it and renders that conversation again.
  const renderAgentViewPanel = (view: ViewTab, active = true) => (
    <AgentViewPanel
      key={`${dashboardCacheOwner}:${view.ref.scope === "conversation"
        ? `conversation:${view.ref.sessionId}:${view.ref.agentId}:${view.ref.viewId}`
        : `agent:${view.ref.agentId}:${view.ref.viewId}`}`}
      viewRef={view.ref}
      title={view.title}
      viewType={view.type}
      availability={view.availability}
      initialPath={view.initial_path}
      viewConfig={view.config}
      pinnedFileEditingUnsupported={pinnedFileEditingUnsupported}
      getToken={getAccessToken}
      refreshKey={previewRefreshKey}
      onPublish={view.ref.scope === "agent" ? handleCodePreviewPublish : undefined}
      publishDisabled={
        view.ref.scope !== "agent"
        || !activeSessionId
        || activeChatStatus === "submitted"
        || activeChatStatus === "streaming"
        || viewPublishRequest !== null
      }
      onPinFile={browserFilePinSupported === true ? handlePinViewerFile : undefined}
      isFilePinned={isViewerFilePinned}
      pinningFilePath={pinningFilePath}
      onClose={handleCloseActiveView}
      onOpenScheduleRun={asideManager.openScheduleRun}
      onOpenWorkflowRun={asideManager.openWorkflowRun}
      resize={asideManager.resize}
      fullscreen={isProjectsWorkspaceRoute}
      active={active}
    />
  );
  const selectedAgentViewPanel = !isAgentConversationsRoute
    && activeAside?.kind === "agent"
    && activeAgentView
    && activeAgentView.type !== "visual_editor"
    && (activeAgentView.type !== "dashboard" || activeAgentView.availability === "unavailable")
    && !(isProjectsWorkspaceRoute && activeAgentView.type === "dashboard")
    && viewAgentId ? (
      renderAgentViewPanel(activeAgentView)
  ) : null;
  const keptDashboardPanels = !isAgentConversationsRoute && !isProjectsWorkspaceRoute
    ? visibleDashboardViews.map((view) => renderAgentViewPanel(
        activeAgentViews.find((candidate) => (
          viewRefKey(candidate.ref) === viewRefKey(view.ref)
          && candidate.type === "dashboard"
          && candidate.availability !== "unavailable"
        )) ?? view,
        activeAside?.kind === "agent" && activeViewId === viewRefKey(view.ref),
      ))
    : null;

  const renderProjectTaskChat = useCallback(({
    project,
    task,
    sessionId,
    initialMessage,
    initialFiles,
    onInitialMessageSent,
    onClose,
    taskStatus,
    onTaskStatusChange,
    onSaveDescription,
    onSaveTitle,
    onSaveDueDate,
    onSaveAssignee,
    onSaveTags,
    onArtifactRemoved,
  }: ProjectTaskChatRenderProps) => {
    const taskSession = sessions.find((session) => session.id === sessionId);
    const sessionAgentId = taskSession?.agent_id ?? null;
    const taskTitle = taskSession?.title ?? task.title;

    if (viewSessionId === sessionId && selectedAgentViewPanel) return selectedAgentViewPanel;

    return (
      <PromptInputProvider
        initialInput={getDraft(sessionId).text}
        initialAttachments={getDraftAttemptFiles(sessionId)}
        key={sessionId}
      >
        <ChatInterface
          user={user}
          getAccessToken={getAccessToken}
          sessionId={sessionId}
          sessionBreadcrumb={taskBreadcrumbSegments(task, taskTitle, t("projects:subtasks.parentUnavailable"))}
          projectBreadcrumbSegment={{ id: project.id, title: project.name }}
          onMenuToggle={handleMenuToggle}
          isMenuOpen={menuOpen}
          agents={allAgents}
          agentsLoading={agentsLoading}
          agentsError={agentsError}
          currentAgentId={sessionAgentId}
          session={taskSession}
          onArchive={async (id) => {
            if ((task.descendant_open_count ?? 0) > 0 && !window.confirm(t("projects:subtasks.parentArchiveConfirm", { count: task.descendant_open_count }))) return;
            const updated = await updateTask(task.id, { status: "cancelled" });
            if (updated) notifyTaskChanged(updated);
            await archive(id);
            onClose();
          }}
          currentUserId={me?.user_id ?? user?.id}
          fallbackAgentId={project.default_agent_id}
          onAgentChange={(agentId) => {
            void handleProjectTaskAgentChange(sessionId, agentId);
          }}
          showAgentSwitcher={showAgentSwitcher}
          onAddParticipation={addParticipation}
          onRemoveParticipation={removeParticipation}
          isAdminUser={isAdminUser}
          canManageTenantAgents={canManageTenantAgents}
          pushApi={pushApi}
          preferencesApi={userPreferences}
          submitShortcut={submitShortcut}
          onStatusChange={handleStatusChange}
          focusTick={chatInputFocusTick}
          onFileLinkClick={handleFileLinkClick}
          initialMessage={initialMessage}
          initialFiles={initialFiles}
          onInitialMessageSent={onInitialMessageSent}
          onOpenThread={(childSessionId, threadAnchor, threadTitle, agentId) => {
            handleOpenProjectTaskThread(
              sessionId,
              childSessionId,
              threadAnchor,
              threadTitle,
              agentId,
            );
          }}
          onOpenSubAgent={handleOpenSubAgent}
          onOpenWorkflowRun={asideManager.openWorkflowRun}
          onSubAgentReferencesResolved={handleSubAgentReferencesResolved}
          onThreadCreated={handleThreadCreated}
          onThreadAnchorsLoaded={handleThreadAnchorsLoaded}
          agentViews={viewSessionId === sessionId ? activeAgentViews : []}
          viewsLoadError={viewSessionId === sessionId ? conversationViewsError : null}
          onRetryViews={() => { void refreshConversationViews().catch(() => {}); }}
          activeViewId={activeViewId ?? undefined}
          onViewSelect={handleViewSelect}
          onViewUnpin={handleViewUnpin}
          onToolCompleted={handleToolCompleted}
          onClose={onClose}
          onOpenFullSession={setActiveSessionId}
          taskStatus={taskStatus}
          taskDescription={task.description}
          taskDueDate={task.due_date}
          taskDueDateSupported={Object.prototype.hasOwnProperty.call(task, "due_date")}
          taskArtifacts={task.artifacts}
          projectTask={task}
          isTaskConversation
          onSaveDescription={onSaveDescription}
          onSaveTitle={onSaveTitle}
          onSaveDueDate={onSaveDueDate}
          onSaveAssignee={onSaveAssignee}
          onSaveTags={onSaveTags}
          onRemoveArtifact={async (path) => {
            const updated = await removeTaskArtifact(task.id, path);
            if (!updated) return false;
            onArtifactRemoved?.(updated);
            notifyTaskChanged(updated);
            return true;
          }}
          getArtifactDownloadLink={(path) => getTaskArtifactDownloadLink(task.id, path)}
          fetchArtifactRaw={(path) => fetchTaskArtifactRaw(task.id, path)}
          onTaskStatusChange={onTaskStatusChange}
        />
      </PromptInputProvider>
    );
  }, [
    archive,
    agentsError,
    agentsLoading,
    addParticipation,
    allAgents,
    chatInputFocusTick,
    getAccessToken,
    getTaskArtifactDownloadLink,
    fetchTaskArtifactRaw,
    handleFileLinkClick,
    handleMenuToggle,
    handleOpenProjectTaskThread,
    handleOpenSubAgent,
    handleProjectTaskAgentChange,
    handleStatusChange,
    handleThreadAnchorsLoaded,
    handleThreadCreated,
    handleSubAgentReferencesResolved,
    isAdminUser,
    menuOpen,
    me?.user_id,
    pushApi,
    removeParticipation,
    removeTaskArtifact,
    submitShortcut,
    t,
    updateTask,
    userPreferences,
    sessions,
    selectedAgentViewPanel,
    viewSessionId,
    setActiveSessionId,
    showAgentSwitcher,
    user,
  ]);

  // Unread count badge in document.title, plus viewer filename when open.
  // Uses the build-time injected APP_NAME (defaults to "Layer Nine") so
  // each instance shows its own name in the browser tab / PWA title.
  const _appName = import.meta.env.APP_NAME ?? "Layer Nine";
  useEffect(() => {
    let totalUnread = 0;
    for (const [, s] of notificationState) {
      if (s.hasUnread) totalUnread++;
    }
    // Durable inbox totals are cross-device and already de-duplicated. Keep
    // the legacy count only until that endpoint has connected; it still drives
    // sidebar state but should not double-count the browser title.
    const authoritativeUnread = inbox.isAvailable ? inbox.unreadCount : totalUnread;
    const unreadPrefix = authoritativeUnread > 0
      ? `(${authoritativeUnread}) `
      : "";
    const viewerSuffix = viewerFile ? ` — ${viewerFile.fileName}` : "";
    document.title = `${unreadPrefix}${_appName}${viewerSuffix}`;
  }, [notificationState, inbox.isAvailable, inbox.unreadCount, viewerFile]);

  // ── Logout-in-progress screen ───────────────────────────────────────
  // Show this while the sign-out/login-redirect cycle is running so the
  // user gets immediate feedback instead of a blank "Checking
  // authentication…" screen.
  if (isLoggingOut) {
    return (
      <div className="flex items-center justify-center bg-background text-foreground" style={{ height: viewportHeight }}>
        <span className="text-sm text-muted-foreground">{t("common:status.signingOut")}</span>
      </div>
    );
  }

  // While loading auth
  if (authLoading || !user) {
    return (
      <div className="flex items-center justify-center bg-background text-foreground" style={{ height: viewportHeight }}>
        <span className="text-sm text-muted-foreground">
          {authLoading
            ? t("common:status.checkingAuth")
            : t("common:status.redirectingLogin")}
        </span>
      </div>
    );
  }

  // Keep the existing initial-load screen only when there is no selection.
  // Unknown active IDs are validated asynchronously above while the current
  // UI remains mounted, avoiding a "Preparing workspace" flash for threads.
  if (!isNonChatSurface && !activeSessionId && loading) {
    return (
      <div className="flex items-center justify-center bg-background text-foreground" style={{ height: viewportHeight }}>
        <span className="text-sm text-muted-foreground">{t("common:status.preparingWorkspace")}</span>
      </div>
    );
  }

  // Pop-out mode: show only the viewer, no chat, no session list.
  if (isPopout && viewerFile) {
    return (
      <div className="flex h-screen flex-col bg-background text-foreground">
        {viewerIsHtml ? (
          <HtmlViewerPage
            url={viewerFile.url}
            fileName={viewerFile.fileName}
            onClose={handleCloseViewer}
          />
        ) : viewerIsPdf ? (
          <PdfViewerPage
            url={viewerFile.url}
            fileName={viewerFile.fileName}
            onClose={handleCloseViewer}
          />
        ) : viewerIsDocx ? (
          <DocxFileViewerPage
            url={viewerFile.url}
            fileName={viewerFile.fileName}
            onClose={handleCloseViewer}
          />
        ) : viewerIsImage ? (
          <ImageViewerPage
            url={viewerFile.url}
            fileName={viewerFile.fileName}
            onClose={handleCloseViewer}
          />
        ) : viewerIsSource ? (
          <SourceFileViewerPage
            url={viewerFile.url}
            fileName={viewerFile.fileName}
            onClose={handleCloseViewer}
          />
        ) : (
          <MarkdownViewerPage
            url={viewerFile.url}
            sessionId={activeSessionId}
            fileName={viewerFile.fileName}
            getToken={getAccessToken}
            onClose={handleCloseViewer}
            buildPopoutUrl={(url, fileName) => asideManager.buildViewerPopoutUrl(
              url,
              fileName,
              `/session/${activeSessionId ?? ""}`,
            )}
            onPinFile={browserFilePinSupported === true ? handlePinViewerFile : undefined}
            isFilePinned={isViewerFilePinned}
            pinningFilePath={pinningFilePath}
          />
        )}
      </div>
    );
  }

  return (
    <AgentCatalogRefreshProvider
      refresh={refreshAgents}
      updatedAt={agentCatalogUpdatedAt}
    >
      <ProjectsDataProvider projects={sharedProjects}>
        <NotificationProvider
          notificationState={notificationState}
          markSessionRead={markSessionRead}
          api={notifApi}
          inbox={inbox}
          setRunStatus={setRunStatus}
          registerSeenRun={registerSeenRun}
        >
          <div className="relative flex bg-background text-foreground overflow-hidden min-w-0" style={{ height: viewportHeight }}>
      {/* Mobile: keep the drawer inside the chat viewport, below app-level bars. */}
      {isMobile ? (
        <>
          {/* Backdrop */}
          {menuOpen && (
            <div
              className="absolute inset-0 z-40 bg-black/50"
              onClick={() => setMenuOpen(false)}
              aria-hidden="true"
            />
          )}
          {/* Drawer */}
          <div
            className={cn(
              "absolute inset-y-0 left-0 z-50 w-72 bg-background transition-transform duration-300 ease-in-out",
              menuOpen ? "translate-x-0" : "-translate-x-full"
            )}
          >
            <SessionSidebar
              sessions={sessions}
              cacheState={sessionsCacheState}
              activeSessionId={isProjectsSurface || isAgentConversationsRoute ? null : activeSessionId}
              loading={loading && sessions.length === 0}
              error={error}
              onRetry={() => { void refreshSessions(); }}
              agents={agentCatalogLoaded ? allAgents : previewAgents}
              agentsCacheState={agentsCacheState}
              onSelect={handleSelect}
              onCreate={handleCreate}
              onRename={handleRename}
              searchQuery={searchQuery}
              onSearchChange={setSearchQuery}
              onOpenAdvancedSearch={commandPalette.open}
              searching={searching}
              hasMore={hasMore}
              loadingMore={loadingMore}
              onLoadMore={loadMore}
              canRetryLoadMore={canRetryLoadMore}
              onRetryLoadMore={retryLoadMore}
              onOpenProject={handleOpenProject}
              onCreateProjectTaskChat={createAndOpenProjectTaskChat}
              activeProjectId={activeProjectId}
              pendingSessionIds={pendingVoiceNoteSessionIds}
              getTaskStatusForSession={getTaskStatusForSession}
              onCollapse={() => setMenuOpen(false)}
              onQuickLaunch={quickLaunch.open}
              className="h-full border-r"
              getToken={getAccessToken}
              tenant={me?.tenant}
              onRealtimeRecordingCreated={refreshSessionsInBackground}
              onRealtimeRecordingClosed={refreshSessionsInBackground}
              registerKnownSessionIds={registerKnownSessionIds}
              childRefreshTrigger={childRefreshTrigger}
              onSessionListRefresh={refreshSessionsInBackground}
              agentPins={agentPins}
              agentPinsLoading={agentPinsLoading}
              agentPinsError={agentPinsError}
              agentsLoading={agentsLoading}
              agentsError={agentsError}
              defaultPinInitializing={defaultPinInitializing}
              activeAgentId={isAgentConversationsRoute ? routeAgentId : null}
              onOpenAgent={openAgentConversations}
              onCreateAgentConversation={createConversationForAgent}
              onRefreshAgentPins={() => { void refreshAgentPins(); }}
              onRefreshAgents={() => { void refreshAgents(); }}
              pinOrdering={sidebarPinOrdering}
              pinnedByDefaultAgentIds={pinnedByDefaultAgentIds}
            />
          </div>
        </>
      ) : (
        <SessionSidebar
          sessions={sessions}
          cacheState={sessionsCacheState}
          activeSessionId={isProjectsSurface || isAgentConversationsRoute ? null : activeSessionId}
          loading={loading && sessions.length === 0}
          error={error}
          onRetry={() => { void refreshSessions(); }}
          agents={agentCatalogLoaded ? allAgents : previewAgents}
          agentsCacheState={agentsCacheState}
          onSelect={handleSelect}
          onCreate={handleCreate}
          newChatAgentId={isAgentConversationsRoute ? routeAgentId : isProjectsSurface ? null : activeSession?.agent_id ?? null}
          settingsMenuProps={{
            user,
            isAdminUser: !!isAdminUser,
            canManageTenantAgents,
            pushApi,
            preferencesApi: userPreferences,
            agents: allAgents,
            agentsLoading,
            agentsError,
          }}
          onRename={handleRename}
          searchQuery={searchQuery}
          onSearchChange={setSearchQuery}
          onOpenAdvancedSearch={commandPalette.open}
          searching={searching}
          hasMore={hasMore}
          loadingMore={loadingMore}
          onLoadMore={loadMore}
          canRetryLoadMore={canRetryLoadMore}
          onRetryLoadMore={retryLoadMore}
          onOpenProject={handleOpenProject}
          onCreateProjectTaskChat={createAndOpenProjectTaskChat}
          activeProjectId={activeProjectId}
          pendingSessionIds={pendingVoiceNoteSessionIds}
          getTaskStatusForSession={getTaskStatusForSession}
          className="h-full shrink-0"
          getToken={getAccessToken}
          tenant={me?.tenant}
          onRealtimeRecordingCreated={refreshSessionsInBackground}
          onRealtimeRecordingClosed={refreshSessionsInBackground}
          registerKnownSessionIds={registerKnownSessionIds}
          childRefreshTrigger={childRefreshTrigger}
          onSessionListRefresh={refreshSessionsInBackground}
          agentPins={agentPins}
          agentPinsLoading={agentPinsLoading}
          agentPinsError={agentPinsError}
          agentsLoading={agentsLoading}
          agentsError={agentsError}
          defaultPinInitializing={defaultPinInitializing}
          activeAgentId={isAgentConversationsRoute ? routeAgentId : null}
          onOpenAgent={openAgentConversations}
          onCreateAgentConversation={createConversationForAgent}
          onRefreshAgentPins={() => { void refreshAgentPins(); }}
          onRefreshAgents={() => { void refreshAgents(); }}
          pinOrdering={sidebarPinOrdering}
          pinnedByDefaultAgentIds={pinnedByDefaultAgentIds}
        />
      )}

      <div className="flex min-h-0 min-w-0 flex-1 flex-col overflow-hidden">
        {isProjectsWorkspaceRoute ? (
          <ProjectsWorkspace
            projectId={activeProjectId}
            getToken={getAccessToken}
            createProjectSession={(title, agentId, projectId) => create(
              title,
              agentId,
              projectId ? undefined : { [PROJECT_TASK_SESSION_METADATA_KEY]: true },
              projectId,
            )}
            discardProjectSession={archive}
            renderTaskChat={renderProjectTaskChat}
            getTaskSessionAgentId={(sessionId) => sessions.find((session) => session.id === sessionId)?.agent_id ?? null}
            agents={allAgents}
            agentsLoading={agentsLoading}
            agentsError={agentsError}
            showAgentSwitcher={showAgentSwitcher}
            isAdminUser={isAdminUser}
            canManageTenantAgents={canManageTenantAgents}
            user={user}
            pushApi={pushApi}
            preferencesApi={userPreferences}
            userRole={me?.role}
            onOpenSession={setActiveSessionId}
            onActiveProjectSessionChange={handleActiveProjectSessionChange}
            createProjectSignal={0}
            isMobile={isMobile}
            isMenuOpen={menuOpen}
            onMenuToggle={handleMenuToggle}
          />
        ) : isProjectsListRoute ? (
          <ProjectsListPage
            getToken={getAccessToken}
            isMobile={isMobile}
            isMenuOpen={menuOpen}
            onMenuToggle={handleMenuToggle}
            agents={allAgents}
            agentsLoading={agentsLoading}
            agentsError={agentsError}
            isAdminUser={isAdminUser}
            canManageTenantAgents={canManageTenantAgents}
            user={user}
            pushApi={pushApi}
            preferencesApi={userPreferences}
          />
        ) : isAgentConversationsRoute && routeAgentId ? (
          <AgentConversationsPage
            selectedAgent={selectedHubAgent}
            agents={allAgents}
            agentsLoading={agentsLoading}
            agentCatalogLoaded={agentCatalogLoaded}
            agentsError={agentsError}
            isAdminUser={isAdminUser}
            canManageTenantAgents={canManageTenantAgents}
            user={user}
            pushApi={pushApi}
            preferencesApi={userPreferences}
            pins={agentPins}
            getToken={getAccessToken}
            onPin={pinAgent}
            onUnpin={unpinAgent}
            onCreateConversation={createConversationForAgent}
            onOpenSession={handleSelect}
            activeViewId={activeAside?.kind === "agent" ? activeViewId : null}
            hasActiveAsideTarget={activeAside !== null}
            onOpenAgentView={asideManager.openAgentView}
            onCloseAgentView={asideManager.closeAgentView}
            resize={asideManager.resize}
            isMenuOpen={menuOpen}
            onMenuToggle={handleMenuToggle}
          />
        ) : activeSessionId ? (
          <PromptInputProvider
            initialInput={activeDraft}
            initialAttachments={activeDraftAttachments}
            key={activeSessionId}
          >
            <ChatInterface
              user={user}
              getAccessToken={getAccessToken}
              sessionId={activeSessionId}
              bootstrap={activeBootstrap}
              bootstrapPending={bootstrapPending}
              sessionBreadcrumb={activeSidebarTask ? taskBreadcrumbSegments(activeSidebarTask, activeSession?.title ?? activeSidebarTask.title, t("projects:subtasks.parentUnavailable")) : sessionBreadcrumb}
              projectBreadcrumbSegment={activeSidebarTaskProjectSegment}
              onMenuToggle={handleMenuToggle}
              isMenuOpen={menuOpen}
              agents={allAgents}
              agentsLoading={agentsLoading}
              agentsError={agentsError}
              currentAgentId={activeSession?.agent_id ?? null}
              session={activeSession}
              threadAnchor={threadAnchorCache[activeSessionId]?.threadAnchor}
              currentUserId={me?.user_id ?? user?.id}
              onAgentChange={handleAgentChange}
              showAgentSwitcher={showAgentSwitcher}
              showModelSwitcher={showModelSwitcher}
              onArchive={handleArchive}
              onAddParticipation={addParticipation}
              onRemoveParticipation={removeParticipation}
              onTaskCreated={handleConversationTaskCreated}
              isAdminUser={isAdminUser}
              canManageTenantAgents={canManageTenantAgents}
              pushApi={pushApi}
              preferencesApi={userPreferences}
              submitShortcut={submitShortcut}
              onStatusChange={handleStatusChange}
              // Tell ChatInterface (and downstream useAgnoChat) that this
              // session was just created by the POST + prepend flow above so
              // it can skip the mount-time /runs check.  We use state (and
              // clear it on the next render) rather than a ref so that a
              // later revisit of the same session id falls back to the
              // normal /runs check.
              isNewSession={justCreatedSessionId === activeSessionId}
              // Bumped by handleRename / handleAgentChange on success so the
              // PromptInputTextarea's autoFocusKey effect re-fires.
              focusTick={chatInputFocusTick}
              onFileLinkClick={handleFileLinkClick}
              initialMessage={
                pendingInitialMessage?.sessionId === activeSessionId
                  ? pendingInitialMessage.text
                  : null
              }
              initialFiles={
                pendingInitialMessage?.sessionId === activeSessionId
                  ? pendingInitialMessage.initialFiles
                  : null
              }
              onInitialMessageSent={() => {
                setPendingInitialMessage((current) => (
                  current?.sessionId === activeSessionId ? null : current
                ));
              }}
              onOpenThread={handleOpenThread}
              onOpenSubAgent={handleOpenSubAgent}
              onOpenWorkflowRun={asideManager.openWorkflowRun}
              onSubAgentReferencesResolved={handleSubAgentReferencesResolved}
              onThreadCreated={handleThreadCreated}
              onThreadAnchorsLoaded={handleThreadAnchorsLoaded}
              agentViews={activeAgentViews}
              viewsLoadError={conversationViewsError}
              onRetryViews={() => { void refreshConversationViews().catch(() => {}); }}
              activeViewId={activeViewId ?? undefined}
              onViewSelect={handleViewSelect}
              onViewUnpin={handleViewUnpin}
              onToolCompleted={handleToolCompleted}
              viewPublishRequest={viewPublishRequest}
              onViewPublishRequestConsumed={handleViewPublishRequestConsumed}
              taskStatus={activeSidebarTask?.status}
              taskDescription={activeSidebarTask?.description}
              taskDueDate={activeSidebarTask?.due_date}
              taskDueDateSupported={activeSidebarTask ? Object.prototype.hasOwnProperty.call(activeSidebarTask, "due_date") : false}
              taskArtifacts={activeSidebarTask?.artifacts}
              projectTask={activeSidebarTask ?? undefined}
              isTaskConversation={Boolean(sidebarTaskSessionId)}
              onSaveDescription={
                activeSidebarTask ? handleSidebarTaskDescriptionSave : undefined
              }
              onSaveTitle={activeSidebarTask ? handleSidebarTaskTitleSave : undefined}
              onSaveDueDate={activeSidebarTask ? async (dueDate) => {
                const updated = await updateTask(activeSidebarTask.id, { due_date: dueDate });
                if (!updated) return false;
                setSidebarTask((current) => current?.task.id === updated.id ? { ...current, task: updated } : current);
                notifyTaskChanged(updated);
                return true;
              } : undefined}
              onSaveAssignee={activeSidebarTask ? async (assignedTo) => {
                const updated = await updateTask(activeSidebarTask.id, { assigned_to: assignedTo });
                if (!updated) return false;
                setSidebarTask((current) => current?.task.id === updated.id ? { ...current, task: updated } : current);
                notifyTaskChanged(updated);
                return true;
              } : undefined}
              onSaveTags={activeSidebarTask ? async (tagIds) => {
                const updated = await updateTask(activeSidebarTask.id, { tags: tagIds });
                if (!updated) return false;
                setSidebarTask((current) => current?.task.id === updated.id ? { ...current, task: updated } : current);
                notifyTaskChanged(updated);
                return true;
              } : undefined}
              onRemoveArtifact={activeSidebarTask ? handleSidebarTaskArtifactRemove : undefined}
              getArtifactDownloadLink={activeSidebarTask
                ? (path) => getTaskArtifactDownloadLink(activeSidebarTask.id, path)
                : undefined}
              fetchArtifactRaw={activeSidebarTask
                ? (path) => fetchTaskArtifactRaw(activeSidebarTask.id, path)
                : undefined}
              onTaskStatusChange={
                activeSidebarTask ? handleSidebarTaskStatusChange : undefined
              }
            />
          </PromptInputProvider>
        ) : (
          <div className="flex flex-1 flex-col">
            <header className="flex items-center gap-2 border-b px-3 py-2.5 min-h-[53px]">
              <div className="flex min-w-0 flex-1 items-center gap-2" />
              {isMobile && <>
                <NotificationInbox />
                <SettingsMenu
                  className="shrink-0"
                  user={user}
                  isAdminUser={!!isAdminUser}
                  canManageTenantAgents={canManageTenantAgents}
                  pushApi={pushApi}
                  preferencesApi={userPreferences}
                  agents={allAgents}
                  agentsLoading={agentsLoading}
                  agentsError={agentsError}
                />
              </>}
            </header>
            <div className="flex flex-1 flex-col items-center justify-center gap-2 text-muted-foreground">
              <p className="text-lg font-medium">{t("common:errors.noConversationSelected")}</p>
              <p className="text-sm">{t("common:errors.noConversationSelectedHint")}</p>
            </div>
          </div>
        )}
      </div>
      {/* The aside manager owns the visible panel. Previously opened dashboards
          stay mounted but hidden so their iframe documents survive tab switches. */}
      {!isProjectsWorkspaceRoute && selectedAgentViewPanel}
      {keptDashboardPanels}
      {!isAgentConversationsRoute && activeAside?.kind === "workflow-run" && (
        <WorkflowRunPanel
          taskId={activeAside.taskId}
          getToken={getAccessToken}
          onClose={asideManager.closeAside}
          onOpenChildSession={asideManager.pushThread}
          onOpenTriggerSession={(sessionId) => navigate(`/session/${encodeURIComponent(sessionId)}`)}
          resize={asideManager.resize}
        />
      )}
      {!isAgentConversationsRoute && activeAside?.kind === "html" && (
        <HtmlViewerPanel
          url={activeAside.file.url}
          fileName={activeAside.file.fileName}
          fileUrl={activeAside.file.url}
          onClose={handleCloseViewer}
          onOpenInNewWindow={handleOpenViewerInNewWindow}
          resize={asideManager.resize}
        />
      )}
      {!isAgentConversationsRoute && activeAside?.kind === "pdf" && (
        <PdfViewerPanel
          url={activeAside.file.url}
          fileName={activeAside.file.fileName}
          onClose={handleCloseViewer}
          onOpenInNewWindow={handleOpenViewerInNewWindow}
          resize={asideManager.resize}
        />
      )}
      {!isAgentConversationsRoute && activeAside?.kind === "docx" && (
        <DocxFileViewerPanel
          url={activeAside.file.url}
          fileName={activeAside.file.fileName}
          onClose={handleCloseViewer}
          onOpenInNewWindow={handleOpenViewerInNewWindow}
          resize={asideManager.resize}
        />
      )}
      {!isAgentConversationsRoute && activeAside?.kind === "image" && (
        <ImageViewerPanel
          url={activeAside.file.url}
          fileName={activeAside.file.fileName}
          onClose={handleCloseViewer}
          onOpenInNewWindow={handleOpenViewerInNewWindow}
          resize={asideManager.resize}
        />
      )}
      {!isAgentConversationsRoute && activeAside?.kind === "source" && (
        <SourceFileViewerPanel
          url={activeAside.file.url}
          fileName={activeAside.file.fileName}
          onClose={handleCloseViewer}
          onOpenInNewWindow={handleOpenViewerInNewWindow}
          resize={asideManager.resize}
        />
      )}
      {!isAgentConversationsRoute && activeAside?.kind === "markdown" && (
        <MarkdownViewerPanel
          url={activeAside.file.url}
          sessionId={activeSessionId}
          fileName={activeAside.file.fileName}
          getToken={getAccessToken}
          fileUrl={activeAside.file.url}
          onClose={handleCloseViewer}
          onOpenInNewWindow={handleOpenViewerInNewWindow}
          onPinFile={browserFilePinSupported === true ? handlePinViewerFile : undefined}
          isFilePinned={isViewerFilePinned}
          pinningFilePath={pinningFilePath}
          resize={asideManager.resize}
        />
      )}
      {!isAgentConversationsRoute
        && (activeAside?.kind === "thread" || activeAside?.kind === "sub-agent" || activeAside?.kind === "schedule-run")
        && threadPanelParentSessionId && (
        <ChildSessionPanel
          key={`${activeAside.kind}:${activeAside.sessionId}`}
          readOnly={activeAside.kind === "schedule-run" || asideManager.stackDepth > 0}
          childSessionId={activeAside.sessionId}
          parentSessionId={threadPanelParentSessionId}
          getAccessToken={getAccessToken}
          agentId={subAgentReferenceCache[activeAside.sessionId]?.agentId
            ?? threadAnchorCache[activeAside.sessionId]?.agent_id
            ?? threadPanelParentSession?.agent_id
            ?? null}
          allAgents={allAgents}
          user={user}
          threadAnchor={activeAside.kind === "thread"
            ? threadAnchorCache[activeAside.sessionId]?.threadAnchor ?? null
            : null}
          threadTitle={threadPanelTitle}
          parentSessionTitle={activeAside.kind === "schedule-run"
            ? activeAgentView?.title ?? t("scheduleRuns.runHistory")
            : threadPanelParentSession?.title}
          onClose={activeAside.kind === "schedule-run"
            ? asideManager.closeScheduleRun
            : activeAside.kind === "sub-agent"
            ? handleCloseSubAgent
            : handleCloseChildSession}
          onBack={asideManager.stackDepth > 0 ? asideManager.popAside : undefined}
          onInvalidTarget={asideManager.stackDepth > 0 ? asideManager.popAside : asideManager.closeAside}
          onOpenFullSession={handleOpenChildFullSession}
          onNavigateSession={activeAside.kind === "sub-agent"
            ? handleNavigateSubAgentBreadcrumb
            : undefined}
          onAgentChanged={handleThreadAgentChange}
          onRunComplete={handleThreadRunComplete}
          onFileLinkClick={handleFileLinkClick}
          onOpenSubAgent={activeAside.kind === "schedule-run" ? undefined : handleOpenSubAgent}
          onOpenWorkflowRun={asideManager.openWorkflowRun}
          onSubAgentReferencesResolved={handleSubAgentReferencesResolved}
          initialSubAgentReference={subAgentReferenceCache[activeAside.sessionId] ?? null}
          submitShortcut={submitShortcut}
          resize={asideManager.resize}
        />
      )}
          </div>
      {projectTaskChatCreationFailure && (
        <div
          role="alert"
          className="fixed inset-x-4 bottom-4 z-[60] mx-auto flex w-auto max-w-md items-center gap-2 rounded-md border border-destructive/30 bg-background px-3 py-2 text-sm shadow-lg"
          data-testid="project-task-chat-creation-error"
        >
          <p className="min-w-0 flex-1 text-destructive">{t("projects:taskChatCreateFailed")}</p>
          <Button
            type="button"
            size="sm"
            variant="outline"
            onClick={retryProjectTaskChatCreation}
          >
            {t("projects:retryTaskChatCreate")}
          </Button>
          <Button
            type="button"
            size="icon-sm"
            variant="ghost"
            aria-label={t("common:actions.close")}
            onClick={() => setProjectTaskChatCreationFailure(null)}
          >
            <X className="size-3.5" aria-hidden="true" />
          </Button>
        </div>
      )}
      <LeaderKeysIndicator
        isPending={leaderKeys.isPending}
        sequences={leaderKeys.pendingSequences}
        picker={leaderKeys.pendingPicker}
      />
      <CommandPalette
        isOpen={commandPalette.isOpen}
        onClose={commandPalette.close}
        onSelectSession={handleSelect}
        getToken={getAccessToken}
        agents={allAgents}
        recentSessions={sessions}
        scopeKey={`${me?.tenant ?? ""}:${me?.user_id ?? user?.id ?? ""}`}
      />
      <QuickLaunchDialog
        open={quickLaunch.isOpen}
        onClose={quickLaunch.close}
        returnFocusRef={quickLaunch.returnFocusRef}
        getAccessToken={getAccessToken}
        getDefaultAgentId={getUserDefaultAgentId}
        agents={allAgents}
        agentsLoading={agentsLoading}
        agentsError={agentsError}
        submitShortcut={submitShortcut}
        onLaunch={handleQuickLaunch}
      />
        </NotificationProvider>
      </ProjectsDataProvider>
    </AgentCatalogRefreshProvider>
  );
}

/* ------------------------------------------------------------------ */
/*  Root                                                               */
/* ------------------------------------------------------------------ */

const Root = shouldHandleWorkOSDashboardCallback()
  ? WorkOSDashboardCallbackPage
  : App;

createRoot(document.getElementById("root")!).render(
  <StrictMode>
    <Root />
    <Toaster />
  </StrictMode>
);
